import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useLocation } from 'react-router-dom';
import {
  Box,
  Typography,
  TextField,
  Grid,
  Paper,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Switch,
  FormControlLabel,
  Alert,
  Checkbox,
  Button,
  Chip,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Snackbar,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Tooltip,
  Autocomplete,
  ClickAwayListener,
  Popper,
  MenuList,
  Avatar
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  CheckCircle as CheckCircleIcon,
  Badge as BadgeIcon,
  Error as ErrorIcon,
  QuestionAnswer as VerifyIcon
} from '@mui/icons-material';
import LaboratoryService from '../../../services/laboratoryService';
import FileUploadComponent from '../../../components/FileUploadComponent';
import CommonAPIService from '../../../services/commonAPIService';
import { LoaderWrapper } from '../../../components/index';
import {
  insertDANC,
  updateDANC,
  updateDANCBySourceId,
  getDANCById,
  getDANC
} from '../../../services/apiCalibration';
import { getScrutinyRemarks, getClauseNo, createScrutinyRemark } from '../../../services/api';
import s3Utils from '../../../utils/s3Utils';
import { decryptText, decryptAppId } from '../../../utils/encryption';
import UnfreezeApiService from '../../../services/UnfreezeApiService';
import axios from 'axios';

// Adequacy Check Component with DA NC Integration on Submit
const AdequacyCheckSection = React.memo(({
  sectionId,
  title,
  applicationStage,
  orgId,
  appId,
  ClauseNo,
  adequacyChecks,
  handleAdequacyCheck,
  handleAdequacyRemarks,
  handleAdequacySubmit,
  loading,
  currentUser = 'System User',
  currentUserId,
  assessorId,
  assessmentId,
}) => {
  // Local state for DA NC operations
  const [dancLoading, setDancLoading] = useState(false);
  const [dancError, setDancError] = useState('');
  const [dancSuccess, setDancSuccess] = useState('');
  const [existingDANCRecord, setExistingDANCRecord] = useState(null);
  const [dataLoaded, setDataLoaded] = useState(false);

  // State for scrutiny remarks
  const [scrutinyRemarks, setScrutinyRemarks] = useState([]);
  const [loadingRemarks, setLoadingRemarks] = useState(false);
  const [isDropdownSelection, setIsDropdownSelection] = useState(false);
  const [clauseNo, setClauseNo] = useState([]);
  const [selectedClause, setSelectedClause] = useState(null);

  // State for checkbox dropdown
  const [selectedRemarksOptions, setSelectedRemarksOptions] = useState([]);
  const [isCheckboxDropdownOpen, setIsCheckboxDropdownOpen] = useState(false);

  // Get expected NCtype based on userTypeId
  const currentUserTypeId = localStorage.getItem('userTypeId');
  const expectedNCType = currentUserTypeId === '6' ? 'Assessment' : 'Document Adequacy';


  // Load existing DA NC data on component mount
  useEffect(() => {
    const loadExistingDANCData = async () => {
      if (!orgId || !appId) return;

      try {
        setDancLoading(true);

        // Determine NCtype based on userTypeId
        const userTypeId = localStorage.getItem('userTypeId');
        let expectedNCType = 'Document Adequacy'; // Default

        if (userTypeId === '4') {
          expectedNCType = 'Document Adequacy';
        } else if (userTypeId === '6') {
          expectedNCType = 'Assessment';
        }

        console.log(`🔍 Loading DANC data for userType ${userTypeId} with NCtype: ${expectedNCType}`);

        // Use getDANC with filters to get records for this section
        const response = await getDANC({
          orgid: orgId,
          appid: appId,
          sectionName: title,
          subsectionName: sectionId,
          isactive: 1,
          ClauseNo: ClauseNo || null,
          assessmentId: assessmentId || 0,
          assessorId: assessorId || 0
        });

        if (response.data.success && response.data.data && response.data.data.length > 0) {

          response.data.data.forEach((record, index) => {
            console.log(`📄 Record ${index + 1}: NCtype="${record.NCtype}", Status="${record.InformationStatus}", UserType mapping: ${record.NCtype === 'Document Adequacy' ? 'UserTypeId 4' : 'UserTypeId 6'}`);
          });

          // Filter by NCtype to get the record for current userType
          const filteredRecords = response.data.data.filter(record =>
            record.NCtype === expectedNCType
          );

          // Check for other record types for awareness
          const otherRecords = response.data.data.filter(record =>
            record.NCtype !== expectedNCType
          );
          if (filteredRecords.length > 0) {
            const recordData = filteredRecords[0];
            console.log(`✅ Existing Record found with NCtype ${recordData.NCtype}:`, recordData);
            setExistingDANCRecord(recordData);

            // Bind DA NC data to adequacy check fields
            console.log(`🔗 Binding DANC data to adequacy check section: ${sectionId}`);

            if (recordData.InformationStatus) {
              console.log(`📊 Setting adequacy check status: ${recordData.InformationStatus}`);
              handleAdequacyCheck(sectionId, recordData.InformationStatus);
            }

            // Use both InformationProvided and Observation fields for remarks
            const remarks = recordData.InformationProvided || recordData.Observation || '';
            if (remarks) {
              console.log(`📝 Setting adequacy check remarks: ${remarks}`);
              handleAdequacyRemarks(sectionId, remarks);
            }

            // Load existing clause if available
            if (recordData.ClauseNo) {
              console.log(`🔢 Loading existing clause: ${recordData.ClauseNo}`);
              // Try to find the clause object from clauseNo array, or create a simple object
              const existingClause = clauseNo.find(clause =>
                (clause.DisplayName === recordData.ClauseNo) ||
                (clause.clause_no === recordData.ClauseNo) ||
                (clause.text === recordData.ClauseNo)
              ) || { DisplayName: recordData.ClauseNo, clause_no: recordData.ClauseNo };
              setSelectedClause(existingClause);
            }
          } else {
            console.log(`ℹ️ No existing DANC record found with NCtype: ${expectedNCType} for section: ${title}`);
          }
        }
        setDataLoaded(true);
      } catch (error) {
        console.log('No existing DA NC record found or error loading:', error.message);
        setDancError(`Error loading DA-NC data: ${error.message}`);
        setDataLoaded(true);
      } finally {
        setDancLoading(false);
      }
    };

    loadExistingDANCData();
  }, [orgId, appId, title, sectionId, dataLoaded, handleAdequacyCheck, handleAdequacyRemarks]);


  // Clear messages after timeout
  useEffect(() => {
    if (dancError || dancSuccess) {
      const timer = setTimeout(() => {
        setDancError('');
        setDancSuccess('');
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [dancError, dancSuccess]);

  // Get adequacy data for this section
  const adequacy = adequacyChecks[sectionId] || { status: '', remarks: '' };

  // Fetch clause numbers and scrutiny remarks when inadequate status is selected
  useEffect(() => {
    const fetchClauseNo = async () => {
      if (adequacy.status === 'Inadequate') {
        try {
          const clauseResponse = await getClauseNo({ standard: '2022', feild: 'Medical' });
          if (clauseResponse.data.success) {
            setClauseNo(clauseResponse.data.data || []);
            console.log('Clause No data:', clauseResponse.data.data);
          } else {
            console.log('No Clause No data found');
          }
        } catch (error) {
          console.error('Error fetching Clause No:', error);
        }
      } else {
        setClauseNo([]);
      }
    };

    const fetchScrutinyRemarks = async () => {
      const type = localStorage.getItem('userTypeId') === '6' ? 'Assessment' : 'Document Review';
      if (adequacy.status === 'Inadequate') {
        try {
          setLoadingRemarks(true);
          // Get only Document Review remarks
          const documentRemarks = await getScrutinyRemarks({ sectionName: title, type });

          // Set only Document Review remarks
          if (documentRemarks.data.success) {
            const remarks = (documentRemarks.data.data || []).map(r => ({ ...r, type }));
            setScrutinyRemarks(remarks);
          } else {
            setScrutinyRemarks([]);
          }
        } catch (error) {
          console.error('Error fetching scrutiny remarks:', error);
          setScrutinyRemarks([]);
        } finally {
          setLoadingRemarks(false);
        }
      } else {
        setScrutinyRemarks([]);
      }
    };

    fetchClauseNo();
    fetchScrutinyRemarks();
  }, [adequacy.status, title]);

  // Function to save new custom remarks to database - DISABLED
  // User doesn't want to auto-create new remarks, only show existing ones
  const saveCustomRemark = async (remarkText) => {
    // This function is intentionally disabled
    // Only existing remarks from database will be shown
    return;
  };


  if ((applicationStage === "18" || applicationStage === "18b" || applicationStage === "20") && currentUserTypeId !== '4' && currentUserTypeId !== '6') return null;
  if (applicationStage !== "18" && applicationStage !== "18a" && applicationStage !== "18b" && applicationStage !== "20") return null;

  // Check if adequacy checks should be read-only (stage 18a with any user type)
  const isAdequacyReadOnly = applicationStage === "18a";

  // Standard status change handler (no DA NC calls)
  const handleStatusChange = (status) => {
    handleAdequacyCheck(sectionId, status);
  };

  // Standard remarks change handler - no auto-save to scrutiny remarks DB
  const handleRemarksChange = (remarks, fromDropdown = false) => {
    handleAdequacyRemarks(sectionId, remarks);
    setIsDropdownSelection(fromDropdown);
    // Note: Auto-save to scrutiny remarks database is disabled
    // Only existing remarks will be shown in autocomplete dropdown
  };

  // Handle checkbox selection for predefined remarks
  const handleCheckboxChange = (option, checked) => {
    let newSelectedOptions;
    if (checked) {
      newSelectedOptions = [...selectedRemarksOptions, option];
    } else {
      newSelectedOptions = selectedRemarksOptions.filter(item =>
        item.inadequate_remarks !== option.inadequate_remarks
      );
    }
    setSelectedRemarksOptions(newSelectedOptions);

    // Update remarks textbox with comma-separated values
    const remarksText = newSelectedOptions
      .map(item => typeof item === 'string' ? item : item.inadequate_remarks)
      .join(', ');
    handleRemarksChange(remarksText, true);
  };

  // Clear all selected checkboxes
  const handleClearAllCheckboxes = () => {
    setSelectedRemarksOptions([]);
    handleRemarksChange('', false);
  };

  // Enhanced submit handler with DA NC API call
  const handleSubmit = async () => {
    try {
      setDancLoading(true);
      setDancError('');
      setDancSuccess('');

      // First submit the adequacy check
      await handleAdequacySubmit(sectionId);

      // Then create/update DA NC record based on status
      if (adequacy.status) {
        if (existingDANCRecord) {
          // Update existing record
          try {
            // Determine NCtype based on userTypeId
            const userTypeId = localStorage.getItem('userTypeId');
            let ncType = 'Document Adequacy'; // Default

            if (userTypeId === '4') {
              ncType = 'Document Adequacy';
            } else if (userTypeId === '6') {
              ncType = 'Assessment';
            }
            const updateData = {
              subsectionName: sectionId,
              informationStatus: adequacy.status,
              observation: adequacy.status === 'Inadequate'
                ? (adequacy.remarks || 'Document adequacy marked as Inadequate')
                : 'Document adequacy check completed',
              NCtype: ncType,
              ClauseNo: selectedClause ? (selectedClause.DisplayName || selectedClause.clause_no || selectedClause.text || selectedClause.toString()) : null,
              isactive: 1,
            };

            const recordId = existingDANCRecord.sourceid || existingDANCRecord.Id || 0;
            const response = await updateDANCBySourceId(orgId, appId, title, recordId, assessmentId, assessorId, updateData);

            if (response.success) {
              setExistingDANCRecord(response.data); // Update local state with new data
              // setDancSuccess('Record updated successfully');
            } else {
              throw new Error(response.message || 'Update failed');
            }
          } catch (updateError) {
            console.error('DA-NC update failed:', updateError);
            // setDancError('Failed to Update: ' + updateError.message);
          }
        } else {
          // Create new record  
          try {
            // Determine NCtype based on userTypeId
            const userTypeId = localStorage.getItem('userTypeId');
            let ncType = 'Document Adequacy'; // Default

            if (userTypeId === '4') {
              ncType = 'Document Adequacy';
            } else if (userTypeId === '6') {
              ncType = 'Assessment';
            }

            const dancData = {
              Orgid: parseInt(orgId),
              Appid: parseInt(appId),
              SectionName: title,
              SubsectionName: sectionId,
              InformationProvided: adequacy.remarks || '',
              InformationStatus: adequacy.status,
              Observation: adequacy.status === 'Inadequate'
                ? (adequacy.remarks || 'Document adequacy marked as Inadequate')
                : 'Document adequacy check completed',
              Insertedby: currentUserId,
              NCtype: ncType,
              ClauseNo: selectedClause ? (selectedClause.DisplayName || selectedClause.clause_no || selectedClause.text || selectedClause.toString()) : null,
              assessmentId: assessmentId,
              assessorId: assessorId,
            };

            const response = await insertDANC(dancData);
            if (response.data.success) {
              setExistingDANCRecord(response.data.data); // Store the created record
              setDancSuccess('Record created successfully');
            } else {
              throw new Error(response.message || 'Insert failed');
            }
          } catch (insertError) {
            console.error('DA-NC creation failed:', insertError);
            setDancError('Failed to create Record: ' + insertError.message);
          }
        }
      }
    } catch (error) {
      console.error('Error submitting adequacy check:', error);
      setDancError('Error submitting adequacy check: ' + error.message);
    } finally {
      setDancLoading(false);
    }
  };

  return (
    <Box sx={{
      mb: 3,
      p: 3,
      backgroundColor: '#f8f9fa',
      borderRadius: 2,
      border: '2px solid #e9ecef',
      borderLeft: '4px solid #007bff'
    }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h6" sx={{
          color: '#495057',
          fontWeight: 600,
          fontSize: '1.1rem',
          display: 'flex',
          alignItems: 'center',
          gap: 1
        }}>
          📋 Adequacy Check - {title}
          {isAdequacyReadOnly && (
            <Chip
              label="🔒 Read Only"
              size="small"
              sx={{
                bgcolor: '#f5f5f5',
                color: '#757575',
                fontWeight: 'bold',
                fontSize: '0.7rem'
              }}
            />
          )}
        </Typography>

        {(adequacy.status || existingDANCRecord) && (
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
            <Chip
              label={`${expectedNCType}: ${adequacy.status || existingDANCRecord.InformationStatus || 'Active'}`}
              size="small"
              sx={{
                bgcolor: (adequacy.status || existingDANCRecord.InformationStatus) === 'Adequate' ? '#28a745' : '#dc3545',
                color: 'white',
                fontWeight: 'bold'
              }}
            />

          </Box>
        )}

        {dancLoading && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <CircularProgress size={16} />
            <Typography variant="caption">Loading DA NC data...</Typography>
          </Box>
        )}
      </Box>

      {/* DA NC Error/Success Messages */}
      {dancError && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setDancError('')}>
          {dancError}
        </Alert>
      )}

      {dancSuccess && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setDancSuccess('')}>
          {dancSuccess}
        </Alert>
      )}

      <Grid container spacing={3} alignItems="flex-start">
        <Grid item xs={12} md={4}>
          <FormControl fullWidth sx={{
            '& .MuiOutlinedInput-root': {
              backgroundColor: 'white',
              borderRadius: 2,
              '&:hover': {
                boxShadow: '0 2px 8px rgba(0,123,255,0.15)',
              },
              '&.Mui-focused': {
                boxShadow: '0 4px 12px rgba(0,123,255,0.25)',
              }
            }
          }}>
            <InputLabel>Document Status</InputLabel>
            <Select
              value={adequacy.status}
              onChange={isAdequacyReadOnly ? undefined : (e) => handleStatusChange(e.target.value)}
              label="Document Status"
              disabled={isAdequacyReadOnly}
              sx={isAdequacyReadOnly ? {
                backgroundColor: '#f5f5f5',
                cursor: 'not-allowed',
                '& .MuiOutlinedInput-notchedOutline': {
                  borderColor: '#e0e0e0',
                },
                '&:hover .MuiOutlinedInput-notchedOutline': {
                  borderColor: '#e0e0e0',
                }
              } : undefined}
            >
              <MenuItem value="">-- Select Status --</MenuItem>
              <MenuItem value="Adequate">
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <CheckCircleIcon sx={{ color: '#28a745', fontSize: '1rem' }} />
                  Adequate
                </Box>
              </MenuItem>
              <MenuItem value="Inadequate">
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <ErrorIcon sx={{ color: '#dc3545', fontSize: '1rem' }} />
                  Inadequate
                </Box>
              </MenuItem>
              {currentUserTypeId === '4' && (
                <MenuItem value="To be Verify">
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <VerifyIcon sx={{ color: '#35dcdcff', fontSize: '1rem' }} />
                    To be Verify
                  </Box>
                </MenuItem>
              )}
            </Select>
          </FormControl>
        </Grid>
        {(adequacy.status === 'Inadequate' && localStorage.getItem('userTypeId') === '6') && (
          <Grid item xs={12} md={4}>
            <Autocomplete
              fullWidth
              freeSolo
              loading={loadingRemarks}
              options={clauseNo}
              value={selectedClause}
              getOptionLabel={(option) => {
                if (typeof option === 'string') return option;
                return option.DisplayName || option.clause_no || option.text || '';
              }}
              onChange={isAdequacyReadOnly ? undefined : (event, newValue) => {
                // Store the selected clause without modifying remarks
                setSelectedClause(newValue);
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label={
                    isAdequacyReadOnly
                      ? "Clause Numbers (View Only)"
                      : `Select Clause Number${clauseNo.length > 0 ? ` (${clauseNo.length} available)` : ''}`
                  }
                  placeholder={isAdequacyReadOnly ?
                    "View Only" :
                    clauseNo.length > 0 ?
                      "Select clause number to add..." :
                      "No clause numbers available"
                  }
                  variant="outlined"
                  InputProps={{
                    ...params.InputProps,
                    readOnly: isAdequacyReadOnly,
                    style: isAdequacyReadOnly ? {
                      backgroundColor: '#f5f5f5',
                      cursor: 'not-allowed'
                    } : undefined
                  }}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      backgroundColor: isAdequacyReadOnly ? '#f5f5f5' : 'white',
                      borderRadius: 2,
                      cursor: isAdequacyReadOnly ? 'not-allowed' : 'text',
                      '&:hover': {
                        boxShadow: isAdequacyReadOnly ? 'none' : '0 2px 8px rgba(33,150,243,0.15)',
                      },
                      '&.Mui-focused': {
                        boxShadow: isAdequacyReadOnly ? 'none' : '0 4px 12px rgba(33,150,243,0.25)',
                      }
                    }
                  }}
                />
              )}
              renderOption={(props, option) => (
                <Box component="li" {...props} sx={{
                  whiteSpace: 'pre-wrap',
                  wordBreak: 'break-word',
                  padding: '8px 16px !important',
                  fontSize: '0.875rem',
                  flexDirection: 'column',
                  alignItems: 'flex-start'
                }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                    <Typography variant="body2" sx={{ fontWeight: 500 }}>
                      {typeof option === 'string' ? option : (option.DisplayName || option.clause_no || option.text || '')}
                    </Typography>
                    <Chip
                      label="Clause"
                      size="small"
                      color="info"
                      variant="outlined"
                      sx={{ fontSize: '0.7rem', height: '20px' }}
                    />
                  </Box>
                </Box>
              )}
              disabled={isAdequacyReadOnly}
              PopperComponent={(props) => (
                <div {...props} style={{ ...props.style, zIndex: 1300 }} />
              )}
              sx={{
                '& .MuiAutocomplete-option': {
                  padding: '8px 16px',
                  fontSize: '0.875rem',
                  whiteSpace: 'pre-wrap',
                  wordBreak: 'break-word',
                }
              }}
            />
            {/* Show selected clause indicator */}
            {selectedClause && (
              <Box sx={{ mt: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography variant="caption" sx={{ color: '#6c757d', fontStyle: 'italic' }}>
                  Selected Clause:
                </Typography>
                <Chip
                  label={typeof selectedClause === 'string' ? selectedClause : (selectedClause.DisplayName || selectedClause.clause_no || selectedClause.text || '')}
                  size="small"
                  color="info"
                  onDelete={isAdequacyReadOnly ? undefined : () => setSelectedClause(null)}
                  sx={{ fontSize: '0.75rem' }}
                />
              </Box>
            )}
          </Grid>
        )}

        {adequacy.status === 'Inadequate' && (
          <>
            <Grid item xs={12} md={8}>
              {/* Simple textarea for remarks */}
              <TextField
                fullWidth
                multiline
                rows={4}
                label={
                  isAdequacyReadOnly
                    ? "Remarks (View Only)"
                    : "Remarks (Required)"
                }
                placeholder={
                  isAdequacyReadOnly
                    ? "View Only (Read-only)"
                    : "Type your detailed remarks here. Use the dropdown below to select predefined remarks."
                }
                value={adequacy.remarks || ''}
                onChange={isAdequacyReadOnly ? undefined : (e) => handleRemarksChange(e.target.value, false)}
                required={!isAdequacyReadOnly}
                variant="outlined"
                disabled={isAdequacyReadOnly}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    backgroundColor: isAdequacyReadOnly ? '#f5f5f5' : 'white',
                    borderRadius: 2,
                    '&:hover': {
                      boxShadow: isAdequacyReadOnly ? 'none' : '0 2px 8px rgba(220,53,69,0.15)',
                    },
                    '&.Mui-focused': {
                      boxShadow: isAdequacyReadOnly ? 'none' : '0 4px 12px rgba(220,53,69,0.25)',
                    }
                  }
                }}
              />

              {/* Predefined Remarks Dropdown */}
              {!isAdequacyReadOnly && scrutinyRemarks.length > 0 && (
                <Box sx={{ mt: 2, position: 'relative' }}>
                  <Button
                    variant="outlined"
                    size="small"
                    onClick={() => setIsCheckboxDropdownOpen(!isCheckboxDropdownOpen)}
                    sx={{
                      mb: 1,
                      borderColor: '#007bff',
                      color: '#007bff',
                      '&:hover': { borderColor: '#0056b3', backgroundColor: '#f8f9ff' }
                    }}
                  >
                    {isCheckboxDropdownOpen ? 'Hide' : 'Show'} Predefined Remarks ({scrutinyRemarks.length} available)
                  </Button>

                  {selectedRemarksOptions.length > 0 && (
                    <Button
                      variant="text"
                      size="small"
                      onClick={handleClearAllCheckboxes}
                      sx={{ ml: 1, color: '#dc3545' }}
                    >
                      Clear All ({selectedRemarksOptions.length})
                    </Button>
                  )}

                  {isCheckboxDropdownOpen && (
                    <Paper
                      elevation={3}
                      sx={{
                        mt: 1,
                        maxHeight: 300,
                        overflow: 'auto',
                        border: '1px solid #e0e0e0',
                        borderRadius: 2
                      }}
                    >
                      <MenuList dense>
                        {scrutinyRemarks.map((option, index) => {
                          const remarkText = typeof option === 'string' ? option : option.inadequate_remarks;
                          const isSelected = selectedRemarksOptions.some(
                            item => (typeof item === 'string' ? item : item.inadequate_remarks) === remarkText
                          );

                          return (
                            <MenuItem
                              key={index}
                              onClick={() => handleCheckboxChange(option, !isSelected)}
                              sx={{
                                py: 1,
                                px: 2,
                                display: 'flex',
                                alignItems: 'flex-start',
                                minHeight: 'auto'
                              }}
                            >
                              <Checkbox
                                checked={isSelected}
                                size="small"
                                sx={{ p: 0, mr: 2 }}
                              />
                              <Box sx={{ flexGrow: 1 }}>
                                <Typography
                                  variant="body2"
                                  sx={{
                                    fontSize: '0.875rem',
                                    lineHeight: 1.4,
                                    wordBreak: 'break-word',
                                    whiteSpace: 'pre-wrap'
                                  }}
                                >
                                  {remarkText}
                                </Typography>
                                {typeof option !== 'string' && option.type && (
                                  <Chip
                                    label={option.type}
                                    size="small"
                                    color={option.type === 'Assessment' ? 'primary' : 'secondary'}
                                    variant="outlined"
                                    sx={{
                                      fontSize: '0.7rem',
                                      height: '18px',
                                      mt: 0.5
                                    }}
                                  />
                                )}
                              </Box>
                            </MenuItem>
                          );
                        })}
                      </MenuList>
                    </Paper>
                  )}
                </Box>
              )}

              <Typography variant="caption" sx={{
                color: '#6c757d',
                display: 'block',
                mt: 1,
                fontStyle: 'italic'
              }}>
                {isAdequacyReadOnly
                  ? "Adequacy check is in read-only mode"
                  : loadingRemarks
                    ? "Loading scrutiny remarks..."
                    : scrutinyRemarks.length > 0
                      ? `${scrutinyRemarks.length} predefined remark(s) available for "${title}". You can select multiple options from the dropdown above.`
                      : "No predefined remarks found for this section. Type your detailed remarks in the text area above."
                }
              </Typography>
            </Grid>
          </>
        )}

      </Grid>

      {adequacy.status && (
        <Box sx={{ mt: 2 }}>
          {/* <Alert
            severity={existingDANCRecord
              ? (adequacy.status === 'Adequate' ? 'info' : 'warning')
              : (adequacy.status === 'Adequate' ? 'success' : 'warning')
            }
            sx={{
              borderRadius: 2,
              '& .MuiAlert-icon': {
                fontSize: '1.2rem'
              }
            }}
          >
            {isAdequacyReadOnly
              ? ''
              : (existingDANCRecord
                ? (adequacy.status === 'Adequate'
                  ? `✓ Record exists with status: ${existingDANCRecord.InformationStatus}. Click Update to modify.`
                  : `⚠ Record exists with status: ${existingDANCRecord.InformationStatus}. Click Update to modify.`)
                : (adequacy.status === 'Adequate'
                  ? '✓ This section is marked as Adequate. Click Submit to create Record.'
                  : '⚠ Please address the remarks above and submit to create Record.'))}
          </Alert> */}

          <Box sx={{ mt: 2, display: 'flex', justifyContent: 'flex-end' }}>
            <Button
              variant="contained"
              size="medium"
              onClick={handleSubmit}
              disabled={
                isAdequacyReadOnly ||
                !adequacy.status ||
                (adequacy.status === 'Inadequate' && (!adequacy.remarks || adequacy.remarks.toString().trim() === '')) ||
                loading ||
                dancLoading
              }
              sx={{
                background: existingDANCRecord
                  ? 'linear-gradient(135deg, #2196f3 0%, #1976d2 100%)' // Blue gradient for update
                  : (adequacy.status === 'Adequate'
                    ? 'linear-gradient(135deg, #28a745 0%, #20c997 100%)'
                    : 'linear-gradient(135deg, #ffc107 0%, #fd7e14 100%)'),
                color: 'white',
                fontWeight: 600,
                px: 3,
                py: 1,
                borderRadius: 2,
                boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                '&:hover': {
                  transform: 'translateY(-2px)',
                  boxShadow: '0 6px 16px rgba(0,0,0,0.2)',
                },
                '&:disabled': {
                  background: '#e9ecef',
                  color: '#6c757d',
                  transform: 'none',
                  boxShadow: 'none'
                }
              }}
            >
              {(loading || dancLoading) ? (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <CircularProgress size={16} sx={{ color: 'white' }} />
                  {existingDANCRecord ? 'Updating...' : 'Submitting...'}
                </Box>
              ) : (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  {isAdequacyReadOnly ? (
                    <>
                      🔒 Read Only Mode
                    </>
                  ) : existingDANCRecord ? (
                    <>
                      🔄 Update
                    </>
                  ) : (
                    <>
                      📤 submit
                    </>
                  )}
                </Box>
              )}
            </Button>
          </Box>
        </Box>
      )}


    </Box>
  );
});

const CalibrationOrganizationSection = ({ data, onUpdate, fields = [] }) => {
  // Get orgId from URL parameters or localStorage
  const { orgId: urlOrgId } = useParams();
  const location = useLocation();

  // Function to get orgId from URL query parameters with decryption
  const getOrgIdFromQuery = () => {
    const searchParams = new URLSearchParams(location.search);
    const encryptedOrgId = searchParams.get('orgId');
    if (encryptedOrgId) {
      try {
        const decryptedOrgId = decryptText(encryptedOrgId);
        return decryptedOrgId;
      } catch (error) {
        return encryptedOrgId; // Return original if decryption fails
      }
    }
    return null;
  };

  // Function to get appId from URL query parameters with decryption
  const getAppIdFromQuery = () => {
    const searchParams = new URLSearchParams(location.search);
    const encryptedAppId = searchParams.get('appId');
    if (encryptedAppId) {
      try {
        const decryptedAppId = decryptAppId(encryptedAppId);
        return decryptedAppId;
      } catch (error) {
        return encryptedAppId; // Return original if decryption fails
      }
    }
    return null;
  };

  // Function to get orgId with priority: URL query > URL params (no localStorage fallback)
  const getOrgId = () => {
    // First check URL query parameters (encrypted) - highest priority
    const queryOrgId = getOrgIdFromQuery();
    if (queryOrgId) {
      return queryOrgId;
    }

    // Then check URL parameters (unencrypted)
    if (urlOrgId) {
      return urlOrgId;
    }

    // No fallback - require URL parameters
    return null;
  };

  // Function to get appId from URL query only (no localStorage fallback)
  const getAppId = () => {
    // Only check URL query parameters (encrypted)
    const queryAppId = getAppIdFromQuery();
    if (queryAppId) {
      return queryAppId;
    }

    // No fallback - require URL parameters
    return null;
  };

  // Function to get assessmentId from URL query parameters with decryption
  const getAssessmentIdFromQuery = () => {
    const searchParams = new URLSearchParams(location.search);
    const encryptedAssessmentId = searchParams.get("assessmentId");
    if (encryptedAssessmentId) {
      try {
        const decryptedAssessmentId = decryptText(encryptedAssessmentId);
        console.log(
          "📍 Using decrypted assessmentId from URL query:",
          decryptedAssessmentId
        );
        return decryptedAssessmentId;
      } catch (error) {
        return encryptedAssessmentId;
      }
    }
    return 0;
  };

  // Function to get labType from URL query parameters with decryption
  const getLabTypeFromQuery = () => {
    const searchParams = new URLSearchParams(location.search);
    const encryptedLabType = searchParams.get("labType");
    if (encryptedLabType) {
      try {
        const decryptedLabType = decryptText(encryptedLabType);
        console.log(
          "📍 Using decrypted labType from URL query:",
          decryptedLabType
        );
        return decryptedLabType;
      } catch (error) {
        return encryptedLabType;
      }
    }
    return null;
  };

  // Function to get assessorId from URL query parameters with decryption
  const getAssessorIdFromQuery = () => {
    const searchParams = new URLSearchParams(location.search);
    const encryptedAssessorId = searchParams.get("assessorId");
    if (encryptedAssessorId) {
      try {
        const decryptedAssessorId = decryptText(encryptedAssessorId);
        console.log(
          "📍 Using decrypted assessorId from URL query:",
          decryptedAssessorId
        );
        return decryptedAssessorId;
      } catch (error) {
        return encryptedAssessorId;
      }
    }
    return 0;
  };

  const getUserTypeFromQuery = () => {
    const searchParams = new URLSearchParams(location.search);
    const encryptedUserType = searchParams.get("userType");
    if (encryptedUserType) {
      try {
        const decryptedUserType = decryptText(encryptedUserType);
        console.log(
          "📍 Using decrypted userType from URL query:",
          decryptedUserType
        );
        return decryptedUserType;
      } catch (error) {
        return encryptedUserType;
      }
    }
    return null;
  };

  const getCapacityFromQuery = () => {
    const searchParams = new URLSearchParams(location.search);
    const encryptedCapacity = searchParams.get("capacity");
    if (encryptedCapacity) {
      try {
        const decryptedCapacity = decryptText(encryptedCapacity);
        console.log(
          "📍 Using decrypted capacity from URL query:",
          decryptedCapacity
        );
        return decryptedCapacity;
      } catch (error) {
        return encryptedCapacity;
      }
    }
    return null;
  };

  const getAssessmentId = () => {
    const queryAssessmentId = getAssessmentIdFromQuery();
    if (queryAssessmentId) {
      return queryAssessmentId;
    }
    return 0;
  };

  const getLabType = () => {
    const queryLabType = getLabTypeFromQuery();
    if (queryLabType) {
      return queryLabType;
    }
    return null;
  };

  const getAssessorId = () => {
    const queryAssessorId = getAssessorIdFromQuery();
    if (queryAssessorId) {
      return queryAssessorId;
    }
    return 0;
  };

  const getCapacity = () => {
    const queryCapacity = getCapacityFromQuery();
    if (queryCapacity) {
      return queryCapacity;
    }
    return null;
  };
  const getUserType = () => {
    const queryUserType = getUserTypeFromQuery();
    if (queryUserType) {
      return queryUserType;
    }
    return null;
  };


  const [orgId, setOrgId] = useState(getOrgId());
  const [appId, setAppId] = useState(getAppId());
  const [assessmentId, setAssessmentId] = useState(getAssessmentId());
  const [labType, setLabType] = useState(getLabType());
  const [assessorId, setAssessorId] = useState(getAssessorId());
  const [userType, setUserType] = useState(getUserType());
  const [capacity, setCapacity] = useState(getCapacity());
  const [alertpopup, setAlertpopup] = useState(false);

  const [formData, setFormData] = useState({
    organizationName: '',
    laboratoryType: 'Diagnostic', // Default to Medical Testing Laboratory
    gstExemption: false,
    gstNumber: '',
    panTanNumber: '',
    gstExemptionFile: null,
    address: '',
    state: '',
    city: '',
    district: '',
    subDistrict: '',
    villageTown: '',
    pincode: '',
    mobileNoRequired: '',
    mobileNo: '',
    landlineNo: '',
    telephoneIsdCode: '',
    telephoneNoWithIsd: '',
    faxNo: '',
    faxIsdCode: '',
    faxNoWithIsd: '',
    emailRequired: '',
    country: '',
    phone: '',
    email: '',
    website: '',
    contactPerson: '',
    designation: '',
    calibrationFocus: '',
    accreditationScope: '',
    // Legal Entity Type fields
    legalEntityType: '',

    // Proprietorship fields
    legalEntityName: '',
    ownerName: '',
    mciRegNumber: '',
    legalIdentityDoc1: null,
    stateRegistrationNumber: '',
    legalIdentityDoc2: null,

    // Partnership fields
    partnershipLegalEntityName: '',
    partnershipOwnerName: '',
    partnershipDeedFile: null,
    mciRegNumber: '',
    legalIdentityDoc1: null,
    stateRegistrationNumber: '',
    legalIdentityDoc2: null,

    // LLP fields
    llpLegalEntityName: '',
    llpCertificateFile: null,

    // Companies Act fields
    companiesActLegalEntityName: '',
    companiesActCertificateFile: null,
    legalIdentity_Address: '',
    legalIdentity_State: '',
    legalIdentity_Pincode: '',

    // Public Ltd fields
    publicLtdLegalEntityName: '',
    publicLtdCertificateFile: null,
    legalIdentity_Address: '',

    // Government fields
    governmentLegalEntityName: '',
    governmentIsAutonomous: '',
    governmentCertificateFile: null,

    // Trust fields
    trustLegalEntityName: '',
    trustCertificateFile: null,

    // Society fields
    societyLegalEntityName: '',
    societyCertificateFile: null,
    societyIsAutonomous: 'No',

    // One Person Company fields
    opcLegalEntityName: '',
    legalIdentity_Address: '',
    opcCertificateFile: null,

    // Type of Functioning fields
    typeOfFunctioning: '',

    // Hospital Owned fields
    hospitalName: '',
    hospitalRegistrationCertificateFile: null,
    hospitalServiceProvider: '',
    legalIdentity_Address: '',
    legalIdentity_State: '',
    legalIdentity_Pincode: '',

    // Hospital Outsource fields
    outsourceLaboratoryName: '',
    hospitalLabMouFile: null,
    outsourceLabAddress: '',
    outsourceLabState: '',
    outsourceLabPincode: '',
    outsourceServiceProvider: '',

    // CRO Labs fields
    croServiceProvider: '',

    // Others fields
    othersServiceProvider: '',

    // Contact Person from laboratory for NABL fields
    contactPersonSalutation: 'Dr.',
    contactPersonName: '',
    contactPersonDesignation: '',
    contactPersonMobileNo: '',
    contactPersonTelephoneNo: '',
    contactPersonFaxNo: '',
    contactPersonEmail1: '',
    contactPersonEmail2: '',

    // Parent Organization fields
    isPartOfOrganization: '',
    parentOrganizationName: '',
    parentMobileNo: '',
    parentLandlineNo: '',
    parentFaxNo: '',
    parentEmail: '',

    // Adverse Action fields
    hasAdverseAction: '',
    adverseActionType: 'Closed',
    adverseDecisionDate: '',
    previousLabId: '',

    // SEZ Region fields
    isFromSEZ: '',
    sezCertificateFile: null,

    // Consultancy fields
    hasConsultancy: '',
    consultancyName: '',
    consultancyAddress: '',

    // Medical Testing Facility fields
    permanentFacility: '',
    laboratorySiteTesting: '',
    poctTesting: '',
    accreditationCategory: '',
    permanentSiteFacility: '',
    permanentSiteFacilityAddress: '',
    isSiteFacility: '',

    // Sample Collection fields
    sampleCollectionCentre: '',
    sampleCollectionDocument: null,
    numberOfCollectionCenters: '',
    homeCollection: '',
    homeCollectionDocument: null,
    otherSources: '',
    otherSourcesDocument: null,

    // Internal Audit fields
    internalAuditFromDate: '',
    internalAuditToDate: '',
    internalAuditDocument: null,
    managementReviewFromDate: '',
    managementReviewToDate: '',
    managementReviewDocument: null,
    iso15189Audited: '',
    locationsAudited: '',
    prePostExamAudited: '',

    ...data
  });

  // Dropdown data
  const [states, setStates] = useState([]);
  const [cities, setCities] = useState([]);
  const [districts, setDistricts] = useState([]);
  const [subDistricts, setSubDistricts] = useState([]);

  useEffect(() => {
    {
      applicationStage === null || applicationStage === "" && (
        setAlertpopup(true)
      )
    }

    const timer = setTimeout(() => {
      setAlertpopup(false); // close after 5 seconds
    }, 5000);

    return () => clearTimeout(timer); // cleanup
  }, []);

  const handleCloseOfficerDialog = () => {
    setAlertpopup(false);
  };
  // Static medical laboratory types
  const labTypes = [
    { id: '', name: 'Select' },
    { id: 'Diagnostic', name: 'Medical Testing Laboratory' },
    { id: 'Medical Imaging', name: 'Medical Imaging' },
    { id: 'Both', name: 'Medical Testing Laboratory + Medical Imaging' }
  ];

  // Common service provider options
  const serviceProviderOptions = [
    { value: '', label: 'Select' },
    { value: 'Open to Others', label: 'Open to Others' },
    { value: 'Partly Open to Others', label: 'Partly Open to Others' },
    { value: 'In-House Activity', label: 'In-House Activity' }
  ];

  // Common Service Provider Dropdown Component
  const ServiceProviderDropdown = ({ fieldName, label = "Type of service provider", required = true }) => (
    <FormControl fullWidth required={required} sx={{
      '& .MuiOutlinedInput-root': {
        backgroundColor: 'white',
        borderRadius: 2,
        '&:hover': {
          boxShadow: '0 4px 8px rgba(0,0,0,0.1)',
        },
        '&.Mui-focused': {
          boxShadow: '0 4px 12px rgba(25, 118, 210, 0.2)',
        }
      }
    }}>
      <InputLabel>{label}</InputLabel>
      <Select
        {...getSelectProps(fieldName)}
        label={label}
      >
        {serviceProviderOptions.map((option) => (
          <MenuItem key={option.value} value={option.value}>
            {option.label}
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );

  const [loading, setLoading] = useState(false);
  const [notification, setNotification] = useState({ open: false, message: '', severity: 'success' });
  const [hasExistingData, setHasExistingData] = useState(false);
  const [dataSource, setDataSource] = useState(null);
  const [validationErrors, setValidationErrors] = useState({});
  const [isFormValid, setIsFormValid] = useState(false);

  // Instructions state
  const [selectedDisciplines, setSelectedDisciplines] = useState([]);
  const [instructions, setInstructions] = useState([]);
  const [instructionsLoading, setInstructionsLoading] = useState(false);

  // Adequacy Check states
  const [applicationStage, setApplicationStage] = useState(null);
  const [adequacyChecks, setAdequacyChecks] = useState({});

  // Unfreeze records state
  const [unfreezeRecords, setUnfreezeRecords] = useState([]);

  // Current user for DA NC system - get from auth context or session
  const [currentUser] = useState(() => {
    return localStorage.getItem('userName') ||
      localStorage.getItem('currentUser') ||
      'System User';
  });

  // Current user ID for DA NC system - get numeric ID for Insertedby field
  const [currentUserId] = useState(() => {
    return parseInt(localStorage.getItem('orgId')) ||
      parseInt(localStorage.getItem('currentUserId')) ||
      0;
  });

  // Validation functions
  const validateEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const validatePhone = (phone) => {
    // Validate exactly 10 digits, numeric only
    const phoneRegex = /^\d{10}$/;
    return phoneRegex.test(phone);
  };

  const validatePincode = (pincode) => {
    // Validate exactly 6 digits, numeric only
    const pincodeRegex = /^\d{6}$/;
    return pincodeRegex.test(pincode);
  };

  const validateGST = (gst) => {
    // Allow "NA" as a valid value (case-insensitive)
    if (gst && gst.toUpperCase() === 'NA') {
      return true;
    }
    // Validate standard GST format
    const gstRegex = /^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z]{1}[1-9A-Z]{1}Z[0-9A-Z]{1}$/;
    return gstRegex.test(gst);
  };

  const validatePAN = (pan) => {
    const panRegex = /^[A-Z]{5}[0-9]{4}[A-Z]{1}$/;
    return panRegex.test(pan);
  };

  // Helper function to handle numeric-only input with length restrictions
  const handleNumericInput = (value, maxLength) => {
    // Remove any non-numeric characters and limit to maxLength
    const numericValue = value.replace(/\D/g, '').slice(0, maxLength);
    return numericValue;
  };

  // Enhanced handleChange function for numeric fields
  const handleNumericChange = (fieldName, value, maxLength) => {
    const numericValue = handleNumericInput(value, maxLength);
    handleChange(fieldName, numericValue);
  };

  // Helper function to check if legal entity type requires MCI or State registration
  const requiresRegistrationValidation = (legalEntityType) => {
    return ['Proprietorship', 'Partnership'].includes(legalEntityType);
  };

  // Helper function to validate MCI or State registration (at least one is required)
  const validateRegistrationRequirement = (formData) => {
    if (!requiresRegistrationValidation(formData.legalEntityType)) {
      return { isValid: true, errors: {} };
    }

    const hasMCIDetails = !!(formData.mciRegNumber && formData.mciRegNumber.trim() &&
      formData.legalIdentityDoc1 && formData.legalIdentityDoc1.fileName);

    const hasStateDetails = !!(formData.stateRegistrationNumber && formData.stateRegistrationNumber.trim() &&
      formData.legalIdentityDoc2 && formData.legalIdentityDoc2.fileName);

    if (!hasMCIDetails && !hasStateDetails) {
      return {
        isValid: false,
        errors: {
          mciRegNumber: 'Provide MCI/NMC registration number and certificate, OR complete the State registration details below',
          stateRegistrationNumber: 'Provide State registration number and certificate, OR complete the MCI/NMC details above',
          legalIdentityDoc1: 'Upload MCI/NMC certificate, OR provide State registration certificate below',
          legalIdentityDoc2: 'Upload State registration certificate, OR provide MCI/NMC certificate above'
        }
      };
    }

    return { isValid: true, errors: {} };
  };

  const validateField = (fieldName, value) => {
    let error = '';

    // Field name mapping for user-friendly error messages
    const fieldLabels = {
      'organizationName': 'Organization Name',
      'laboratoryType': 'Laboratory Type',
      'address': 'Address',
      'state': 'State',
      'city': 'City',
      'district': 'District',
      'pincode': 'Pincode',
      'mobileNoRequired': 'Mobile Number',
      'emailRequired': 'Email Address',
      'legalEntityType': 'Legal Entity Type',
      'typeOfFunctioning': 'Type of Functioning',
      'contactPersonName': 'Contact Person Name',
      'contactPersonDesignation': 'Contact Person Designation',
      'contactPersonMobileNo': 'Contact Person Mobile Number',
      'contactPersonEmail1': 'Contact Person Email',
      'legalEntityName': 'Legal Entity Name',
      'ownerName': 'Owner Name',
      'partnershipLegalEntityName': 'Partnership Legal Entity Name',
      'partnershipOwnerName': 'Name of the Owner',
      'hospitalName': 'Hospital Name',
      'hospitalServiceProvider': 'Hospital Service Provider',
      'gstNumber': 'GST Number',
      'panTanNumber': 'PAN Number',
      'website': 'Website URL',
      'numberOfCollectionCenters': 'Number of Collection Centers',
      'legalIdentity_Pincode': 'Pincode',
      'outsourceLabPincode': 'Outsource Lab Pincode',
      'sampleCollectionCentre': 'Sample Collection Centre',
      'homeCollection': 'Home Collection',
      'otherSources': 'Other Sources',
      'permanentFacility': 'Permanent Facility',
      'permanentSiteFacility': 'Permanent Site Facility',
      'permanentSiteFacilityAddress': 'Permanent Site Facility Address',
      'laboratorySiteTesting': 'Laboratory Site Testing',
      'poctTesting': 'POCT Testing',
      'accreditationCategory': 'Accreditation Category',
      'isPartOfOrganization': 'Is Laboratory part of an Organization',
      'hasAdverseAction': 'Has adverse action been initiated/taken against the laboratory',
      'isFromSEZ': 'Are you from SEZ region',
      'hasConsultancy': 'Whether any individual or organization has provided consultancy for preparing towards NABL Accreditation',
      'internalAuditFromDate': 'Internal Audit From Date',
      'internalAuditToDate': 'Internal Audit To Date',
      'managementReviewFromDate': 'Management Review From Date',
      'managementReviewToDate': 'Management Review To Date',
      'iso15189Audited': 'Whether all requirements of ISO 15189:2022 have been audited',
      'locationsAudited': 'Whether various locations were covered in the audit',
      'prePostExamAudited': 'Whether pre and post examination activities were included in the audit schedule',
      // Legal Entity specific fields
      'legalIdentity_Address': 'Registered Office Address',
      'legalIdentity_State': 'State',
      'governmentIsAutonomous': 'Are you an Autonomous Body',
      'societyIsAutonomous': 'Are you an Autonomous Body',
      'mciRegNumber': 'MCI/NMC Registered Number',
      'stateRegistrationNumber': 'State Registration Certificate Number',
      // Type of Functioning specific fields
      'outsourceLaboratoryName': 'Name of the Outsource Laboratory',
      'outsourceLabAddress': 'Laboratory Address',
      'outsourceLabState': 'State',
      'outsourceServiceProvider': 'Type of service provider',
      'croServiceProvider': 'Type of service provider',
      'othersServiceProvider': 'Type of service provider',
      // Conditional fields
      'parentOrganizationName': 'Parent Organization Name',
      'adverseActionType': 'Adverse Action Type',
      'adverseDecisionDate': 'Decision Date',
      'consultancyName': 'Consultancy Name',
      'consultancyAddress': 'Consultancy Address'
    };

    // Base required field validation (always required regardless of selections)
    const baseRequiredFields = [
      'organizationName', 'laboratoryType', 'address', 'state', 'city',
      'pincode', 'emailRequired', 'accreditationCategory', 'panTanNumber',
      'legalEntityType', 'typeOfFunctioning', 'contactPersonName',
      'contactPersonDesignation', 'contactPersonMobileNo', 'contactPersonEmail1',
      'isPartOfOrganization', 'hasAdverseAction', 'isFromSEZ', 'hasConsultancy',
      'permanentFacility', 'poctTesting',
      'sampleCollectionCentre', 'homeCollection', 'otherSources',
      'internalAuditFromDate', 'internalAuditToDate', 'managementReviewFromDate', 'managementReviewToDate',
      'iso15189Audited', 'locationsAudited', 'prePostExamAudited'
    ];

    // Dynamic required fields based on form selections
    let requiredFields = [...baseRequiredFields];

    // Add GST number to required fields if GST exemption is not checked
    if (!formData.gstExemption) {
      requiredFields.push('gstNumber');
    }

    if (!formData.laboratoryType === "Medical Imaging") {
      requiredFields.push('isSiteFacility');
    }

    // Add conditional required fields based on selections
    if (formData.sampleCollectionCentre === 'Yes' && formData.numberOfCollectionCenters && parseInt(formData.numberOfCollectionCenters) > 0) {
      requiredFields.push('numberOfCollectionCenters');
    }

    if (requiredFields.includes(fieldName) && (!value || value.toString().trim() === '' || value === '0')) {
      const fieldLabel = fieldLabels[fieldName] || fieldName;
      error = `${fieldLabel} is required`;
    } else {
      // Specific field validations
      switch (fieldName) {
        case 'emailRequired':
        case 'contactPersonEmail1':
        case 'contactPersonEmail2':
        case 'parentEmail':
          if (value && !validateEmail(value)) {
            const fieldLabel = fieldLabels[fieldName] || 'Email';
            error = `Please enter a valid email address for ${fieldLabel}`;
          }
          break;

        case 'mobileNoRequired':
        case 'mobileNo':
        case 'contactPersonMobileNo':
        case 'parentMobileNo':
          if (value && !validatePhone(value)) {
            const fieldLabel = fieldLabels[fieldName] || 'Mobile Number';
            error = `Please enter a valid 10-digit mobile number for ${fieldLabel}`;
          }
          break;

        case 'pincode':
        case 'legalIdentity_Pincode':
        case 'outsourceLabPincode':
          if (value && !validatePincode(value)) {
            const fieldLabel = fieldLabels[fieldName] || 'Pincode';
            error = `Please enter a valid 6-digit pincode for ${fieldLabel}`;
          }
          break;

        case 'gstNumber':
          if (!formData.gstExemption && value && !validateGST(value)) {
            error = 'Please enter a valid GST number (format: 22AAAAA0000A1Z5) or "NA"';
          }
          break;

        case 'panTanNumber':
          if (value && !validatePAN(value)) {
            error = 'Please enter a valid PAN number (format: AAAAA0000A)';
          }
          break;

        case 'website':
          if (value && !value.match(/^https?:\/\/.+/)) {
            error = 'Please enter a valid website URL starting with http:// or https://';
          }
          break;

        case 'numberOfCollectionCenters':
          if (value && (isNaN(value) || parseInt(value) < 0)) {
            error = 'Please enter a valid number for collection centers';
          }
          break;

        case 'internalAuditFromDate':
        case 'internalAuditToDate':
        case 'managementReviewFromDate':
        case 'managementReviewToDate':
          if (value && !value.match(/^\d{4}-\d{2}-\d{2}$/)) {
            const fieldLabel = fieldLabels[fieldName] || 'Date';
            error = `Please enter a valid date for ${fieldLabel}`;
          }
          break;

        default:
          break;
      }
    }

    return error;
  };

  const validateForm = () => {
    const errors = {};
    let hasErrors = false;

    // Validate all form fields
    Object.keys(formData).forEach(fieldName => {
      const error = validateField(fieldName, formData[fieldName]);
      if (error) {
        errors[fieldName] = error;
        hasErrors = true;
      }
    });

    // Validate MCI or State registration requirement for applicable legal entity types
    const registrationValidation = validateRegistrationRequirement(formData);
    if (!registrationValidation.isValid) {
      Object.keys(registrationValidation.errors).forEach(fieldName => {
        // Only add the error if there isn't already a validation error for this field
        if (!errors[fieldName]) {
          errors[fieldName] = registrationValidation.errors[fieldName];
          hasErrors = true;
        }
      });
    }

    // Legal Entity Type specific required field validations
    const legalEntityRequiredFields = {
      'Proprietorship': {
        fields: ['legalEntityName', 'ownerName'],
        conditionalFields: [],
        requiredFiles: []
      },
      'Partnership': {
        fields: ['legalEntityName', 'partnershipOwnerName'],
        conditionalFields: [],
        requiredFiles: ['partnershipDeedFile']
      },
      'LLP': {
        fields: ['legalEntityName'],
        conditionalFields: [],
        requiredFiles: ['llpCertificateFile']
      },
      'Companies Act': {
        fields: ['legalEntityName', 'legalIdentity_Address', 'legalIdentity_State', 'legalIdentity_Pincode'],
        conditionalFields: [],
        requiredFiles: ['companiesActCertificateFile']
      },
      'Public Ltd': {
        fields: ['legalEntityName', 'legalIdentity_Address'],
        conditionalFields: [],
        requiredFiles: ['publicLtdCertificateFile']
      },
      'Government': {
        fields: ['legalEntityName', 'governmentIsAutonomous'],
        conditionalFields: [],
        requiredFiles: ['governmentCertificateFile']
      },
      'Trust': {
        fields: ['legalEntityName'],
        conditionalFields: [],
        requiredFiles: ['trustCertificateFile']
      },
      'Society': {
        fields: ['legalEntityName', 'societyIsAutonomous'],
        conditionalFields: [],
        requiredFiles: ['societyCertificateFile']
      },
      'One Person Company': {
        fields: ['legalEntityName', 'legalIdentity_Address'],
        conditionalFields: [],
        requiredFiles: ['opcCertificateFile']
      }
    };


    // Validate legal entity type specific fields
    if (formData.legalEntityType && legalEntityRequiredFields[formData.legalEntityType]) {
      const config = legalEntityRequiredFields[formData.legalEntityType];

      // Check required fields
      config.fields.forEach(field => {
        if (!formData[field] || formData[field].toString().trim() === '') {
          const fieldLabels = {
            'legalEntityName': 'Legal Entity Name',
            'ownerName': 'Owner Name',
            'partnershipOwnerName': 'Name of the Owner',
            'legalIdentity_Address': 'Registered Office Address',
            'legalIdentity_State': 'State',
            'legalIdentity_Pincode': 'Pincode',
            'governmentIsAutonomous': 'Are you an Autonomous Body',
            'societyIsAutonomous': 'Are you an Autonomous Body'
          };
          const fieldLabel = fieldLabels[field] || field;
          errors[field] = `${fieldLabel} is required`;
          hasErrors = true;
        }
      });

      // Check required files
      config.requiredFiles.forEach(fileField => {
        if (!formData[fileField] || !formData[fileField].fileName) {
          const fileLabels = {
            'partnershipDeedFile': 'Partnership Deed document',
            'llpCertificateFile': 'LLP Certificate',
            'companiesActCertificateFile': 'Companies Act Certificate',
            'publicLtdCertificateFile': 'Public Ltd Certificate',
            'governmentCertificateFile': 'Government Certificate/Notification',
            'trustCertificateFile': 'Trust Certificate/Deed',
            'societyCertificateFile': 'Society Certificate',
            'opcCertificateFile': 'One Person Company Certificate'
          };
          const fileLabel = fileLabels[fileField] || fileField;
          errors[fileField] = `${fileLabel} is required`;
          hasErrors = true;
        }
      });
    }

    // Type of Functioning specific required field validations
    const functioningRequiredFields = {
      'hospital-owned': {
        fields: ['hospitalName', 'hospitalServiceProvider', 'legalIdentity_Address', 'legalIdentity_State', 'legalIdentity_Pincode'],
        requiredFiles: ['hospitalRegistrationCertificateFile']
      },
      'hospital-outsource': {
        fields: ['outsourceLaboratoryName', 'outsourceLabAddress', 'outsourceLabState', 'outsourceLabPincode', 'outsourceServiceProvider'],
        requiredFiles: ['hospitalLabMouFile']
      },
      'cro-lab': {
        fields: ['croServiceProvider'],
        requiredFiles: []
      },
      'others': {
        fields: ['othersServiceProvider'],
        requiredFiles: []
      }
    };

    // Validate type of functioning specific fields
    if (formData.typeOfFunctioning && functioningRequiredFields[formData.typeOfFunctioning]) {
      const config = functioningRequiredFields[formData.typeOfFunctioning];

      // Check required fields
      config.fields.forEach(field => {
        if (!formData[field] || formData[field].toString().trim() === '') {
          const fieldLabels = {
            'hospitalName': 'Name of Hospital',
            'hospitalServiceProvider': 'Type of service provider',
            'legalIdentity_Pincode': 'Pincode',
            'outsourceLaboratoryName': 'Name of the Outsource Laboratory',
            'outsourceLabAddress': 'Laboratory Address',
            'outsourceLabState': 'State',
            'outsourceLabPincode': 'Pincode',
            'outsourceServiceProvider': 'Type of service provider',
            'croServiceProvider': 'Type of service provider',
            'othersServiceProvider': 'Type of service provider'
          };
          const fieldLabel = fieldLabels[field] || field;
          errors[field] = `${fieldLabel} is required`;
          hasErrors = true;
        }
      });

      // Check required files
      config.requiredFiles.forEach(fileField => {
        if (!formData[fileField] || !formData[fileField].fileName) {
          const fileLabels = {
            'hospitalRegistrationCertificateFile': 'Hospital Registration Certificate',
            'hospitalLabMouFile': 'Hospital and Laboratory MOU'
          };
          const fileLabel = fileLabels[fileField] || fileField;
          errors[fileField] = `${fileLabel} is required`;
          hasErrors = true;
        }
      });
    }

    // Conditional validations based on user selections
    if (formData.isPartOfOrganization === 'Yes') {
      const parentOrgRequiredFields = ['parentOrganizationName'];
      parentOrgRequiredFields.forEach(field => {
        if (!formData[field] || formData[field].toString().trim() === '') {
          const fieldLabels = {
            'parentOrganizationName': 'Parent Organization Name'
          };
          const fieldLabel = fieldLabels[field] || field;
          errors[field] = `${fieldLabel} is required`;
          hasErrors = true;
        }
      });
    }

    if (formData.hasAdverseAction === 'Yes') {
      const adverseActionRequiredFields = ['adverseActionType', 'adverseDecisionDate'];
      adverseActionRequiredFields.forEach(field => {
        if (!formData[field] || formData[field].toString().trim() === '') {
          const fieldLabels = {
            'adverseActionType': 'Type',
            'adverseDecisionDate': 'Decision Date'
          };
          const fieldLabel = fieldLabels[field] || field;
          errors[field] = `${fieldLabel} is required`;
          hasErrors = true;
        }
      });
    }

    if (formData.isFromSEZ === 'Yes') {
      if (!formData.sezCertificateFile || !formData.sezCertificateFile.fileName) {
        errors['sezCertificateFile'] = 'SEZ Certificate is required';
        hasErrors = true;
      }
    }

    if (formData.hasConsultancy === 'Yes') {
      const consultancyRequiredFields = ['consultancyName', 'consultancyAddress'];
      consultancyRequiredFields.forEach(field => {
        if (!formData[field] || formData[field].toString().trim() === '') {
          const fieldLabels = {
            'consultancyName': 'Consultancy Name',
            'consultancyAddress': 'Consultancy Address'
          };
          const fieldLabel = fieldLabels[field] || field;
          errors[field] = `${fieldLabel} is required`;
          hasErrors = true;
        }
      });
    }

    // Permanent Site Facility Address validation
    if (formData.permanentSiteFacility === 'Yes') {
      if (!formData.permanentSiteFacilityAddress || formData.permanentSiteFacilityAddress.toString().trim() === '') {
        errors['permanentSiteFacilityAddress'] = 'Permanent Site Facility Address is required';
        hasErrors = true;
      }
    }

    // Document validation for sample collection
    if (formData.sampleCollectionCentre === 'Yes') {
      if (formData.numberOfCollectionCenters && parseInt(formData.numberOfCollectionCenters) > 0) {
        if (!formData.sampleCollectionDocument || !formData.sampleCollectionDocument.fileName) {
          errors['sampleCollectionDocument'] = 'Sample Collection Document is required';
          hasErrors = true;
        }
      }
    }

    if (formData.homeCollection === 'Yes') {
      if (!formData.homeCollectionDocument || !formData.homeCollectionDocument.fileName) {
        errors['homeCollectionDocument'] = 'Home Collection Document is required';
        hasErrors = true;
      }
    }

    if (formData.otherSources === 'Yes') {
      if (!formData.otherSourcesDocument || !formData.otherSourcesDocument.fileName) {
        errors['otherSourcesDocument'] = 'Other Sources Document is required';
        hasErrors = true;
      }
    }

    // Internal audit and management review document validation
    if (formData.internalAuditFromDate && formData.internalAuditToDate) {
      if (!formData.internalAuditDocument || !formData.internalAuditDocument.fileName) {
        errors['internalAuditDocument'] = 'Internal Audit Document is required';
        hasErrors = true;
      }
    }

    if (formData.managementReviewFromDate && formData.managementReviewToDate) {
      if (!formData.managementReviewDocument || !formData.managementReviewDocument.fileName) {
        errors['managementReviewDocument'] = 'Management Review Document is required';
        hasErrors = true;
      }
    }

    // Date range validations
    const today = new Date();
    today.setHours(0, 0, 0, 0); // remove time part

    // Internal Audit Validation
    if (formData.internalAuditFromDate && formData.internalAuditToDate) {
      const fromDate = new Date(formData.internalAuditFromDate);
      const toDate = new Date(formData.internalAuditToDate);

      if (fromDate >= today) {
        errors['internalAuditFromDate'] = 'From Date must be earlier than today';
        hasErrors = true;
      }

      if (fromDate > toDate) {
        errors['internalAuditToDate'] = 'Internal Audit To Date must be after From Date';
        hasErrors = true;
      }
    }

    // Management Review Validation
    if (formData.managementReviewFromDate && formData.managementReviewToDate) {
      const fromDate = new Date(formData.managementReviewFromDate);
      const toDate = new Date(formData.managementReviewToDate);

      if (fromDate >= today) {
        errors['managementReviewFromDate'] = 'From Date must be earlier than today';
        hasErrors = true;
      }

      if (fromDate > toDate) {
        errors['managementReviewToDate'] = 'Management Review To Date must be after From Date';
        hasErrors = true;
      }
    }

    setValidationErrors(errors);
    setIsFormValid(!hasErrors);
    return !hasErrors;
  };

  // Helper function to check if a section is unfrozen based on API data
  const isSectionUnfrozen = (sectionTitle) => {
    if (!unfreezeRecords || unfreezeRecords.length === 0) {
      console.log(`No unfreeze records found for section: ${sectionTitle}`);
      return false;
    }

    const today = new Date();
    // Reset time to start of day for date-only comparison
    today.setHours(0, 0, 0, 0);

    console.log(`Checking unfreeze for section "${sectionTitle}"`);
    console.log(`Available unfreeze records:`, unfreezeRecords.map(r => ({ name: r.sectionname, expiry: r.expirydate })));

    const isUnfrozen = unfreezeRecords.some(record => {
      const recordSectionName = record.sectionname || '';

      // Exact match first
      if (recordSectionName === sectionTitle) {
        const expiryDate = new Date(record.expirydate);
        // Reset expiry date time to start of day for date-only comparison
        expiryDate.setHours(0, 0, 0, 0);
        const isActive = expiryDate.getTime() >= today.getTime();
        console.log(`✅ Exact match found: "${recordSectionName}" = "${sectionTitle}", Active: ${isActive}`);
        return isActive;
      }

      // Handle Contact Person Details variations
      if (sectionTitle === 'Contact Person Details') {
        const variations = [
          'Contact Person Details',
          'Contact Person Detai', // Truncated version
          'Contact Person',
          'contact person details', // Lowercase
          'contact person detai',   // Lowercase truncated
          'contact person',
          'Contact Person for NABL',
          'Contact Person for',
          'Contact'
        ];

        const isMatch = variations.some(variation =>
          recordSectionName === variation ||
          recordSectionName.toLowerCase() === variation.toLowerCase() ||
          recordSectionName.toLowerCase().includes(variation.toLowerCase()) ||
          variation.toLowerCase().includes(recordSectionName.toLowerCase())
        );

        if (isMatch) {
          const expiryDate = new Date(record.expirydate);
          // Reset expiry date time to start of day for date-only comparison
          expiryDate.setHours(0, 0, 0, 0);
          const isActive = expiryDate.getTime() >= today.getTime();
          console.log(`✅ Contact Person variation match: "${recordSectionName}" matches "${sectionTitle}", Active: ${isActive}`);
          return isActive;
        }
      }

      // Generic partial matching for other sections
      const sectionLower = sectionTitle.toLowerCase();
      const recordLower = recordSectionName.toLowerCase();

      if (sectionLower.includes(recordLower) || recordLower.includes(sectionLower)) {
        const expiryDate = new Date(record.expirydate);
        // Reset expiry date time to start of day for date-only comparison
        expiryDate.setHours(0, 0, 0, 0);
        const isActive = expiryDate.getTime() >= today.getTime();
        console.log(`✅ Partial match found: "${recordSectionName}" ~ "${sectionTitle}", Active: ${isActive}`);
        return isActive;
      }

      return false;
    });

    console.log(`🔍 Final result - Section "${sectionTitle}" unfreeze status: ${isUnfrozen}`);
    return isUnfrozen;
  };

  // Function to check if fields should be read-only
  const isFieldReadOnly = (sectionId = null) => {
    const userTypeId = localStorage.getItem('userTypeId');

    // Get section title for unfreeze check
    const fieldName = Object.keys(formData).find(key => getFieldSectionId(key) === sectionId);
    const sectionTitle = getSectionTitleFromFieldName(fieldName);


    // Special debug for Contact Person fields
    if (sectionId === 'contact-person-details' || sectionTitle === 'Contact Person Details') {
      console.log(`📞 Contact Person field detected:`, {
        sectionId,
        fieldName,
        sectionTitle,
        unfreezeRecords: unfreezeRecords?.map(r => ({ name: r.sectionname, expiry: r.expirydate }))
      });
    }

    // Check if section is unfrozen first - this overrides ALL other conditions
    if (sectionTitle && isSectionUnfrozen(sectionTitle)) {
      console.log(`🔓 Section ${sectionTitle} is unfrozen, allowing edits`);
      return false; // Allow editing if section is unfrozen
    }

    // Temporary override for Contact Person Details - remove this after debugging
    if (sectionId === 'contact-person-details' || sectionTitle === 'Contact Person Details') {
      // Check if we have any unfreeze records that might match
      const hasContactUnfreeze = unfreezeRecords?.some(record => {
        const recordName = record.sectionname?.toLowerCase() || '';
        return recordName.includes('contact') || recordName.includes('person') || recordName.includes('detai');
      });

      if (hasContactUnfreeze) {
        console.log(`🔓 Contact Person override: Found potential unfreeze record, allowing edits`);
        return false;
      }
    }

    // Allow full editing when applicationStage is null or empty (initial form state)
    if (applicationStage === null || applicationStage === '' || applicationStage === undefined) {
      console.log(`📝 No application stage set, allowing edits`);
      return false; // Fields are fully editable when no stage is set
    }

    // Define form filling stages where users can normally edit
    const formFillingStages = [
      "1",   // Initial application
      "2",   // Draft stage
      "3",   // In progress
      "4",   // Under review by lab
      "5",   // Pending submission
      "6",   // Submitted for review
      "7",   // Under initial review
      "8",   // Pending documents
      "9",   // Documents submitted
      "10",  // Under technical review
      "11",  // Clarifications required
      "12",  // Clarifications submitted
      "13",  // Under final review
      "14",  // Approved for assessment
      "15",  // Assessment scheduled
      "16",  // Assessment in progress
      "17"   // Assessment completed
    ];

    // Allow editing during form filling stages
    if (formFillingStages.includes(applicationStage)) {
      console.log(`📝 Application stage ${applicationStage} allows editing`);
      return false; // Fields are editable during form filling
    }

    // Stage 18a: Only inadequate sections are editable (for any user type)
    if (applicationStage === "18a") {
      if (!sectionId) return true; // Default to read-only if no section specified

      // Check if this section is marked as inadequate
      const adequacy = adequacyChecks[sectionId];
      const isReadOnly = !(adequacy && adequacy.status === 'Inadequate');

      console.log(`📋 Stage 18a - Section ${sectionId} adequacy:`, adequacy, `Read-only: ${isReadOnly}`);
      return isReadOnly;
    }

    // All other stages (18, 18b, 20, etc.): Read-only unless unfrozen
    // This includes post-assessment stages where no changes should be allowed
    // unless specifically unfrozen
    console.log(`🔒 Application stage ${applicationStage} - defaulting to read-only`);
    return true;
  };

  // Function to get section status indicator for stage 18a
  const getSectionStatusIndicator = (sectionId) => {
    // Show indicators for stage 18a with any user type
    if (applicationStage !== "18a") {
      return null;
    }

    const adequacy = adequacyChecks[sectionId];
    const isEditable = adequacy && adequacy.status === 'Inadequate';

    if (isEditable) {
      return (
        <Chip
          label="✏️ Editable"
          size="small"
          sx={{
            ml: 2,
            bgcolor: '#e8f5e8',
            color: '#2e7d32',
            fontWeight: 'bold',
            fontSize: '0.75rem'
          }}
        />
      );
    } else {
      return (
        <Chip
          label="🔒 Read-only"
          size="small"
          sx={{
            ml: 2,
            bgcolor: '#f5f5f5',
            color: '#757575',
            fontWeight: 'bold',
            fontSize: '0.75rem'
          }}
        />
      );
    }
  };

  // Helper function to get section title from field name
  const getSectionTitleFromFieldName = (fieldName) => {
    // Laboratory Details section fields
    if (['organizationName', 'laboratoryType', 'gstExemption', 'gstNumber', 'panTanNumber',
      'gstExemptionFile', 'address', 'state', 'city', 'district', 'subDistrict',
      'villageTown', 'pincode', 'mobileNoRequired', 'mobileNo', 'landlineNo',
      'telephoneIsdCode', 'telephoneNoWithIsd', 'faxNo', 'faxIsdCode', 'faxNoWithIsd',
      'emailRequired', 'country', 'phone', 'email', 'website'].includes(fieldName)) {
      return 'Laboratory Details';
    }

    // Legal Entity Type section fields
    if (['legalEntityType', 'legalEntityName', 'ownerName', 'partnershipLegalEntityName',
      'partnershipOwnerName', 'mciRegNumber', 'stateRegistrationNumber', 'legalIdentityDoc1',
      'legalIdentityDoc2', 'partnershipDeedFile', 'llpCertificateFile', 'companiesActCertificateFile',
      'publicLtdCertificateFile', 'governmentCertificateFile', 'trustCertificateFile',
      'societyCertificateFile', 'opcCertificateFile', 'legalIdentity_Address', 'legalIdentity_State',
      'legalIdentity_Pincode', 'governmentIsAutonomous', 'societyIsAutonomous'].includes(fieldName)) {
      return 'Legal Entity Type';
    }

    // Type of Functioning section fields
    if (['typeOfFunctioning', 'hospitalName', 'hospitalRegistrationCertificateFile',
      'hospitalServiceProvider', 'outsourceLaboratoryName', 'hospitalLabMouFile',
      'outsourceLabAddress', 'outsourceLabState', 'outsourceLabPincode',
      'outsourceServiceProvider', 'croServiceProvider', 'othersServiceProvider'].includes(fieldName)) {
      return 'Type of Functioning';
    }

    // Contact Person for NABL section fields
    if (['contactPersonSalutation', 'contactPersonName', 'contactPersonDesignation',
      'contactPersonMobileNo', 'contactPersonTelephoneNo', 'contactPersonFaxNo',
      'contactPersonEmail1', 'contactPersonEmail2'].includes(fieldName)) {
      return 'Contact Person Details';
    }

    // Parent Organization section fields
    if (['isPartOfOrganization', 'parentOrganizationName', 'parentMobileNo',
      'parentLandlineNo', 'parentFaxNo', 'parentEmail'].includes(fieldName)) {
      return 'Parent Organization';
    }

    // Adverse Action History section fields
    if (['hasAdverseAction', 'adverseActionType', 'adverseDecisionDate', 'previousLabId'].includes(fieldName)) {
      console.log('Mapping field to Adverse Action History section:', fieldName);
      return 'Adverse Action History';
    }

    // SEZ Region section fields
    if (['isFromSEZ', 'sezCertificateFile'].includes(fieldName)) {
      console.log('Mapping field to SEZ Region section:', fieldName);
      return 'SEZ Region';
    }

    // NABL Accreditation Consultancy section fields
    if (['hasConsultancy', 'consultancyName', 'consultancyAddress'].includes(fieldName)) {
      return 'NABL Accreditation Consultancy';
    }

    // Category of Medical Testing section fields
    if (['permanentFacility', 'laboratorySiteTesting', 'poctTesting', 'accreditationCategory'].includes(fieldName)) {
      return 'Category of Medical Testing Facility';
    }

    // Collection of Sample From section fields
    if (['sampleCollectionCentre', 'numberOfCollectionCenters', 'sampleCollectionDocument',
      'homeCollection', 'homeCollectionDocument', 'otherSources', 'otherSourcesDocument'].includes(fieldName)) {
      return 'Collection of Sample From';
    }

    // Internal Audit and Management Review section fields
    if (['internalAuditFromDate', 'internalAuditToDate', 'internalAuditDocument',
      'managementReviewFromDate', 'managementReviewToDate', 'managementReviewDocument',
      'iso15189Audited', 'locationsAudited', 'prePostExamAudited'].includes(fieldName)) {
      return 'Internal Audit and Management Review';
    }

    // Add other sections as needed
    return null;
  };

  // Map field names to their corresponding section IDs
  const getFieldSectionId = (fieldName) => {
    const fieldSectionMap = {
      // Laboratory Details section
      'organizationName': 'laboratory-details',
      'laboratoryType': 'laboratory-details',
      'address': 'laboratory-details',
      'state': 'laboratory-details',
      'city': 'laboratory-details',
      'district': 'laboratory-details',
      'subDistrict': 'laboratory-details',
      'villageTown': 'laboratory-details',
      'pincode': 'laboratory-details',
      'country': 'laboratory-details',
      'mobileNo': 'laboratory-details',
      'landlineNo': 'laboratory-details',
      'telephoneIsdCode': 'laboratory-details',
      'telephoneNoWithIsd': 'laboratory-details',
      'faxNo': 'laboratory-details',
      'faxIsdCode': 'laboratory-details',
      'faxNoWithIsd': 'laboratory-details',
      'email': 'laboratory-details',
      'emailRequired': 'laboratory-details',
      'website': 'laboratory-details',
      'gstNumber': 'laboratory-details',
      'gstExemption': 'laboratory-details',
      'gstExemptionFile': 'laboratory-details',
      'panTanNumber': 'laboratory-details',

      // Legal Entity Type section
      'legalEntityType': 'legal-entity-type',
      'legalEntityName': 'legal-entity-type',
      'ownerName': 'legal-entity-type',
      'partnershipLegalEntityName': 'legal-entity-type',
      'partnershipOwnerName': 'legal-entity-type',
      'mciRegNumber': 'legal-entity-type',
      'stateRegistrationNumber': 'legal-entity-type',
      'legalIdentityDoc1': 'legal-entity-type',
      'legalIdentityDoc2': 'legal-entity-type',
      'partnershipDeedFile': 'legal-entity-type',
      'llpCertificateFile': 'legal-entity-type',
      'companiesActCertificateFile': 'legal-entity-type',
      'publicLtdCertificateFile': 'legal-entity-type',
      'governmentCertificateFile': 'legal-entity-type',
      'trustCertificateFile': 'legal-entity-type',
      'societyCertificateFile': 'legal-entity-type',
      'opcCertificateFile': 'legal-entity-type',
      'legalIdentity_Address': 'legal-entity-type',
      'legalIdentity_State': 'legal-entity-type',
      'legalIdentity_Pincode': 'legal-entity-type',
      'governmentIsAutonomous': 'legal-entity-type',
      'societyIsAutonomous': 'legal-entity-type',

      // Type of Functioning section
      'typeOfFunctioning': 'type-of-functioning',
      'hospitalName': 'type-of-functioning',
      'hospitalRegistrationCertificateFile': 'type-of-functioning',
      'hospitalServiceProvider': 'type-of-functioning',
      'outsourceLaboratoryName': 'type-of-functioning',
      'hospitalLabMouFile': 'type-of-functioning',
      'outsourceLabAddress': 'type-of-functioning',
      'outsourceLabState': 'type-of-functioning',
      'outsourceLabPincode': 'type-of-functioning',
      'outsourceServiceProvider': 'type-of-functioning',
      'croServiceProvider': 'type-of-functioning',
      'othersServiceProvider': 'type-of-functioning',

      // Contact Person Details section
      'contactPersonSalutation': 'contact-person-details',
      'contactPersonName': 'contact-person-details',
      'contactPersonDesignation': 'contact-person-details',
      'contactPersonMobileNo': 'contact-person-details',
      'contactPersonTelephoneNo': 'contact-person-details',
      'contactPersonFaxNo': 'contact-person-details',
      'contactPersonEmail1': 'contact-person-details',
      'contactPersonEmail2': 'contact-person-details',

      // Parent Organization section
      'isPartOfOrganization': 'parent-organization',
      'parentOrganizationName': 'parent-organization',
      'parentMobileNo': 'parent-organization',
      'parentLandlineNo': 'parent-organization',
      'parentFaxNo': 'parent-organization',
      'parentEmail': 'parent-organization',

      // Adverse Actions section
      'hasAdverseAction': 'adverse-action-history',
      'adverseActionType': 'adverse-action-history',
      'adverseDecisionDate': 'adverse-action-history',
      'previousLabId': 'adverse-action-history',

      // SEZ Region section
      'isFromSEZ': 'sez-region',
      'sezCertificateFile': 'sez-region',

      // Consultancy section
      'hasConsultancy': 'consultancy',
      'consultancyName': 'consultancy',
      'consultancyAddress': 'consultancy',

      // Medical Testing Facility section
      'permanentFacility': 'medical-testing-facility',
      'permanentSiteFacility': 'medical-testing-facility',
      'permanentSiteFacilityAddress': 'medical-testing-facility',
      'laboratorySiteTesting': 'medical-testing-facility',
      'poctTesting': 'medical-testing-facility',
      'accreditationCategory': 'medical-testing-facility',

      // Sample Collection section
      'sampleCollectionCentre': 'sample-collection',
      'numberOfCollectionCenters': 'sample-collection',
      'sampleCollectionDocument': 'sample-collection',
      'homeCollection': 'sample-collection',
      'homeCollectionDocument': 'sample-collection',
      'otherSources': 'sample-collection',
      'otherSourcesDocument': 'sample-collection',

      // Internal Audit section
      'internalAuditFromDate': 'internal-audit-management-review',
      'internalAuditToDate': 'internal-audit-management-review',
      'internalAuditDocument': 'internal-audit-management-review',
      'managementReviewFromDate': 'internal-audit-management-review',
      'managementReviewToDate': 'internal-audit-management-review',
      'managementReviewDocument': 'internal-audit-management-review',
      'iso15189Audited': 'internal-audit-management-review',
      'locationsAudited': 'internal-audit-management-review',
      'prePostExamAudited': 'internal-audit-management-review'
    };

    return fieldSectionMap[fieldName] || null;
  };

  // Helper function to get Select props with read-only support
  const getSelectProps = (fieldName, options = {}) => {
    const sectionId = getFieldSectionId(fieldName);
    const readOnly = isFieldReadOnly(sectionId);

    return {
      value: formData[fieldName] || '',
      onChange: readOnly ? undefined : (e) => handleChange(fieldName, e.target.value),
      readOnly: readOnly,
      disabled: readOnly,
      sx: readOnly ? {
        backgroundColor: '#f5f5f5',
        cursor: 'not-allowed',
        '& .MuiOutlinedInput-notchedOutline': {
          borderColor: '#e0e0e0',
        },
        '&:hover .MuiOutlinedInput-notchedOutline': {
          borderColor: '#e0e0e0',
        },
        '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
          borderColor: '#e0e0e0',
        }
      } : undefined,
      ...options
    };
  };

  // Helper function to get radio button/input props with read-only support
  const getInputProps = (fieldName, value, inputType = 'radio') => {
    const sectionId = getFieldSectionId(fieldName);
    const readOnly = isFieldReadOnly(sectionId);

    const baseProps = {
      disabled: readOnly,
      style: readOnly ? {
        cursor: 'not-allowed',
        opacity: 0.6
      } : {}
    };

    if (inputType === 'checkbox') {
      return {
        ...baseProps,
        checked: formData[fieldName] || false,
        onChange: readOnly ? undefined : (e) => handleChange(fieldName, e.target.checked)
      };
    } else {
      return {
        ...baseProps,
        type: inputType,
        name: fieldName,
        value: value,
        checked: formData[fieldName] === value,
        onChange: readOnly ? undefined : (e) => handleChange(fieldName, e.target.value)
      };
    }
  };

  // Helper function to get FileUploadComponent props with read-only support
  const getFileUploadProps = (baseProps) => {
    const sectionId = getFieldSectionId(baseProps.fieldName);
    const readOnly = isFieldReadOnly(sectionId);

    return {
      ...baseProps,
      disabled: readOnly,
      localDeleteOnly: true, // Only clear state on delete, no API calls
      onChange: readOnly ? () => { } : baseProps.onChange,
      helperText: readOnly ? 'File uploads are disabled in read-only mode' : baseProps.helperText
    };
  };

  // Helper function to get TextField props with validation
  const getTextFieldProps = (fieldName, label, options = {}) => {
    const sectionId = getFieldSectionId(fieldName);
    const readOnly = isFieldReadOnly(sectionId);

    return {
      fullWidth: true,
      label,
      value: formData[fieldName] || '',
      onChange: readOnly ? undefined : (e) => handleChange(fieldName, e.target.value),
      error: !readOnly && !!validationErrors[fieldName],
      helperText: !readOnly && validationErrors[fieldName],
      required: !readOnly && (options.required || false),
      type: options.type || 'text',
      placeholder: readOnly ? 'View Only (Read-only)' : (options.placeholder || 'Enter Details..'),
      InputProps: readOnly ? {
        readOnly: true,
        style: {
          backgroundColor: '#f5f5f5',
          cursor: 'not-allowed'
        }
      } : undefined,
      sx: {
        '& .MuiOutlinedInput-root': {
          backgroundColor: readOnly ? '#f5f5f5' : 'white',
          borderRadius: 2,
          cursor: readOnly ? 'not-allowed' : 'text',
          '&:hover': {
            boxShadow: readOnly ? 'none' : '0 4px 8px rgba(0,0,0,0.1)',
          },
          '&.Mui-focused': {
            boxShadow: readOnly ? 'none' : '0 4px 12px rgba(25, 118, 210, 0.2)',
          },
          '&.Mui-error': {
            borderColor: readOnly ? 'transparent' : '#f44336',
            '&:hover': {
              boxShadow: readOnly ? 'none' : '0 4px 8px rgba(244, 67, 54, 0.2)',
            },
            '&.Mui-focused': {
              boxShadow: readOnly ? 'none' : '0 4px 12px rgba(244, 67, 54, 0.3)',
            }
          }
        },
        '& .MuiFormHelperText-root.Mui-error': {
          color: readOnly ? 'transparent' : '#f44336',
          fontWeight: 500,
          marginLeft: 0
        },
        ...options.sx
      }
    };
  };

  // Effect to monitor URL changes and update orgId and appId from URL parameters
  useEffect(() => {
    const newOrgId = getOrgId();
    if (newOrgId !== orgId) {
      setOrgId(newOrgId);
    }

    const newAppId = getAppId();
    if (newAppId !== appId) {
      setAppId(newAppId);
    }
  }, [urlOrgId, location.search, orgId, appId]);

  useEffect(() => {
    if (onUpdate) {
      onUpdate(formData);
    }
    // Validate form whenever formData changes
    const timeoutId = setTimeout(() => validateForm(), 100);
    return () => clearTimeout(timeoutId);
  }, [formData]);

  // Load initial data on component mount
  useEffect(() => {
    if (orgId) {
      loadInitialData();
    } else {
      // Show error if orgId is not available from URL
      showNotification('Organization ID not found in URL parameters. Please access this page through proper navigation.', 'error');
      setLoading(false);
    }
  }, [orgId]);

  // Get application stage when appId is available
  useEffect(() => {
    if (appId) {
      getApplicationStage();
    } else {
      // For demo purposes, set stage to 1 if no appId
      setApplicationStage(1);
    }
  }, [appId]);

  // Initialize adequacy checks with some demo data in development
  useEffect(() => {
    if (process.env.NODE_ENV === 'development' && applicationStage === 1) {
      setAdequacyChecks({
        'laboratory-details': { status: 'Adequate', remarks: '' },
        'legal-entity-type': { status: 'Inadequate', remarks: 'Missing partnership deed documentation' },
        'type-of-functioning': { status: '', remarks: '' }
      });
    }
  }, [applicationStage]);

  // Fetch instructions from API
  useEffect(() => {
    const fetchInstructions = async () => {
      try {
        setInstructionsLoading(true);
        const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/reactApi/api';
        const response = await fetch(`${API_BASE_URL}/instructions?sectionName=Laboratory Details&labTypeId=3`);
        const data = await response.json();

        if (data.success && data.data.instructions) {
          // Sort instructions by orderNo
          const sortedInstructions = data.data.instructions.sort((a, b) => a.orderNo - b.orderNo);
          setInstructions(sortedInstructions);
        }
      } catch (error) {
        console.error('Error fetching instructions:', error);
        // Fallback to default instructions if API fails
        setInstructions([]);
      } finally {
        setInstructionsLoading(false);
      }
    };

    fetchInstructions();
  }, []);

  // Load unfreeze records
  const loadUnfreezeRecords = useCallback(async () => {
    if (!appId) return;

    try {
      const response = await UnfreezeApiService.getUnfreezeRecords(appId, orgId);
      if (response.success) {
        console.log('Unfreeze records loaded:', response.data);
        setUnfreezeRecords(response.data);
      }
    } catch (err) {
      console.error("Load unfreeze records error:", err);
    }
  }, [appId, orgId]);

  // Load DA NC data for all sections
  const loadDANCData = async () => {
    if (!orgId || !appId) return;

    try {
      // Get all DA NC records for this org and app (with higher limit to ensure we get all)
      const response = await getDANC({
        orgid: orgId,
        appid: appId,
        isactive: 1,
        limit: 600,
        assessmentId: assessmentId || 0,
        assessorId: assessorId || 0
      });


      if (response.data.success && response.data && response.data.data && response.data.data.length > 0) {
        // Determine expected NCtype based on userTypeId
        const userTypeId = localStorage.getItem('userTypeId');
        let expectedNCType = 'Document Adequacy'; // Default

        if (userTypeId === '4') {
          expectedNCType = 'Document Adequacy';
        } else if (userTypeId === '6') {
          expectedNCType = 'Assessment';
        }

        // Log all records by NCtype for debugging
        const recordsByType = response.data.data.reduce((acc, record) => {
          acc[record.NCtype] = (acc[record.NCtype] || 0) + 1;
          return acc;
        }, {});


        // Filter records by NCtype before processing
        const filteredRecords = response.data.data.filter(record =>
          record.NCtype === expectedNCType
        );

        const otherRecords = response.data.data.filter(record =>
          record.NCtype !== expectedNCType
        );


        // Create adequacy checks from filtered DA NC data
        const dancAdequacyChecks = {};

        filteredRecords.forEach(record => {
          console.log('🔍 Record:', {
            SectionName: record.SectionName,
            SubsectionName: record.SubsectionName,
            InformationStatus: record.InformationStatus
          });

          // Handle both SubsectionName and direct mapping from SectionName
          let sectionKey = record.SubsectionName;

          // Map SubsectionName to correct form section IDs
          if (sectionKey) {
            const subsectionMappings = {
              'contact-person-nabl': 'contact-person-details',
              'laboratory-details': 'laboratory-details',
              'legal-entity-type': 'legal-entity-type',
              'type-of-functioning': 'type-of-functioning',
              'parent-organization': 'parent-organization',
              'adverse-action-history': 'adverse-action-history',
              'sez-region': 'sez-region',
              'consultancy': 'consultancy',
              'medical-testing-facility': 'medical-testing-facility',
              'sample-collection': 'sample-collection',
              'internal-audit-management-review': 'internal-audit-management-review'
            };

            // Apply subsection mapping if available
            const originalKey = sectionKey;
            sectionKey = subsectionMappings[sectionKey] || sectionKey;

            if (originalKey !== sectionKey) {
              console.log(`🔄 Mapped subsection: ${originalKey} → ${sectionKey}`);
            }
          }

          // If SubsectionName is not available, try to map from SectionName
          if (!sectionKey && record.SectionName) {
            // Normalize section name by trimming and handling case variations
            const normalizedSectionName = record.SectionName.trim();

            const sectionMappings = {
              'Laboratory Details': 'laboratory-details',
              'Legal Entity Type': 'legal-entity-type',
              'Type of Functioning': 'type-of-functioning',
              'Contact Person Details': 'contact-person-details',
              'Contact Person from laboratory': 'contact-person-details',
              'Contact Person from laboratory for NABL': 'contact-person-details',
              'Contact Per': 'contact-person-details',
              'Parent Organization': 'parent-organization',
              'Adverse Action History': 'adverse-action-history',
              'SEZ Region': 'sez-region',
              'Consultancy': 'consultancy',
              'Medical Testing Facility': 'medical-testing-facility',
              'Sample Collection': 'sample-collection',
              'Internal Audit': 'internal-audit-management-review',
              'Internal Audit and Management Review': 'internal-audit-management-review',
              'Proficiency Testing': 'proficiency-testing'
            };

            // Try direct mapping first
            sectionKey = sectionMappings[normalizedSectionName];

            // If no direct mapping, try partial matching for contact person
            if (!sectionKey && normalizedSectionName.toLowerCase().includes('contact person')) {
              sectionKey = 'contact-person-details';
            }

            // Fallback to normalized string
            if (!sectionKey) {
              sectionKey = normalizedSectionName.toLowerCase().replace(/\s+/g, '-');
            }
          }

          if (sectionKey) {
            console.log('✅ Mapped section key:', sectionKey, 'Status:', record.InformationStatus);

            // Special logging for contact person section
            if (sectionKey === 'contact-person-details') {
              console.log('🎯 Contact Person Section Found!', {
                originalSubsection: record.SubsectionName,
                mappedSection: sectionKey,
                status: record.InformationStatus,
                observation: record.Observation
              });
            }

            dancAdequacyChecks[sectionKey] = {
              status: record.InformationStatus || '',
              remarks: record.InformationProvided || record.Observation || '',
              isSubmitted: false, // Allow users to modify and resubmit
              submittedAt: record.updatedate || record.Insertdate,
              dancRecord: record, // Store original record for reference
              sourceid: record.sourceid || record.Id // Store sourceid for updates
            };

            console.log(`✅ Stored DANC record for section "${sectionKey}" with sourceid: ${record.sourceid || record.Id}`);

            console.log(`Mapped Record to section "${sectionKey}":`, {
              status: record.InformationStatus,
              remarks: record.InformationProvided || record.Observation
            });
          }
        });

        // Merge with existing adequacy checks
        setAdequacyChecks(prev => {
          const merged = {
            ...prev,
            ...dancAdequacyChecks
          };
          console.log('Final merged adequacy checks:', merged);
          console.log('Contact Person adequacy check:', merged['contact-person-details']);
          return merged;
        });
      } else {
        console.log('No Records found in response');
      }
    } catch (error) {
      console.error('Error loading DA-NC data:', error);
      showNotification('Error loading DA-NC data: ' + error.message, 'error');
    }
  };

  // Load states, lab types, laboratory details, and DA NC data
  const loadInitialData = async () => {
    setLoading(true);
    try {
      // Load states first, then laboratory details, then DA NC data, then unfreeze records
      await loadStates();
      await loadLaboratoryDetails();
      await loadDANCData();
      await loadUnfreezeRecords();
    } catch (error) {
      showNotification('Error loading initial data', 'error');
    } finally {
      setLoading(false);
    }
  };

  // Utility function to extract filename from full path
  const extractFileName = (fullPath) => {
    if (!fullPath) return null;

    // Handle different path separators and extract the actual filename
    const pathParts = fullPath.split(/[/\\]/);
    const lastPart = pathParts[pathParts.length - 1];

    // If the filename contains timestamp and hash, extract the original name
    // Pattern: timestamp_hash_originalname.ext (e.g., "1758096638217_78b8f5e65409d0fc_sample.pdf")
    const match = lastPart.match(/^\d+_[a-f0-9]+_(.+)$/);
    if (match) {
      return match[1]; // Return the original filename part (e.g., "sample.pdf")
    }

    return lastPart; // Return as-is if no pattern match
  };

  // Get application stage from LabApplications table
  const getApplicationStage = async () => {
    try {
      if (!appId) return;

      const response = await fetch(`${process.env.REACT_APP_API_URL}/laboratory/application-stage/${appId}`);
      const result = await response.json();

      if (result.success) {
        setApplicationStage(result.stage);
      } else {
        console.log('Stage not found, defaulting to stage 1 for demo purposes');
        setApplicationStage(1); // Default to 1 for demo purposes
      }
    } catch (error) {
      console.error('Error fetching application stage:', error);
      console.log('Error occurred, defaulting to stage 1 for demo purposes');
      setApplicationStage(1); // Default to 1 for demo purposes
    }
  };

  // Load laboratory details with fallback
  const loadLaboratoryDetails = async () => {
    try {
      const result = await LaboratoryService.getLaboratoryDetails(orgId);
      if (result.success == true) {
        const labData = result.data;

        setDataSource(result.source);
        // Map API data to form fields based on source table
        if (result.source === 'Form153LabDetails_new') {
          await mapForm153Data(labData);
          setHasExistingData(true);
        } else if (result.source === 'Lab_BasicDetails') {
          await mapLabBasicData(labData);
        }


      }
    } catch (error) {
      setHasExistingData(false);
      setDataSource(null);
    }
  };

  // Helper function to format date from ISO string to YYYY-MM-DD
  const formatDateForInput = (dateString) => {
    if (!dateString) return '';
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return '';
      return date.toISOString().split('T')[0]; // Returns YYYY-MM-DD format
    } catch (error) {
      return '';
    }
  };

  // Map Form153LabDetails_new data to form
  const mapForm153Data = async (data) => {
    // First, batch process all file fields for S3 signed URLs
    const fileFields = [
      'HospitalRegistrationCertificateFile',
      'HospitalLabMouFile',
      'LegalIdentityDoc1',
      'MCICertificateFile',
      'LegalIdentityDoc2',
      'StateCertificateFile',
      'GSTExemptionFile',
      'PartnershipDeedFile',
      'LLPCertificateFile',
      'CompaniesActCertificateFile',
      'PublicLtdCertificateFile',
      'GovernmentCertificateFile',
      'TrustCertificateFile',
      'SocietyCertificateFile',
      'OPCCertificateFile',
      'SEZCertificateFile',
      'SampleCollectionDocument',
      'HomeCollectionDocument',
      'PartnershipMCICertificateFile',
      'PartnershipStateCertificateFile',
      'InternalAuditDocument',
      'ManagementReviewDocument',
      'OtherSourcesDocument'
    ];

    console.log('🔄 Processing Laboratory Section file fields for S3 signed URLs...');
    const processedFiles = await s3Utils.batchProcessForm153Files(data, fileFields);
    const newFormData = {
      // Basic Organization Information
      organizationName: data.CabName || '',
      gstNumber: data.GSTIN || '',
      laboratoryType: data.CabType || '',
      country: data.Country || '',
      state: `${data.State}` || '',
      city: data.City || '',
      district: '', // We'll set this after loading districts
      subDistrict: '', // We'll set this after loading sub-districts
      villageTown: data.VillageTown || '',
      address: data.Address || '',
      pincode: data.Pincode || '',

      // Contact Information
      contactPersonName: data.NablContPerson || '',
      contactPersonDesignation: data.SpocDesig || '',
      mobileNo: data.MobileNo || '',
      landlineNo: data.LandlineNo || '',
      telephoneIsdCode: data.TelephoneIsdCode || '',
      telephoneNoWithIsd: data.TelephoneNoWithIsd || '',
      faxNo: data.FaxNo || '',
      faxIsdCode: data.FaxIsdCode || '',
      faxNoWithIsd: data.FaxNoWithIsd || '',
      email: data.Email || '',
      emailRequired: data.Email || '', // Map Email to emailRequired for form display
      website: data.Website || '',

      // Legal Entity Information
      legalEntityType: data.LegalIdentityType || '',
      legalEntityName: data.LegalEntityName ||
        data.PartnershipLegalEntityName ||
        data.LLPLegalEntityName ||
        data.CompaniesActLegalEntityName ||
        data.PublicLtdLegalEntityName ||
        data.GovernmentLegalEntityName ||
        data.TrustLegalEntityName ||
        data.SocietyLegalEntityName ||
        data.OPCLegalEntityName || '',
      ownerName: data.OwnerName || '',
      typeOfFunctioning: data.TypeOfFunctioning || '',

      // Type of Functioning related fields
      hospitalName: data.HospitalName || '',
      hospitalRegistrationCertificateFile: processedFiles.HospitalRegistrationCertificateFile || null,
      hospitalServiceProvider: data.HospitalServiceProvider || '',
      // hospitalAddress and hospitalState are now handled by legalIdentity_Address and legalIdentity_State
      // hospitalPincode is now handled by legalIdentity_Pincode
      outsourceLaboratoryName: data.OutsourceLaboratoryName || '',
      hospitalLabMouFile: processedFiles.HospitalLabMouFile || null,
      outsourceLabAddress: data.OutsourceLabAddress || '',
      outsourceLabState: data.OutsourceLabState || '',
      outsourceLabPincode: data.OutsourceLabPincode || '',
      outsourceServiceProvider: data.OutsourceServiceProvider || '',
      croServiceProvider: data.CroServiceProvider || '',
      othersServiceProvider: data.OthersServiceProvider || '',

      mciRegNumber: data.MCIRegNumber || data.MCIRegisteredNumber || '',
      stateRegistrationNumber: data.StateRegistrationNumber || '',
      panTanNumber: data.PANTANNumber || '',

      // Facility Types (convert boolean to Yes/No strings for form)
      permanentFacility: data.PermanentFacility ? 'Yes' : 'No',
      laboratorySiteTesting: data.SiteFacility ? 'Yes' : 'No',
      poctTesting: data.MobileFacility ? 'Yes' : 'No',
      permanentSiteFacility: data.permanentSiteFacility ? 'Yes' : 'No',
      permanentSiteFacilityAddress: data.permanentSiteFacilityAddress || '',
      isSiteFacility: data.isSiteFacility == 1 ? 'Yes' : 'No',

      // Parent Organization
      isPartOfOrganization: data.IsParentOrgApplicable ? 'Yes' : 'No',
      parentOrganizationName: data.ParentOrgName || '',
      parentMobileNo: data.ParentOrgContactNo || '',
      parentLandlineNo: data.ParentOrgLandlineNo || '',
      parentFaxNo: data.ParentOrgFaxNo || '',
      parentEmail: data.ParentOrgEmailId || '',

      // Accreditation and Categories
      accreditationCategory: data.AccreditationCategory || '',

      // Sample Collection
      sampleCollectionCentre: data.SampleColletion ? 'Yes' : 'No',
      numberOfCollectionCenters: data.NumberOfCollectionCenters || '',
      homeCollection: data.HomeColletion ? 'Yes' : 'No',
      otherSources: data.OtherSource ? 'Yes' : 'No',

      // Additional Facilities
      forMobileVan: data.ForMobileVan ? 'Yes' : 'No',

      // Adverse Actions
      hasAdverseAction: data.HasAdverseAction ? 'Yes' : 'No',
      adverseActionType: data.AdverseActionType || 'Closed',
      adverseDecisionDate: formatDateForInput(data.AdverseDecisionDate),
      previousLabId: data.PreviousLabId || '',

      // SEZ and Consultancy
      isFromSEZ: data.IsFromSEZ ? 'Yes' : 'No',
      hasConsultancy: data.HasConsultancy ? 'Yes' : 'No',
      consultancyName: data.ConsultancyName || '',
      consultancyAddress: data.ConsultancyAddress || '',

      // Internal Audit and Management Review
      internalAuditFromDate: formatDateForInput(data.InternalAuditFromDate),
      internalAuditToDate: formatDateForInput(data.InternalAuditToDate),
      managementReviewFromDate: formatDateForInput(data.ManagementReviewFromDate),
      managementReviewToDate: formatDateForInput(data.ManagementReviewToDate),
      iso15189Audited: data.ISO15189Audited ? 'Yes' : 'No',
      locationsAudited: data.LocationsAudited ? 'Yes' : 'No',
      prePostExamAudited: data.PrePostExamAudited ? 'Yes' : 'No',

      // GST Information
      gstExemption: data.GSTExemption || false,

      // Contact Person Details
      contactPersonSalutation: data.ContactPersonSalutation || 'Dr.',
      contactPersonMobileNo: data.ContactPersonMobileNo || '',
      contactPersonTelephoneNo: data.ContactPersonTelephoneNo || '',
      contactPersonFaxNo: data.ContactPersonFaxNo || '',
      contactPersonEmail1: data.ContactPersonEmail1 || '',
      contactPersonEmail2: data.ContactPersonEmail2 || '',

      // Document Files (use processed files with S3 signed URLs)
      legalIdentityDoc1: processedFiles.LegalIdentityDoc1 || processedFiles.MCICertificateFile || processedFiles.PartnershipMCICertificateFile || null,
      legalIdentityDoc2: processedFiles.LegalIdentityDoc2 || processedFiles.StateCertificateFile || processedFiles.PartnershipStateCertificateFile || null,
      gstExemptionFile: processedFiles.GSTExemptionFile || null,
      partnershipDeedFile: processedFiles.PartnershipDeedFile || null,
      llpCertificateFile: processedFiles.LLPCertificateFile || null,
      companiesActCertificateFile: processedFiles.CompaniesActCertificateFile || null,
      publicLtdCertificateFile: processedFiles.PublicLtdCertificateFile || null,
      governmentCertificateFile: processedFiles.GovernmentCertificateFile || null,
      trustCertificateFile: processedFiles.TrustCertificateFile || null,
      societyCertificateFile: processedFiles.SocietyCertificateFile || null,
      opcCertificateFile: processedFiles.OPCCertificateFile || null,
      sezCertificateFile: processedFiles.SEZCertificateFile || null,
      sampleCollectionDocument: processedFiles.SampleCollectionDocument || null,
      homeCollectionDocument: processedFiles.HomeCollectionDocument || null,
      otherSourcesDocument: processedFiles.OtherSourcesDocument || null,
      internalAuditDocument: processedFiles.InternalAuditDocument || null,
      managementReviewDocument: processedFiles.ManagementReviewDocument || null,

      // Partnership Specific Fields
      partnershipLegalEntityName: data.PartnershipLegalEntityName || '',
      partnershipOwnerName: data.PartnershipOwnerName || '',

      // Registration Numbers (consolidated for all entity types)
      mciRegNumber: data.MCIRegNumber || data.PartnershipMCIRegisteredNumber || '',
      stateRegistrationNumber: data.StateRegistrationNumber || data.PartnershipStateRegistrationNumber || '',

      // Legal Entity Address and State (consolidated for all entity types)
      legalIdentity_Address: data.LegalIdentity_Address || data.CompaniesActAddress || data.PublicLtdAddress || data.OPCAddress || data.HospitalAddress || '',
      legalIdentity_State: data.LegalIdentity_State || data.CompaniesActState || data.HospitalState || '',

      // Legal Entity Pincode (consolidated for all entity types)
      legalIdentity_Pincode: data.LegalIdentity_pincode || data.CompaniesActPincode || data.HospitalPincode || '',

      // Government Specific Fields
      governmentIsAutonomous: data.GovernmentIsAutonomous ? 'Yes' : 'No',

      // Society Specific Fields
      societyIsAutonomous: data.SocietyIsAutonomous ? 'Yes' : 'No'
    };

    // Set form data first (without district/subDistrict)
    setFormData(prev => ({
      ...prev,
      ...newFormData
    }));

    // Load dependent dropdowns if state value exists
    if (newFormData.state) {
      try {
        // Get fresh states data to find the state code
        const statesResult = await LaboratoryService.getStates();
        if (statesResult.success) {
          const selectedState = statesResult.data.find(state => state.id == newFormData.state); // Use loose equality

          if (selectedState) {
            await loadCities(newFormData.state);

            // Set city after loading cities if city value exists
            if (data.City) {
              setTimeout(async () => {
                try {
                  const cityResult = await LaboratoryService.getCitiesByState(newFormData.state);
                  if (cityResult.success) {
                    const cities = cityResult.data;

                    // Try to find city by ID
                    const matchedCity = cities.find(c => c.id === data.City || c.id === parseInt(data.City));

                    if (matchedCity) {
                      setFormData(prev => ({
                        ...prev,
                        city: matchedCity.id.toString()
                      }));
                    }
                  }
                } catch (error) {
                }
              }, 100);
            }

            // Try different possible property names for state code
            const stateCode = selectedState.State_Code || selectedState.code || selectedState.state_code || selectedState.state_id || selectedState.id;

            if (stateCode) {
              // Always load districts for the state
              await loadDistricts(stateCode);
              // After loading districts, try to find and set the correct district if data exists
              if (data.District && data.District.trim() !== '') {
                setTimeout(async () => {
                  try {
                    const districtResult = await LaboratoryService.getDistrictsByState(stateCode);
                    if (districtResult.success) {
                      const districts = districtResult.data;

                      // Try to find district by code first, then by name (case-insensitive)
                      let matchedDistrict = districts.find(d => d.District_Code === parseInt(data.District) || d.District_Code === data.District);

                      if (!matchedDistrict) {
                        matchedDistrict = districts.find(d =>
                          d.District_Name_Eng &&
                          d.District_Name_Eng.toLowerCase().trim() === data.District.toLowerCase().trim()
                        );
                      }

                      // If still not found, try partial matching
                      if (!matchedDistrict) {
                        matchedDistrict = districts.find(d =>
                          d.District_Name_Eng &&
                          (d.District_Name_Eng.toLowerCase().includes(data.District.toLowerCase()) ||
                            data.District.toLowerCase().includes(d.District_Name_Eng.toLowerCase()))
                        );
                      }

                      if (matchedDistrict) {
                        setFormData(prev => ({
                          ...prev,
                          district: matchedDistrict.District_Code
                        }));

                        // Load sub-districts if we have a sub-district value
                        if (data.SubDistrict && data.SubDistrict.trim() !== '') {
                          await loadSubDistricts(matchedDistrict.District_Code);

                          // Try to set sub-district after loading
                          setTimeout(async () => {
                            try {
                              const subDistrictResult = await LaboratoryService.getSubDistrictsByDistrict(matchedDistrict.District_Code);
                              if (subDistrictResult.success) {
                                const subDistricts = subDistrictResult.data;
                                const matchedSubDistrict = subDistricts.find(sd =>
                                  sd.Sub_District_Code === parseInt(data.SubDistrict) ||
                                  sd.Sub_District_Code === data.SubDistrict ||
                                  (sd.Sub_District_Name_Eng && sd.Sub_District_Name_Eng === data.SubDistrict) ||
                                  (sd.Sub_District_Name_Eng && sd.Sub_District_Name_Eng.toLowerCase() === data.SubDistrict.toLowerCase())
                                );

                                if (matchedSubDistrict) {
                                  setFormData(prev => ({
                                    ...prev,
                                    subDistrict: matchedSubDistrict.Sub_District_Code
                                  }));
                                }
                              }
                            } catch (error) {
                            }
                          }, 100);
                        }
                      } else {
                      }
                    }
                  } catch (error) {
                  }
                }, 200); // Increased delay to ensure districts are loaded
              } else {
              }
            } else {
            }
          } else {
          }
        }
      } catch (error) {
      }
    }
  };

  // Map Lab_BasicDetails data to form
  const mapLabBasicData = async (data) => {

    const newFormData = {
      organizationName: data.LaboratoryName || '',
      address: data.AddressLine1 || '',
      state: data.StateId || '',
      city: data.CityId || '',
      district: '', // We'll set this after loading districts
      subDistrict: '', // We'll set this after loading sub-districts  
      pincode: data.Pin || '',
      contactPersonName: data.ContactPerson || '',
      mobileNo: data.ContactMobile || '',
      faxNo: data.Fax || '',
      emailRequired: data.ContactEmail || '',
      contactPersonSalutation: data.Salutation || 'Dr.',
      panTanNumber: data.TAN || '',
      parentOrganizationName: data.ParentOrgName || '',
      parentMobileNo: data.ParentOrgPhone || '',
      parentFaxNo: data.ParentOrgFax || '',
      parentEmail: data.ParentOrgEmail || '',
      laboratoryType: data.LabTypeId || ''
    };

    // Set form data first (without district/subDistrict)
    setFormData(prev => ({
      ...prev,
      ...newFormData
    }));

    // Load dependent dropdowns if state value exists
    if (newFormData.state) {
      try {
        // Get fresh states data to find the state code
        const statesResult = await LaboratoryService.getStates();
        if (statesResult.success) {
          const selectedState = statesResult.data.find(state => state.id == newFormData.state); // Use loose equality
          if (selectedState) {
            await loadCities(newFormData.state);

            // Use the State_Code for districts API
            const stateCode = selectedState.State_Code || selectedState.code || selectedState.state_code;
            await loadDistricts(stateCode);

            // After loading districts, try to find and set the correct district
            if (data.District) {
              setTimeout(async () => {
                try {
                  const districtResult = await LaboratoryService.getDistrictsByState(stateCode);
                  if (districtResult.success) {
                    const districts = districtResult.data;

                    // Try to find district by code first, then by name (case-insensitive)
                    let matchedDistrict = districts.find(d => d.District_Code === data.District);

                    if (!matchedDistrict) {
                      matchedDistrict = districts.find(d =>
                        d.District_Name_Eng &&
                        d.District_Name_Eng.toLowerCase().trim() === data.District.toLowerCase().trim()
                      );
                    }

                    // If still not found, try partial matching
                    if (!matchedDistrict) {
                      matchedDistrict = districts.find(d =>
                        d.District_Name_Eng &&
                        (d.District_Name_Eng.toLowerCase().includes(data.District.toLowerCase()) ||
                          data.District.toLowerCase().includes(d.District_Name_Eng.toLowerCase()))
                      );
                    }

                    if (matchedDistrict) {
                      setFormData(prev => ({
                        ...prev,
                        district: matchedDistrict.District_Code
                      }));

                      // Load sub-districts if we have a sub-district value
                      if (data.SubDistrict) {
                        await loadSubDistricts(matchedDistrict.District_Code);

                        // Try to set sub-district after loading
                        setTimeout(async () => {
                          try {
                            const subDistrictResult = await LaboratoryService.getSubDistrictsByDistrict(matchedDistrict.District_Code);
                            if (subDistrictResult.success) {
                              const subDistricts = subDistrictResult.data;
                              const matchedSubDistrict = subDistricts.find(sd =>
                                sd.Sub_District_Code === data.SubDistrict ||
                                sd.Sub_District_Name_Eng === data.SubDistrict ||
                                sd.Sub_District_Name_Eng.toLowerCase() === data.SubDistrict.toLowerCase()
                              );

                              if (matchedSubDistrict) {
                                setFormData(prev => ({
                                  ...prev,
                                  subDistrict: matchedSubDistrict.Sub_District_Code
                                }));
                              }
                            }
                          } catch (error) {
                          }
                        }, 100);
                      }
                    } else {
                    }
                  }
                } catch (error) {
                }
              }, 100);
            }
          }
        }
      } catch (error) {
      }
    }
  };

  // Load states
  const loadStates = async () => {
    try {
      const result = await LaboratoryService.getStates();
      if (result.success) {
        setStates(result.data);
      }
    } catch (error) {
    }
  };

  // Load cities by state
  const loadCities = async (stateId) => {
    try {
      const result = await LaboratoryService.getCitiesByState(stateId);
      if (result.success) {
        setCities(result.data);
      } else {
        setCities([]);
      }
    } catch (error) {
      setCities([]);
    }
  };

  // Load districts by state code
  const loadDistricts = async (stateCode) => {
    try {
      const result = await LaboratoryService.getDistrictsByState(stateCode);
      if (result.success) {
        setDistricts(result.data);
      }
    } catch (error) {
      setDistricts([]);
    }
  };

  // Load sub-districts by district code
  const loadSubDistricts = async (districtCode) => {
    try {
      const result = await LaboratoryService.getSubDistrictsByDistrict(districtCode);
      if (result.success) {
        setSubDistricts(result.data);
      } else {
        setSubDistricts([]);
      }
    } catch (error) {
      setSubDistricts([]);
    }
  };



  // Save or Update laboratory details
  const saveLaboratoryDetails = async () => {
    // Validate form before saving
    if (!validateForm()) {
      showNotification('Please fix all validation errors before saving', 'error');
      return;
    }

    setLoading(true);
    try {
      // Prepare data with file metadata - fix email field mapping
      const dataToSave = {
        ...formData,
        // Map emailRequired to email for backend compatibility
        email: formData.emailRequired,
        orgId,
        appId,
        lastUpdated: new Date().toISOString(),
        validationStatus: 'validated',
        // Include adequacy check data
        adequacyChecks: adequacyChecks
      };

      const result = await LaboratoryService.saveLaboratoryDetails(orgId, dataToSave);

      if (result.success) {
        // Update state to reflect that data now exists
        if (!hasExistingData) {
          setHasExistingData(true);
          setDataSource('Form153LabDetails_new');
        }

        const message = hasExistingData
          ? 'Laboratory details updated successfully'
          : 'Laboratory details saved successfully';

        showNotification(message, 'success');

        // Clear validation errors on successful save
        setValidationErrors({});
      }
    } catch (error) {
      const message = hasExistingData
        ? 'Error updating laboratory details'
        : 'Error saving laboratory details';

      showNotification(error.message || message, 'error');
    } finally {
      setLoading(false);
    }
  };

  // Show notification
  const showNotification = (message, severity = 'success') => {
    setNotification({ open: true, message, severity });
  };

  // Close notification
  const closeNotification = () => {
    setNotification({ open: false, message: '', severity: 'success' });
  };


  const loadSelectedDisciplines = useCallback(async () => {

    try {

      const isSubUser = localStorage.getItem('isSubUser') === 'true';

      const effectiveOrgId = isSubUser
        ? (localStorage.getItem('mainOrgId') || orgId)
        : orgId;

      console.log(
        '🔍 DisciplineSection - getSelectedDisciplines using appId:',
        appId,
        'orgId:',
        effectiveOrgId,
        'isSubUser:',
        isSubUser
      );

      const result = await LaboratoryService.getSelectedDisciplines(
        appId,
        effectiveOrgId
      );

      if (result.success) {

        let selectedDisciplinesToDisplay = result.data;

        if (isSubUser) {

          try {

            const disciplineIdsString =
              localStorage.getItem('disciplineIds');

            if (disciplineIdsString) {

              const assignedDisciplineIds =
                JSON.parse(disciplineIdsString);

              if (
                Array.isArray(assignedDisciplineIds) &&
                assignedDisciplineIds.length > 0
              ) {

                selectedDisciplinesToDisplay =
                  result.data.filter(discipline => {

                    const disciplineId = parseInt(
                      discipline.DisciplineId ||
                      discipline.disciplineId ||
                      discipline.Id ||
                      discipline.id
                    );

                    return assignedDisciplineIds.includes(
                      disciplineId
                    );
                  });
              }
            }

          } catch (error) {
            console.error(
              'Error filtering selected disciplines for subuser:',
              error
            );
          }
        }

        setSelectedDisciplines(selectedDisciplinesToDisplay);

      } else {

        setSelectedDisciplines([]);
      }

    } catch (error) {

      console.error(
        'Error loading selected disciplines:',
        error
      );

      setSelectedDisciplines([]);

      showNotification(
        'Error loading selected disciplines: ' + error.message,
        'error'
      );
    }

  }, [orgId, appId]);

  useEffect(() => {
    loadSelectedDisciplines();
  }, [
    loadSelectedDisciplines
  ])


  const handleChange = (field, value) => {
    // Check if this specific field should be read-only
    const sectionId = getFieldSectionId(field);
    if (isFieldReadOnly(sectionId)) {
      return; // Don't allow changes in read-only mode
    }

    if (
      field === 'accreditationCategory' &&
      (value === 'Micro Laboratories (Up to 25 patients/ day)' || value === 'Mini Laboratories (26 - 50 patients/ day)')
    ) {

      const disciplineCount = selectedDisciplines?.length || 0;

      if (disciplineCount > 2) {
        setNotification({
          open: true,
          message:
            'You cannot change the category to this because you have added more than 2 disciplines in your discipline section.',
          severity: 'error'
        });

        return;
      }
    }


    setFormData(prev => ({
      ...prev,
      [field]: value
    }));

    // Real-time validation
    const error = validateField(field, value);
    setValidationErrors(prev => ({
      ...prev,
      [field]: error
    }));

    // Clear GST validation if exemption is selected
    if (field === 'gstExemption' && value === true) {
      setValidationErrors(prev => ({
        ...prev,
        gstNumber: ''
      }));
    }

    // Load cities and districts when state changes
    if (field === 'state' && value) {
      // Find the selected state object to get the state code
      const selectedState = states.find(state => state.id == value); // Use loose equality
      if (selectedState) {
        loadCities(value);

        // Use State_Code for districts
        const stateCode = selectedState.State_Code || selectedState.code || selectedState.state_code;
        loadDistricts(stateCode);
        // Clear dependent selections when state changes
        setFormData(prev => ({
          ...prev,
          city: '',
          district: '',
          subDistrict: ''
        }));
        setCities([]);
        setSubDistricts([]);
      }
    }

    // Load sub-districts when district changes
    if (field === 'district' && value) {
      // Find the selected district object to get the district code
      const selectedDistrict = districts.find(district => district.District_Code === value);
      if (selectedDistrict) {
        loadSubDistricts(selectedDistrict.District_Code);
        // Clear sub-district selection when district changes
        setFormData(prev => ({
          ...prev,
          subDistrict: ''
        }));
      }
    }

    // Call onUpdate if provided
    if (onUpdate) {
      onUpdate({ ...formData, [field]: value });
    }
  };

  const handleFileUpload = (event) => {
    const file = event.target.files[0];
    setFormData(prev => ({
      ...prev,
      gstExemptionFile: file
    }));
  };

  // Handle S3 file upload callback (now using C# style implementation)
  // Files are saved with naming pattern: filename_timestamp.ext
  // URLs are direct S3 URLs: https://nabl7t.s3.ap-south-1.amazonaws.com/live/filename_timestamp.ext
  const handleS3FileUpload = (fieldName, uploadData) => {
    // Check if this specific field should be read-only
    const sectionId = getFieldSectionId(fieldName);
    if (isFieldReadOnly(sectionId)) {
      return; // Don't allow file uploads in read-only mode
    }

    if (uploadData === null || uploadData === undefined) {
      // File was deleted, remove from form data
      setFormData(prev => ({
        ...prev,
        [fieldName]: null
      }));
      return;
    }

    const fileData = {
      ...uploadData,
      fileName: uploadData.fileName || uploadData.originalName,
      filePath: uploadData.filePath || uploadData.url,
      uploadDate: new Date().toISOString(),
      fileSize: uploadData.fileSize,
      mimeType: uploadData.mimeType
    };

    setFormData(prev => ({
      ...prev,
      [fieldName]: fileData
    }));

    // Clear file validation error if upload successful
    if (uploadData && uploadData.fileName) {
      setValidationErrors(prev => ({
        ...prev,
        [fieldName]: ''
      }));
    }

    console.log(`File uploaded for ${fieldName} using C# style implementation:`, fileData);
  };

  // Example: Upload file from base64 string (C# style)
  // This demonstrates how to use the new base64 upload functionality
  const handleBase64FileUpload = async (fieldName, base64Content, filename, contentType, extension) => {
    try {
      const s3Service = await import('../../../services/s3Service').then(module => module.default);
      const s3ServiceInstance = new s3Service();

      const result = await s3ServiceInstance.uploadFileFromBase64(
        filename,      // 'TestDocument' 
        base64Content, // base64 string without data: prefix
        contentType,   // 'application/pdf'
        extension      // '.pdf'
      );

      if (result.success) {
        // Handle successful upload with C# style response
        const fileData = {
          ...result.data,
          fileName: result.data.imgName,        // C# style imgName: TestDocument_20251011140223.pdf
          filePath: result.data.url,            // Direct S3 URL
          uploadDate: new Date().toISOString(),
          fileSize: base64Content.length * 0.75, // Approximate file size from base64
          mimeType: contentType
        };

        setFormData(prev => ({
          ...prev,
          [fieldName]: fileData
        }));

        console.log(`Base64 file uploaded for ${fieldName} (C# style):`, fileData);
        return result;
      } else {
        console.error('Base64 upload failed:', result.error);
        return result;
      }
    } catch (error) {
      console.error('Base64 upload error:', error);
      return { success: false, error: error.message };
    }
  };

  // Handle adequacy check change with useCallback to prevent re-renders
  const handleAdequacyCheck = useCallback((sectionId, value) => {
    setAdequacyChecks(prev => {
      const current = prev[sectionId] || {};
      return {
        ...prev,
        [sectionId]: {
          ...current,
          status: value,
          remarks: value === 'Adequate' ? '' : (current.remarks || '')
        }
      };
    });
  }, []);

  // Handle adequacy remarks change with useCallback to prevent re-renders
  const handleAdequacyRemarks = useCallback((sectionId, remarks) => {
    setAdequacyChecks(prev => {
      const current = prev[sectionId] || {};
      // Only update if the value actually changed
      if (current.remarks === remarks) {
        return prev;
      }
      return {
        ...prev,
        [sectionId]: {
          ...current,
          remarks: remarks
        }
      };
    });
  }, []);

  // Handle adequacy check submission
  const handleAdequacySubmit = async (sectionId) => {
    const adequacy = adequacyChecks[sectionId];

    if (!adequacy?.status) {
      showNotification('Please select a status before submitting', 'error');
      return;
    }

    if (adequacy.status === 'Inadequate' && (!adequacy.remarks || adequacy.remarks.trim() === '')) {
      showNotification('Please provide remarks for inadequate status', 'error');
      return;
    }

    try {
      setLoading(true);

      // Here you can add API call to save adequacy check
      const adequacyData = {
        sectionId: sectionId,
        status: adequacy.status,
        remarks: adequacy.remarks,
        orgId: orgId,
        appId: appId,
        submittedAt: new Date().toISOString(),
        submittedBy: 'current_user',
        assessmentId: assessmentId,
        assessorId: assessorId
      };

      console.log('Submitting adequacy check:', adequacyData);

      // Update the adequacy check to mark it as submitted
      setAdequacyChecks(prev => ({
        ...prev,
        [sectionId]: {
          ...prev[sectionId],
          isSubmitted: true,
          submittedAt: new Date().toISOString()
        }
      }));

      showNotification(`Adequacy check for "${sectionId}" submitted successfully`, 'success');

    } catch (error) {
      console.error('Error submitting adequacy check:', error);
      showNotification('Failed to submit adequacy check', 'error');
    } finally {
      setLoading(false);
    }
  };

  // Function to render form based on legal entity type
  const renderLegalEntityForm = () => {
    const { legalEntityType } = formData;

    if (legalEntityType === 'Proprietorship') {
      return renderProprietorshipForm();
    } else if (legalEntityType === 'Partnership') {
      return renderPartnershipForm();
    } else if (legalEntityType === 'LLP') {
      return renderLLPForm();
    } else if (legalEntityType === 'Companies Act') {
      return renderCompaniesActForm();
    } else if (legalEntityType === 'Public Ltd') {
      return renderPublicLtdForm();
    } else if (legalEntityType === 'Government') {
      return renderGovernmentForm();
    } else if (legalEntityType === 'Trust') {
      return renderTrustForm();
    } else if (legalEntityType === 'Society') {
      return renderSocietyForm();
    } else if (legalEntityType === 'One Person Company') {
      return renderOnePersonCompanyForm();
    } else {
      return (
        <Grid item xs={12}>
          <Alert severity="warning" sx={{ borderRadius: 2 }}>
            Please select a legal entity type to view the form.
          </Alert>
        </Grid>
      );
    }
  };

  // Proprietorship form
  const renderProprietorshipForm = () => (
    <>
      <Grid item xs={12} md={4}>
        <TextField
          {...getTextFieldProps('legalEntityName', 'Legal Entity Name', { required: true })}
        />
      </Grid>

      <Grid item xs={12} md={2}>
        <TextField
          fullWidth
          value="DR"
          disabled
          placeholder="DR"
          sx={{
            '& .MuiOutlinedInput-root': {
              backgroundColor: '#f5f5f5',
              borderRadius: 2,
            }
          }}
        />
      </Grid>

      <Grid item xs={12} md={6}>
        <TextField
          {...getTextFieldProps('ownerName', 'Name of the Owner', { required: true })}
        />
      </Grid>

      {/* MCI/NMC Details Section */}
      <Grid item xs={12}>
        <Typography variant="h6" sx={{
          mt: 2,
          mb: 2,
          color: '#667eea',
          fontWeight: 600,
          borderBottom: '2px solid #667eea',
          paddingBottom: 1
        }}>
          MCI/NMC Details <Typography component="span" sx={{ fontSize: '0.8rem', fontWeight: 400, color: '#ff9800' }}>
            (Either MCI/NMC details OR State registration details must be provided)
          </Typography>
        </Typography>
      </Grid>

      <Grid item xs={12} md={6}>
        <TextField
          fullWidth
          label="MCI/NMC Registered Number"
          value={formData.mciRegNumber || ''}
          onChange={(e) => handleChange('mciRegNumber', e.target.value)}
          placeholder="Enter MCI/NMC Number"
          error={!!validationErrors.mciRegNumber}
          helperText={validationErrors.mciRegNumber}
          sx={{
            '& .MuiOutlinedInput-root': {
              backgroundColor: 'white',
              borderRadius: 2,
              '&:hover': {
                boxShadow: '0 4px 8px rgba(0,0,0,0.1)',
              },
              '&.Mui-focused': {
                boxShadow: '0 4px 12px rgba(25, 118, 210, 0.2)',
              }
            }
          }}
        />
      </Grid>

      <Grid item xs={12} md={6}>
        {/* FileUploadComponent now uses C# style implementation:
            - Files saved as: filename_timestamp.ext (e.g., MCICertificate_20251011140223.pdf)
            - Direct S3 URLs: https://nabl7t.s3.ap-south-1.amazonaws.com/live/filename_timestamp.ext
            - Backend endpoints: /api/upload/single, /api/upload/multiple, /api/upload/base64
            - No expiry on URLs, publicly accessible (matching PHP getObjectUrl behavior) */}
        <FileUploadComponent
          {...getFileUploadProps({
            fieldName: "legalIdentityDoc1",
            label: "MCI/NMC Certificate",
            orgId: orgId,
            section: "medical-certificates",
            required: false,
            accept: ".pdf,.jpg,.jpeg,.png,.doc,.docx",
            maxSize: 10,
            onChange: handleS3FileUpload,
            initialFile: formData.legalIdentityDoc1,
            error: !!validationErrors.legalIdentityDoc1,
            helperText: validationErrors.legalIdentityDoc1,
            disabled: isFieldReadOnly('legalIdentityDoc1')
          })}
        />
      </Grid>

      {/* State Registration Details Section */}
      <Grid item xs={12}>
        <Typography variant="h6" sx={{
          mt: 2,
          mb: 2,
          color: '#667eea',
          fontWeight: 600,
          borderBottom: '2px solid #667eea',
          paddingBottom: 1
        }}>
          State Registration Details <Typography component="span" sx={{ fontSize: '0.8rem', fontWeight: 400, color: '#ff9800' }}>
            (Alternative to MCI/NMC details above)
          </Typography>
        </Typography>
      </Grid>

      <Grid item xs={12} md={6}>
        <TextField
          fullWidth
          label="State Registration Certificate Number"
          value={formData.stateRegistrationNumber || ''}
          onChange={(e) => handleChange('stateRegistrationNumber', e.target.value)}
          placeholder="Enter Certificate Number"
          error={!!validationErrors.stateRegistrationNumber}
          helperText={validationErrors.stateRegistrationNumber}
          sx={{
            '& .MuiOutlinedInput-root': {
              backgroundColor: 'white',
              borderRadius: 2,
              '&:hover': {
                boxShadow: '0 4px 8px rgba(0,0,0,0.1)',
              },
              '&.Mui-focused': {
                boxShadow: '0 4px 12px rgba(25, 118, 210, 0.2)',
              }
            }
          }}
        />
      </Grid>

      <Grid item xs={12} md={6}>
        <FileUploadComponent
          fieldName="legalIdentityDoc2"
          label="State Registration Certificate"
          orgId={orgId}
          section="medical-certificates"
          required={false}
          accept=".pdf,.jpg,.jpeg,.png,.doc,.docx"
          maxSize={10}
          onChange={handleS3FileUpload}
          initialFile={formData.legalIdentityDoc2}
          error={!!validationErrors.legalIdentityDoc2}
          helperText={validationErrors.legalIdentityDoc2}
          disabled={isFieldReadOnly('legal-entity-type')}
        />
      </Grid>
    </>
  );

  // Partnership form
  const renderPartnershipForm = () => (
    <>
      <Grid item xs={12} md={4}>
        <TextField
          {...getTextFieldProps('legalEntityName', 'Legal Entity Name', { required: true })}
        />
      </Grid>

      <Grid item xs={12} md={2}>
        <TextField
          fullWidth
          value="DR"
          disabled
          placeholder="DR"
          sx={{
            '& .MuiOutlinedInput-root': {
              backgroundColor: '#f5f5f5',
              borderRadius: 2,
            }
          }}
        />
      </Grid>

      <Grid item xs={12} md={4}>
        <TextField
          {...getTextFieldProps('partnershipOwnerName', 'Name of the Owner', { required: true })}
        />
      </Grid>

      <Grid item xs={6}>
        <FileUploadComponent
          fieldName="partnershipDeedFile"
          label="Upload Partnership Deed"
          orgId={orgId}
          section="legal-documents"
          required={true}
          accept=".pdf,.jpg,.jpeg,.png,.doc,.docx"
          maxSize={10}
          onChange={handleS3FileUpload}
          initialFile={formData.partnershipDeedFile}
          disabled={isFieldReadOnly('legal-entity-type')}
        />
      </Grid>

      {/* MCI/NMC Details Section */}
      <Grid item xs={12}>
        <Typography variant="h6" sx={{
          mt: 2,
          mb: 2,
          color: '#667eea',
          fontWeight: 600,
          borderBottom: '2px solid #667eea',
          paddingBottom: 1
        }}>
          MCI/NMC Details <Typography component="span" sx={{ fontSize: '0.8rem', fontWeight: 400, color: '#ff9800' }}>
            (Either MCI/NMC details OR State registration details must be provided)
          </Typography>
        </Typography>
      </Grid>

      <Grid item xs={12} md={6}>
        <TextField
          fullWidth
          label="MCI/NMC Registered Number"
          value={formData.mciRegNumber || ''}
          onChange={(e) => handleChange('mciRegNumber', e.target.value)}
          placeholder="Enter MCI/NMC Number"
          error={!!validationErrors.mciRegNumber}
          helperText={validationErrors.mciRegNumber}
          sx={{
            '& .MuiOutlinedInput-root': {
              backgroundColor: 'white',
              borderRadius: 2,
              '&:hover': {
                boxShadow: '0 4px 8px rgba(0,0,0,0.1)',
              },
              '&.Mui-focused': {
                boxShadow: '0 4px 12px rgba(25, 118, 210, 0.2)',
              }
            }
          }}
        />
      </Grid>

      <Grid item xs={12} md={6}>
        <FileUploadComponent
          fieldName="legalIdentityDoc1"
          label="MCI/NMC Certificate"
          orgId={orgId}
          section="medical-certificates"
          required={false}
          accept=".pdf,.jpg,.jpeg,.png,.doc,.docx"
          maxSize={10}
          onChange={handleS3FileUpload}
          initialFile={formData.legalIdentityDoc1}
          error={!!validationErrors.legalIdentityDoc1}
          helperText={validationErrors.legalIdentityDoc1}
          disabled={isFieldReadOnly('legal-entity-type')}
        />
      </Grid>

      {/* State Registration Details Section */}
      <Grid item xs={12}>
        <Typography variant="h6" sx={{
          mt: 2,
          mb: 2,
          color: '#667eea',
          fontWeight: 600,
          borderBottom: '2px solid #667eea',
          paddingBottom: 1
        }}>
          State Registration Details <Typography component="span" sx={{ fontSize: '0.8rem', fontWeight: 400, color: '#ff9800' }}>
            (Alternative to MCI/NMC details above)
          </Typography>
        </Typography>
      </Grid>

      <Grid item xs={12} md={6}>
        <TextField
          fullWidth
          label="State Registration Certificate Number"
          value={formData.stateRegistrationNumber || ''}
          onChange={(e) => handleChange('stateRegistrationNumber', e.target.value)}
          placeholder="Enter Certificate Number"
          error={!!validationErrors.stateRegistrationNumber}
          helperText={validationErrors.stateRegistrationNumber}
          sx={{
            '& .MuiOutlinedInput-root': {
              backgroundColor: 'white',
              borderRadius: 2,
              '&:hover': {
                boxShadow: '0 4px 8px rgba(0,0,0,0.1)',
              },
              '&.Mui-focused': {
                boxShadow: '0 4px 12px rgba(25, 118, 210, 0.2)',
              }
            }
          }}
        />
      </Grid>

      <Grid item xs={12} md={6}>
        <FileUploadComponent
          fieldName="legalIdentityDoc2"
          label="State Registration Certificate"
          orgId={orgId}
          section="medical-certificates"
          required={false}
          accept=".pdf,.jpg,.jpeg,.png,.doc,.docx"
          maxSize={10}
          onChange={handleS3FileUpload}
          initialFile={formData.legalIdentityDoc2}
          error={!!validationErrors.legalIdentityDoc2}
          helperText={validationErrors.legalIdentityDoc2}
          disabled={isFieldReadOnly('legal-entity-type')}
        />
      </Grid>
    </>
  );

  // LLP form
  const renderLLPForm = () => (
    <>
      <Grid item xs={12} md={5}>
        <TextField
          fullWidth
          label={

            '   Legal Entity Name'


          }
          value={formData.legalEntityName || ''}
          onChange={(e) => handleChange('legalEntityName', e.target.value)}
          placeholder="Enter Details.."
          required
          sx={{
            '& .MuiOutlinedInput-root': {
              backgroundColor: 'white',
              borderRadius: 2,
              '&:hover': {
                boxShadow: '0 4px 8px rgba(0,0,0,0.1)',
              },
              '&.Mui-focused': {
                boxShadow: '0 4px 12px rgba(25, 118, 210, 0.2)',
              }
            }
          }}
        />
      </Grid>

      <Grid item xs={12} md={7}>
        <FileUploadComponent
          fieldName="llpCertificateFile"
          label="Upload LLP Certificate"
          orgId={orgId}
          section="legal-documents"
          required={true}
          accept=".pdf,.jpg,.jpeg,.png,.doc,.docx"
          maxSize={10}
          onChange={handleS3FileUpload}
          initialFile={formData.llpCertificateFile}
          disabled={isFieldReadOnly('legal-entity-type')}
        />
      </Grid>
    </>
  );

  // Companies Act form
  const renderCompaniesActForm = () => (
    <>
      <Grid item xs={12} md={4}>
        <TextField
          fullWidth
          label={

            '   Legal Entity Name'


          }
          value={formData.legalEntityName || ''}
          onChange={(e) => handleChange('legalEntityName', e.target.value)}
          placeholder="Enter Details.."
          required
          sx={{
            '& .MuiOutlinedInput-root': {
              backgroundColor: 'white',
              borderRadius: 2,
              '&:hover': { boxShadow: '0 4px 8px rgba(0,0,0,0.1)' },
              '&.Mui-focused': { boxShadow: '0 4px 12px rgba(25, 118, 210, 0.2)' }
            }
          }}
        />
      </Grid>

      <Grid item xs={12} md={6}>
        <FileUploadComponent
          fieldName="companiesActCertificateFile"
          label="Upload Companies Act Certificate"
          orgId={orgId}
          section="legal-documents"
          required={true}
          accept=".pdf,.jpg,.jpeg,.png,.doc,.docx"
          maxSize={10}
          onChange={handleS3FileUpload}
          initialFile={formData.companiesActCertificateFile}
          disabled={isFieldReadOnly('legal-entity-type')}
        />
      </Grid>

      <Grid item xs={12}>
        <Typography variant="h6" sx={{
          mt: 2, mb: 2, color: '#667eea', fontWeight: 600,
          borderBottom: '2px solid #667eea', paddingBottom: 1
        }}>
          Address Details
        </Typography>
      </Grid>

      <Grid item xs={12} md={4}>
        <TextField
          fullWidth
          label={<Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            Registered Office Address
          </Box>}
          value={formData.legalIdentity_Address || ''}
          onChange={(e) => handleChange('legalIdentity_Address', e.target.value)}
          placeholder="Enter Details.."
          required
          sx={{ '& .MuiOutlinedInput-root': { backgroundColor: 'white', borderRadius: 2 } }}
        />
      </Grid>

      <Grid item xs={12} md={4}>
        <TextField
          fullWidth
          label={<Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            State
          </Box>}
          value={formData.legalIdentity_State || ''}
          onChange={(e) => handleChange('legalIdentity_State', e.target.value)}
          placeholder="Enter State.."
          required
          sx={{ '& .MuiOutlinedInput-root': { backgroundColor: 'white', borderRadius: 2 } }}
        />
      </Grid>

      <Grid item xs={12} md={4}>
        <TextField
          fullWidth
          label={<Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            Pincode
          </Box>}
          value={formData.legalIdentity_Pincode || ''}
          onChange={(e) => handleNumericChange('legalIdentity_Pincode', e.target.value, 6)}
          placeholder="Enter 6-digit pincode"
          inputProps={{ maxLength: 6 }}
          required
          sx={{ '& .MuiOutlinedInput-root': { backgroundColor: 'white', borderRadius: 2 } }}
        />
      </Grid>
    </>
  );

  // Public Ltd form
  const renderPublicLtdForm = () => (
    <>
      <Grid item xs={12} md={4}>
        <TextField
          fullWidth
          label={<Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            Legal Entity Name
          </Box>}
          value={formData.legalEntityName || ''}
          onChange={(e) => handleChange('legalEntityName', e.target.value)}
          placeholder="Enter Details.."
          required
          sx={{ '& .MuiOutlinedInput-root': { backgroundColor: 'white', borderRadius: 2 } }}
        />
      </Grid>

      <Grid item xs={12} md={4}>
        <FileUploadComponent
          fieldName="publicLtdCertificateFile"
          label="Upload Public Ltd Certificate"
          orgId={orgId}
          section="legal-documents"
          required={true}
          accept=".pdf,.jpg,.jpeg,.png,.doc,.docx"
          maxSize={10}
          onChange={handleS3FileUpload}
          initialFile={formData.publicLtdCertificateFile}
          disabled={isFieldReadOnly('legal-entity-type')}
        />
      </Grid>

      <Grid item xs={12}>
        <Typography variant="h6" sx={{
          mt: 2, mb: 2, color: '#667eea', fontWeight: 600,
          borderBottom: '2px solid #667eea', paddingBottom: 1
        }}>
          Office Address
        </Typography>
      </Grid>

      <Grid item xs={12} md={4}>
        <TextField
          fullWidth
          label={<Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            Registered Office Address
          </Box>}
          value={formData.legalIdentity_Address || ''}
          onChange={(e) => handleChange('legalIdentity_Address', e.target.value)}
          placeholder="Enter Details.."
          required
          sx={{ '& .MuiOutlinedInput-root': { backgroundColor: 'white', borderRadius: 2 } }}
        />
      </Grid>
    </>
  );

  // Government form
  const renderGovernmentForm = () => (
    <>
      <Grid item xs={12} md={4}>
        <TextField
          fullWidth
          label={<Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            Legal Entity Name
          </Box>}
          value={formData.legalEntityName || ''}
          onChange={(e) => handleChange('legalEntityName', e.target.value)}
          placeholder="Enter Details.."
          required
          sx={{ '& .MuiOutlinedInput-root': { backgroundColor: 'white', borderRadius: 2 } }}
        />
      </Grid>

      <Grid item xs={12} md={4}>
        <Box>
          <Typography variant="body2" sx={{ mb: 2, color: '#666', fontWeight: 500 }}>
            Are you an Autonomous Body?<span style={{ color: 'red', fontWeight: 'bold' }}>*</span>
          </Typography>
          <Box sx={{ display: 'flex', gap: 2 }}>
            <FormControlLabel
              control={
                <input
                  {...getInputProps('governmentIsAutonomous', 'Yes')}
                />
              }
              label="Yes"
            />
            <FormControlLabel
              control={
                <input
                  {...getInputProps('governmentIsAutonomous', 'No')}
                />
              }
              label="No"
            />
          </Box>
        </Box>
      </Grid>

      <Grid item xs={12} md={6}>
        <FileUploadComponent
          fieldName="governmentCertificateFile"
          label="Upload Gazette or Government Notification or self-Declaration on letter head by Head of the organization"
          orgId={orgId}
          section="legal-documents"
          required={true}
          accept=".pdf,.jpg,.jpeg,.png,.doc,.docx"
          maxSize={10}
          onChange={handleS3FileUpload}
          initialFile={formData.governmentCertificateFile}
          disabled={isFieldReadOnly('legal-entity-type')}
        />
      </Grid>
    </>
  );

  // Trust form
  const renderTrustForm = () => (
    <>
      <Grid item xs={12} md={4}>
        <TextField
          fullWidth
          label={<Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            Legal Entity Name
          </Box>}
          value={formData.legalEntityName || ''}
          onChange={(e) => handleChange('legalEntityName', e.target.value)}
          placeholder="Enter Details.."
          required
          sx={{ '& .MuiOutlinedInput-root': { backgroundColor: 'white', borderRadius: 2 } }}
        />
      </Grid>

      <Grid item xs={12} md={6}>
        <FileUploadComponent
          fieldName="trustCertificateFile"
          label="Upload Trust Certificate/Deed"
          orgId={orgId}
          section="legal-documents"
          required={true}
          accept=".pdf,.jpg,.jpeg,.png,.doc,.docx"
          maxSize={10}
          onChange={handleS3FileUpload}
          initialFile={formData.trustCertificateFile}
          disabled={isFieldReadOnly('legal-entity-type')}
        />
      </Grid>
    </>
  );

  // Society form
  const renderSocietyForm = () => (
    <>
      <Grid item xs={12} md={5}>
        <TextField
          fullWidth
          label={<Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            Legal Entity Name
          </Box>}
          value={formData.legalEntityName || ''}
          onChange={(e) => handleChange('legalEntityName', e.target.value)}
          placeholder="Enter Details.."
          required
          sx={{ '& .MuiOutlinedInput-root': { backgroundColor: 'white', borderRadius: 2 } }}
        />
      </Grid>

      <Grid item xs={12} md={6}>
        <FileUploadComponent
          fieldName="societyCertificateFile"
          label="Upload Society Certificate"
          orgId={orgId}
          section="legal-documents"
          required={true}
          accept=".pdf,.jpg,.jpeg,.png,.doc,.docx"
          maxSize={10}
          onChange={handleS3FileUpload}
          initialFile={formData.societyCertificateFile}
          disabled={isFieldReadOnly('legal-entity-type')}
        />
      </Grid>

      <Grid item xs={12} md={4}>
        <Box>
          <Typography variant="body2" sx={{ mb: 2, color: '#666', fontWeight: 500 }}>
            Are you an Autonomous Body?<span style={{ color: 'red', fontWeight: 'bold' }}>*</span>
          </Typography>
          <Box sx={{ display: 'flex', gap: 2 }}>
            <FormControlLabel
              control={
                <input
                  type="radio"
                  name="societyAutonomous"
                  value="Yes"
                  checked={formData.societyIsAutonomous === 'Yes'}
                  onChange={(e) => handleChange('societyIsAutonomous', e.target.value)}
                />
              }
              label="Yes"
            />
            <FormControlLabel
              control={
                <input
                  type="radio"
                  name="societyAutonomous"
                  value="No"
                  checked={formData.societyIsAutonomous === 'No'}
                  onChange={(e) => handleChange('societyIsAutonomous', e.target.value)}
                />
              }
              label="No"
            />
          </Box>
        </Box>
      </Grid>
    </>
  );

  // One Person Company form
  const renderOnePersonCompanyForm = () => (
    <>
      <Grid item xs={12} md={5}>
        <TextField
          fullWidth
          label={<Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            Legal Entity Name
          </Box>}
          value={formData.legalEntityName || ''}
          onChange={(e) => handleChange('legalEntityName', e.target.value)}
          placeholder="Enter Details.."
          sx={{ '& .MuiOutlinedInput-root': { backgroundColor: 'white', borderRadius: 2 } }}
        />
      </Grid>

      <Grid item xs={12} md={4}>
        <TextField
          fullWidth
          label={<Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            Registered Office Address
          </Box>}
          value={formData.legalIdentity_Address || ''}
          onChange={(e) => handleChange('legalIdentity_Address', e.target.value)}
          placeholder="Enter Details.."
          sx={{ '& .MuiOutlinedInput-root': { backgroundColor: 'white', borderRadius: 2 } }}
        />
      </Grid>

      <Grid item xs={12} md={6}>
        <FileUploadComponent
          fieldName="opcCertificateFile"
          label="Upload One Person Company Certificate"
          orgId={orgId}
          section="legal-documents"
          required={true}
          accept=".pdf,.jpg,.jpeg,.png,.doc,.docx"
          maxSize={10}
          onChange={handleS3FileUpload}
          initialFile={formData.opcCertificateFile}
          disabled={isFieldReadOnly('legal-entity-type')}
        />
      </Grid>
    </>
  );

  // Function to render form based on type of functioning
  const renderTypeOfFunctioningForm = () => {
    const { typeOfFunctioning } = formData;

    if (typeOfFunctioning === 'hospital-owned') {
      return renderHospitalOwnedForm();
    } else if (typeOfFunctioning === 'hospital-outsource') {
      return renderHospitalOutsourceForm();
    } else if (typeOfFunctioning === 'cro-lab') {
      return renderCROLabForm();
    } else if (typeOfFunctioning === 'others') {
      return renderOthersForm();
    } else {
      return (
        <Grid item xs={12}>
          <Alert severity="warning" sx={{ borderRadius: 2 }}>
            Please select a type of functioning to view the form.
          </Alert>
        </Grid>
      );
    }
  };

  // Hospital Owned form
  const renderHospitalOwnedForm = () => (
    <>
      <Grid item xs={12} md={6}>
        <TextField
          fullWidth
          label="Name of Hospital"
          value={formData.hospitalName || ''}
          onChange={(e) => handleChange('hospitalName', e.target.value)}
          placeholder="Enter Details.."
          required
          sx={{
            '& .MuiOutlinedInput-root': {
              backgroundColor: 'white',
              borderRadius: 2,
              '&:hover': {
                boxShadow: '0 4px 8px rgba(0,0,0,0.1)',
              },
              '&.Mui-focused': {
                boxShadow: '0 4px 12px rgba(25, 118, 210, 0.2)',
              }
            }
          }}
        />
      </Grid>

      <Grid item xs={12} md={6}>
        <FileUploadComponent
          fieldName="hospitalRegistrationCertificateFile"
          label="Upload Hospital Registration Certificate"
          orgId={orgId}
          section="hospital-documents"
          required={true}
          accept=".pdf,.jpg,.jpeg,.png,.doc,.docx"
          maxSize={10}
          onChange={handleS3FileUpload}
          initialFile={formData.hospitalRegistrationCertificateFile}
          disabled={isFieldReadOnly('type-of-functioning')}
        />
      </Grid>

      <Grid item xs={12} md={6}>
        <ServiceProviderDropdown fieldName="hospitalServiceProvider" />
      </Grid>

      {/* Address Details Section */}
      <Grid item xs={12}>
        <Typography variant="h6" sx={{
          mt: 2,
          mb: 2,
          color: '#667eea',
          fontWeight: 600,
          borderBottom: '2px solid #667eea',
          paddingBottom: 1
        }}>
          Address Details
        </Typography>
      </Grid>

      <Grid item xs={12} md={4}>
        <TextField
          fullWidth
          label="Address of Hospital"
          value={formData.legalIdentity_Address || ''}
          onChange={(e) => handleChange('legalIdentity_Address', e.target.value)}
          placeholder="Enter Address.."
          required
          sx={{
            '& .MuiOutlinedInput-root': {
              backgroundColor: 'white',
              borderRadius: 2,
              '&:hover': {
                boxShadow: '0 4px 8px rgba(0,0,0,0.1)',
              },
              '&.Mui-focused': {
                boxShadow: '0 4px 12px rgba(25, 118, 210, 0.2)',
              }
            }
          }}
        />
      </Grid>

      <Grid item xs={12} md={4}>
        <TextField
          fullWidth
          label="State"
          value={formData.legalIdentity_State || ''}
          onChange={(e) => handleChange('legalIdentity_State', e.target.value)}
          placeholder="Enter State.."
          required
          sx={{
            '& .MuiOutlinedInput-root': {
              backgroundColor: 'white',
              borderRadius: 2,
              '&:hover': {
                boxShadow: '0 4px 8px rgba(0,0,0,0.1)',
              },
              '&.Mui-focused': {
                boxShadow: '0 4px 12px rgba(25, 118, 210, 0.2)',
              }
            }
          }}
        />
      </Grid>

      <Grid item xs={12} md={4}>
        <TextField
          fullWidth
          label="Pincode"
          value={formData.legalIdentity_Pincode || ''}
          onChange={(e) => handleNumericChange('legalIdentity_Pincode', e.target.value, 6)}
          placeholder="Enter 6-digit pincode"
          inputProps={{ maxLength: 6 }}
          required
          sx={{
            '& .MuiOutlinedInput-root': {
              backgroundColor: 'white',
              borderRadius: 2,
              '&:hover': {
                boxShadow: '0 4px 8px rgba(0,0,0,0.1)',
              },
              '&.Mui-focused': {
                boxShadow: '0 4px 12px rgba(25, 118, 210, 0.2)',
              }
            }
          }}
        />
      </Grid>
    </>
  );

  // Hospital Outsource form
  const renderHospitalOutsourceForm = () => (
    <>
      <Grid item xs={12} md={6}>
        <TextField
          fullWidth
          label="Name of the Outsource Laboratory"
          value={formData.outsourceLaboratoryName || ''}
          onChange={(e) => handleChange('outsourceLaboratoryName', e.target.value)}
          placeholder="Enter Details.."
          required
          sx={{
            '& .MuiOutlinedInput-root': {
              backgroundColor: 'white',
              borderRadius: 2,
              '&:hover': {
                boxShadow: '0 4px 8px rgba(0,0,0,0.1)',
              },
              '&.Mui-focused': {
                boxShadow: '0 4px 12px rgba(25, 118, 210, 0.2)',
              }
            }
          }}
        />
      </Grid>

      <Grid item xs={12} md={6}>
        <FileUploadComponent
          fieldName="hospitalLabMouFile"
          label="Upload Hospital and Laboratory MOU"
          orgId={orgId}
          section="hospital-documents"
          required={true}
          accept=".pdf,.jpg,.jpeg,.png,.doc,.docx"
          maxSize={10}
          onChange={handleS3FileUpload}
          initialFile={formData.hospitalLabMouFile}
          disabled={isFieldReadOnly('type-of-functioning')}
        />
      </Grid>

      {/* Address Details Section */}
      <Grid item xs={12}>
        <Typography variant="h6" sx={{
          mt: 2,
          mb: 2,
          color: '#667eea',
          fontWeight: 600,
          borderBottom: '2px solid #667eea',
          paddingBottom: 1
        }}>
          Address Details
        </Typography>
      </Grid>

      <Grid item xs={12} md={3}>
        <TextField
          fullWidth
          label="Laboratory Address"
          value={formData.outsourceLabAddress || ''}
          onChange={(e) => handleChange('outsourceLabAddress', e.target.value)}
          placeholder="Enter Address.."
          required
          sx={{
            '& .MuiOutlinedInput-root': {
              backgroundColor: 'white',
              borderRadius: 2,
              '&:hover': {
                boxShadow: '0 4px 8px rgba(0,0,0,0.1)',
              },
              '&.Mui-focused': {
                boxShadow: '0 4px 12px rgba(25, 118, 210, 0.2)',
              }
            }
          }}
        />
      </Grid>

      <Grid item xs={12} md={3}>
        <TextField
          fullWidth
          label="State"
          value={formData.outsourceLabState || ''}
          onChange={(e) => handleChange('outsourceLabState', e.target.value)}
          placeholder="Enter State.."
          required
          sx={{
            '& .MuiOutlinedInput-root': {
              backgroundColor: 'white',
              borderRadius: 2,
              '&:hover': {
                boxShadow: '0 4px 8px rgba(0,0,0,0.1)',
              },
              '&.Mui-focused': {
                boxShadow: '0 4px 12px rgba(25, 118, 210, 0.2)',
              }
            }
          }}
        />
      </Grid>

      <Grid item xs={12} md={3}>
        <TextField
          fullWidth
          label="Pincode"
          value={formData.outsourceLabPincode || ''}
          onChange={(e) => handleNumericChange('outsourceLabPincode', e.target.value, 6)}
          placeholder="Enter 6-digit pincode"
          inputProps={{ maxLength: 6 }}
          required
          sx={{
            '& .MuiOutlinedInput-root': {
              backgroundColor: 'white',
              borderRadius: 2,
              '&:hover': {
                boxShadow: '0 4px 8px rgba(0,0,0,0.1)',
              },
              '&.Mui-focused': {
                boxShadow: '0 4px 12px rgba(25, 118, 210, 0.2)',
              }
            }
          }}
        />
      </Grid>

      <Grid item xs={12} md={3}>
        <ServiceProviderDropdown fieldName="outsourceServiceProvider" />
      </Grid>
    </>
  );

  // CRO Lab form
  const renderCROLabForm = () => (
    <>
      <Grid item xs={12} md={4}>
        <ServiceProviderDropdown fieldName="croServiceProvider" />
      </Grid>
    </>
  );

  // Others form
  const renderOthersForm = () => (
    <>
      <Grid item xs={12} md={4}>
        <ServiceProviderDropdown fieldName="othersServiceProvider" />
      </Grid>
    </>
  );

  return (
    <Box sx={{
      p: 3,
      backgroundColor: '#f8fafc',
      minHeight: '100vh'
    }}>
      {/* Deadline Alert Dialog */}
      <Dialog
        open={alertpopup}
        onClose={handleCloseOfficerDialog}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Deadline Alert</DialogTitle>
        <DialogContent>
          <Box sx={{ display: "flex", alignItems: "center", mb: 3 }}>
            <Avatar
              sx={{
                bgcolor: "error.main",
                mr: 2,
                width: 40,
                height: 40,
              }}
            >
              <ErrorIcon />
            </Avatar>
            <Typography
              variant="h6"
              sx={{ fontWeight: 600, color: "text.error" }}
            >
              The deadline for filling the application is 30 days
            </Typography>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={handleCloseOfficerDialog}
            color="primary"
            variant="outlined"
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>


      {/* Instructions Section */}
      <Paper sx={{
        mb: 3,
        p: 3,
        backgroundColor: '#e3f2fd',
        border: '1px solid #1976d2',
        borderRadius: 2,
        boxShadow: '0 2px 8px rgba(25, 118, 210, 0.15)'
      }}>
        <Typography variant="h6" sx={{
          color: '#1976d2',
          fontWeight: 600,
          mb: 2,
          display: 'flex',
          alignItems: 'center',
          gap: 1
        }}>
          📋 Instructions
        </Typography>

        {instructionsLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 2 }}>
            <CircularProgress size={24} sx={{ mr: 2 }} />
            <Typography variant="body2" color="text.secondary">
              Loading instructions...
            </Typography>
          </Box>
        ) : instructions.length > 0 ? (
          <Box sx={{
            pl: 2,
            '& p': {
              mb: 1,
              fontSize: '0.95rem',
              lineHeight: 1.6,
              color: '#424242'
            }
          }}>
            {instructions.map((instruction, index) => (
              <Typography key={instruction.id} component="p">
                {index + 1}. {instruction.instruction}
              </Typography>
            ))}
          </Box>
        ) : (
          <Box sx={{
            pl: 2,
            '& p': {
              mb: 1,
              fontSize: '0.95rem',
              lineHeight: 1.6,
              color: '#424242'
            }
          }}>
            <Typography component="p">
              i. Please review the Help manual to understand the Lab Admin and Sub-user process flow before you begin. Follow the menu sequence numbers to complete the application.
            </Typography>
            <Typography component="p">
              ii. All fields marked with * are mandatory.
            </Typography>
            <Typography component="p">
              iii. All the Values made by Sub-users need to be Approve by Lab Admin to make it the part of the Application.
            </Typography>
            <Typography component="p">
              iv. If you face any difficulty, please contact our support team at the numbers 011-40032401, 011-40032402.
            </Typography>
          </Box>
        )}
      </Paper>

      {/* Form Mode Status Notification */}
      {/* {(() => {
        const isInitialState =
          applicationStage === null ||
          applicationStage === "" ||
          applicationStage === undefined;

        const formFillingStages = [
          "1", "2", "3", "4", "5", "6", "7", "8", "9", "10", "11", "12", "13", "14", "15", "16", "17"
        ];

        const isFormFillingStage = formFillingStages.includes(applicationStage);

        const sectionsToCheck = [
          "Adverse Action History",
          "Category of Medical Testing Facility",
          "Collection of Sample From",
          "Contact Person from laboratory for NABL",
          "Declaration Section",
          "Equipment",
          "Internal Audit and Management Review",
          "Laboratory Details",
          "Legal Entity Type",
          "NABL Accreditation Consultancy",
          "Non-Analytical Instruments",
          "Organization Details",
          "Parent Organization",
          "Proficiency Testing",
          "Proficiency Testing Document",
          "Reference Material",
          "Scope",
          "SEZ Region",
          "Staff",
          "Type of Functioning"
        ];

        const hasUnfrozenSections = sectionsToCheck.some((section) =>
          isSectionUnfrozen(section)
        );

        // ==========================
        //  SHOW UNFROZEN SECTIONS
        // ==========================
        if (hasUnfrozenSections) {
          return (
            <Paper
              sx={{
                p: 2,
                mb: 3,
                backgroundColor: "#e8f5e8",
                border: "2px solid #4caf50",
                borderRadius: 2,
                boxShadow: "0 2px 8px rgba(76, 175, 80, 0.2)"
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 1 }}>
                <Typography
                  variant="h6"
                  sx={{
                    color: "#2e7d32",
                    fontWeight: "bold",
                    display: "flex",
                    alignItems: "center",
                    gap: 1
                  }}
                >
                  🔓 Sections Unfrozen for Editing
                </Typography>
              </Box>

              <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
                {sectionsToCheck.map(
                  (section) =>
                    isSectionUnfrozen(section) && (
                      <Chip
                        key={section}
                        label={section}
                        color="success"
                        variant="filled"
                        sx={{ fontWeight: "bold" }}
                      />
                    )
                )}
              </Box>

              <Typography
                variant="body2"
                sx={{ mt: 1, color: "#2e7d32", fontStyle: "italic" }}
              >
                The above sections are temporarily unlocked for editing based on
                unfreeze requests.
              </Typography>
            </Paper>
          );
        }

        // ==========================
        //  INITIAL MODE
        // ==========================
        if (isInitialState) {
          return (
            <Paper
              sx={{
                p: 2,
                mb: 3,
                backgroundColor: "#e8f5e8",
                border: "2px solid #4caf50",
                borderRadius: 2,
                boxShadow: "0 2px 8px rgba(76, 175, 80, 0.2)"
              }}
            >
              <Typography
                variant="h6"
                sx={{
                  color: "#2e7d32",
                  fontWeight: "bold",
                  display: "flex",
                  alignItems: "center",
                  gap: 1
                }}
              >
                📝 Initial Form Mode
              </Typography>

              <Typography
                variant="body2"
                sx={{ color: "#2e7d32", fontStyle: "italic" }}
              >
                You can create and edit all form fields. No stage restrictions apply.
              </Typography>
            </Paper>
          );
        }

        // ==========================
        //  FORM FILLING (Stages 1–17)
        // ==========================
        if (isFormFillingStage) {
          return (
            <Paper
              sx={{
                p: 2,
                mb: 3,
                backgroundColor: "#e3f2fd",
                border: "2px solid #2196f3",
                borderRadius: 2,
                boxShadow: "0 2px 8px rgba(33, 150, 243, 0.2)"
              }}
            >
              <Typography
                variant="h6"
                sx={{
                  color: "#1976d2",
                  fontWeight: "bold",
                  display: "flex",
                  alignItems: "center",
                  gap: 1
                }}
              >
                ✏️ Form Filling Mode - Stage {applicationStage}
              </Typography>

              <Typography
                variant="body2"
                sx={{ color: "#1976d2", fontStyle: "italic" }}
              >
                You can edit all form fields during the application filling process.
              </Typography>
            </Paper>
          );
        }

        // ==========================
        //  MODE 18a – INADEQUATE ONLY
        // ==========================
        if (applicationStage === "18a") {
          return (
            <Paper
              sx={{
                p: 2,
                mb: 3,
                backgroundColor: "#fff3e0",
                border: "2px solid #ff9800",
                borderRadius: 2,
                boxShadow: "0 2px 8px rgba(255, 152, 0, 0.2)"
              }}
            >
              <Typography
                variant="h6"
                sx={{
                  color: "#f57c00",
                  fontWeight: "bold",
                  display: "flex",
                  alignItems: "center",
                  gap: 1
                }}
              >
                ⚠️ Partial Edit Mode - Only Inadequate Sections Editable
              </Typography>

              <Typography
                variant="body2"
                sx={{ color: "#f57c00", fontStyle: "italic" }}
              >
                Only sections marked as "Inadequate" during document review can be
                edited.
              </Typography>
            </Paper>
          );
        }

        return (
          <Paper
            sx={{
              p: 2,
              mb: 3,
              backgroundColor: "#f5f5f5",
              border: "2px solid #9e9e9e",
              borderRadius: 2,
              boxShadow: "0 2px 8px rgba(158, 158, 158, 0.2)"
            }}
          >

            <Typography
              variant="body2"
              sx={{ color: "#616161", fontStyle: "italic" }}
            >
              Form is locked for editing. Changes can only be made through unfreeze
              requests or during assessment review.
            </Typography>
          </Paper>
        );
      })()} */}


      {/* DA NC Summary Section */}


      <Accordion
        defaultExpanded
        sx={{
          mb: 2,
          boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
          '&:before': { display: 'none' },
          borderRadius: 2,
          overflow: 'hidden'
        }}
      >
        <AccordionSummary
          expandIcon={<ExpandMoreIcon />}
          sx={{
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            color: 'white',
            '& .MuiAccordionSummary-content': {
              margin: '16px 0',
            },
            '& .MuiSvgIcon-root': {
              color: 'white',
            }
          }}
        >
          <Typography variant="h6" sx={{ fontWeight: 600, display: 'flex', alignItems: 'center', gap: 1 }}>
            🏢 Laboratory Details
            {getSectionStatusIndicator('laboratory-details')}
            {isSectionUnfrozen('Laboratory Details') && (
              <Chip
                label="🔓 UNFROZEN"
                size="small"
                color="success"
                variant="filled"
                sx={{
                  fontWeight: 'bold',
                  fontSize: '0.7rem',
                  color: 'white',
                  backgroundColor: '#4caf50',
                  animation: 'pulse 2s infinite'
                }}
              />
            )}
          </Typography>
        </AccordionSummary>
        <AccordionDetails sx={{ p: 3, backgroundColor: 'white' }}>
          {/* Adequacy Check Section */}
          <AdequacyCheckSection
            sectionId="laboratory-details"
            title="Laboratory Details"
            applicationStage={applicationStage}
            orgId={orgId}
            appId={appId}
            adequacyChecks={adequacyChecks}
            handleAdequacyCheck={handleAdequacyCheck}
            handleAdequacyRemarks={handleAdequacyRemarks}
            handleAdequacySubmit={handleAdequacySubmit}
            loading={loading}
            currentUser={currentUser}
            currentUserId={currentUserId}
            assessorId={assessorId}
            assessmentId={assessmentId}
          />
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Name/Identification of the Laboratory"
                value={formData.organizationName}
                onChange={(e) => handleChange('organizationName', e.target.value)}
                required
                sx={{
                  '& .MuiOutlinedInput-root': {
                    backgroundColor: 'white',
                    borderRadius: 2,
                    '&:hover': {
                      boxShadow: '0 4px 8px rgba(0,0,0,0.1)',
                    },
                    '&.Mui-focused': {
                      boxShadow: '0 4px 12px rgba(25, 118, 210, 0.2)',
                    }
                  }
                }}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <FormControl fullWidth required sx={{
                '& .MuiOutlinedInput-root': {
                  backgroundColor: 'white',
                  borderRadius: 2,
                  '&:hover': {
                    boxShadow: '0 4px 8px rgba(0,0,0,0.1)',
                  },
                  '&.Mui-focused': {
                    boxShadow: '0 4px 12px rgba(25, 118, 210, 0.2)',
                  }
                }
              }}>
                <InputLabel>Type of Laboratory</InputLabel>
                <Select
                  value={formData.laboratoryType || 'Select'}
                  onChange={(e) => handleChange('laboratoryType', e.target.value)}
                  label="Type of Laboratory"
                  disabled={loading}
                >
                  {labTypes.map((labType) => (
                    <MenuItem
                      key={labType.id}
                      value={labType.id}
                      selected={labType.id === 'Select'}
                    >
                      {labType.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12}>
              <Box sx={{
                backgroundColor: 'white',
                borderRadius: 2,
                p: 3,
                border: '1px solid #e0e0e0',
                boxShadow: '0 2px 4px rgba(0,0,0,0.05)'
              }}>
                <FormControlLabel
                  control={
                    <Checkbox
                      {...getInputProps('gstExemption', null, 'checkbox')}
                      sx={{
                        color: isFieldReadOnly() ? '#ccc' : '#667eea',
                        '&.Mui-checked': {
                          color: isFieldReadOnly() ? '#ccc' : '#667eea',
                        },
                        cursor: isFieldReadOnly() ? 'not-allowed' : 'pointer'
                      }}
                    />
                  }
                  label={
                    <Box>
                      <Typography variant="body1" sx={{ fontWeight: 500, mb: 1 }}>
                        GST Exemption
                      </Typography>
                      <Typography variant="body2" sx={{ color: '#666', fontSize: '0.875rem' }}>
                        Serial no.3 of chapter 99 of notification no.12 of 2017 dated 28.06.2017
                      </Typography>
                    </Box>
                  }
                />
              </Box>
            </Grid>

            {formData.gstExemption ? (
              <Grid item xs={12} md={6}>
                <FileUploadComponent
                  fieldName="gstExemptionFile"
                  label="Upload GST Exemption Certificate"
                  orgId={orgId}
                  section="gst-documents"
                  required={true}
                  accept=".pdf,.jpg,.jpeg,.png,.doc,.docx"
                  maxSize={10}
                  onChange={handleS3FileUpload}
                  initialFile={formData.gstExemptionFile}
                  disabled={isFieldReadOnly('laboratory-details')}
                />
              </Grid>
            ) : (
              <>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="GST Number"
                    value={formData.gstNumber}
                    onChange={(e) => handleChange('gstNumber', e.target.value)}
                    placeholder="Enter your GST number or 'NA'"
                    required
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        backgroundColor: 'white',
                        borderRadius: 2,
                        '&:hover': {
                          boxShadow: '0 4px 8px rgba(0,0,0,0.1)',
                        },
                        '&.Mui-focused': {
                          boxShadow: '0 4px 12px rgba(25, 118, 210, 0.2)',
                        }
                      }
                    }}
                  />
                </Grid>

              </>
            )}
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="PAN/TAN Number"
                value={formData.panTanNumber}
                onChange={(e) => handleChange('panTanNumber', e.target.value)}
                placeholder="Enter your PAN or TAN number"
                required
                sx={{
                  '& .MuiOutlinedInput-root': {
                    backgroundColor: 'white',
                    borderRadius: 2,
                    '&:hover': {
                      boxShadow: '0 4px 8px rgba(0,0,0,0.1)',
                    },
                    '&.Mui-focused': {
                      boxShadow: '0 4px 12px rgba(25, 118, 210, 0.2)',
                    }
                  }
                }}
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Address"
                multiline
                rows={3}
                value={formData.address}
                onChange={(e) => handleChange('address', e.target.value)}
                required
                sx={{
                  '& .MuiOutlinedInput-root': {
                    backgroundColor: 'white',
                    borderRadius: 2,
                    '&:hover': {
                      boxShadow: '0 4px 8px rgba(0,0,0,0.1)',
                    },
                    '&.Mui-focused': {
                      boxShadow: '0 4px 12px rgba(25, 118, 210, 0.2)',
                    }
                  }
                }}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <FormControl fullWidth required sx={{
                '& .MuiOutlinedInput-root': {
                  backgroundColor: 'white',
                  borderRadius: 2,
                  '&:hover': {
                    boxShadow: '0 4px 8px rgba(0,0,0,0.1)',
                  },
                  '&.Mui-focused': {
                    boxShadow: '0 4px 12px rgba(25, 118, 210, 0.2)',
                  }
                }
              }}>
                <InputLabel>State</InputLabel>
                <Select
                  value={formData.state}
                  onChange={(e) => handleChange('state', e.target.value)}
                  label="State"
                  disabled={loading}
                >
                  {states.map((state) => (
                    <MenuItem key={state.id} value={state.id}>
                      {state.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>



            <Grid item xs={12} md={6}>
              <FormControl fullWidth required sx={{
                '& .MuiOutlinedInput-root': {
                  backgroundColor: 'white',
                  borderRadius: 2,
                  '&:hover': {
                    boxShadow: '0 4px 8px rgba(0,0,0,0.1)',
                  },
                  '&.Mui-focused': {
                    boxShadow: '0 4px 12px rgba(25, 118, 210, 0.2)',
                  }
                }
              }}>
                <InputLabel>District</InputLabel>
                <Select
                  value={formData.district}
                  onChange={(e) => handleChange('district', e.target.value)}
                  label="District"
                  disabled={loading || !formData.state}
                >
                  {districts.map((district) => (
                    <MenuItem key={district.District_Code} value={district.District_Code}>
                      {district.District_Name_Eng}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} md={6}>
              <FormControl fullWidth sx={{
                '& .MuiOutlinedInput-root': {
                  backgroundColor: 'white',
                  borderRadius: 2,
                  '&:hover': {
                    boxShadow: '0 4px 8px rgba(0,0,0,0.1)',
                  },
                  '&.Mui-focused': {
                    boxShadow: '0 4px 12px rgba(25, 118, 210, 0.2)',
                  }
                }
              }}>
                <InputLabel>Sub District</InputLabel>
                <Select
                  value={formData.subDistrict}
                  onChange={(e) => handleChange('subDistrict', e.target.value)}
                  label="Sub District"
                  disabled={loading || !formData.district}
                >
                  {subDistricts.map((subDistrict) => (
                    <MenuItem key={subDistrict.Sub_District_Code} value={subDistrict.Sub_District_Code}>
                      {subDistrict.Sub_District_Name_Eng}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} md={6}>
              <FormControl fullWidth required sx={{
                '& .MuiOutlinedInput-root': {
                  backgroundColor: 'white',
                  borderRadius: 2,
                  '&:hover': {
                    boxShadow: '0 4px 8px rgba(0,0,0,0.1)',
                  },
                  '&.Mui-focused': {
                    boxShadow: '0 4px 12px rgba(25, 118, 210, 0.2)',
                  }
                }
              }}>
                <InputLabel>City</InputLabel>
                <Select
                  value={formData.city}
                  onChange={(e) => handleChange('city', e.target.value)}
                  label="City"
                  disabled={loading || !formData.state}
                >
                  {cities.map((city) => (
                    <MenuItem key={city.id} value={city.id}>
                      {city.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Village/Town"
                value={formData.villageTown}
                onChange={(e) => handleChange('villageTown', e.target.value)}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    backgroundColor: 'white',
                    borderRadius: 2,
                    '&:hover': {
                      boxShadow: '0 4px 8px rgba(0,0,0,0.1)',
                    },
                    '&.Mui-focused': {
                      boxShadow: '0 4px 12px rgba(25, 118, 210, 0.2)',
                    }
                  }
                }}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Pincode"
                value={formData.pincode}
                onChange={(e) => handleNumericChange('pincode', e.target.value, 6)}
                placeholder="Enter 6-digit pincode"
                inputProps={{ maxLength: 6 }}
                required
                sx={{
                  '& .MuiOutlinedInput-root': {
                    backgroundColor: 'white',
                    borderRadius: 2,
                    '&:hover': {
                      boxShadow: '0 4px 8px rgba(0,0,0,0.1)',
                    },
                    '&.Mui-focused': {
                      boxShadow: '0 4px 12px rgba(25, 118, 210, 0.2)',
                    }
                  }
                }}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Mobile No"
                value={formData.mobileNo}
                onChange={(e) => handleNumericChange('mobileNo', e.target.value, 10)}
                placeholder="Enter 10-digit mobile number"
                inputProps={{ maxLength: 10 }}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    backgroundColor: 'white',
                    borderRadius: 2,
                    '&:hover': {
                      boxShadow: '0 4px 8px rgba(0,0,0,0.1)',
                    },
                    '&.Mui-focused': {
                      boxShadow: '0 4px 12px rgba(25, 118, 210, 0.2)',
                    }
                  }
                }}
              />
            </Grid>

            <Grid item xs={12} md={2}>
              <TextField
                fullWidth
                label="ISD CODE"
                value={formData.telephoneIsdCode}
                onChange={(e) => handleChange('telephoneIsdCode', e.target.value)}
                placeholder="+91"
                sx={{
                  '& .MuiOutlinedInput-root': {
                    backgroundColor: 'white',
                    borderRadius: 2,
                    '&:hover': {
                      boxShadow: '0 4px 8px rgba(0,0,0,0.1)',
                    },
                    '&.Mui-focused': {
                      boxShadow: '0 4px 12px rgba(25, 118, 210, 0.2)',
                    }
                  }
                }}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                label="Landline No"
                value={formData.landlineNo}
                onChange={(e) => handleChange('landlineNo', e.target.value)}
                placeholder="Enter landline number"
                sx={{
                  '& .MuiOutlinedInput-root': {
                    backgroundColor: 'white',
                    borderRadius: 2,
                    '&:hover': {
                      boxShadow: '0 4px 8px rgba(0,0,0,0.1)',
                    },
                    '&.Mui-focused': {
                      boxShadow: '0 4px 12px rgba(25, 118, 210, 0.2)',
                    }
                  }
                }}
              />
            </Grid>

            <Grid item xs={12} md={2}>
              <TextField
                fullWidth
                label="ISD CODE"
                value={formData.faxIsdCode}
                onChange={(e) => handleChange('faxIsdCode', e.target.value)}
                placeholder="+91"
                sx={{
                  '& .MuiOutlinedInput-root': {
                    backgroundColor: 'white',
                    borderRadius: 2,
                    '&:hover': {
                      boxShadow: '0 4px 8px rgba(0,0,0,0.1)',
                    },
                    '&.Mui-focused': {
                      boxShadow: '0 4px 12px rgba(25, 118, 210, 0.2)',
                    }
                  }
                }}
              />
            </Grid>

            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                label="Fax No."
                value={formData.faxNo}
                onChange={(e) => handleChange('faxNo', e.target.value)}
                placeholder="Enter fax number"
                sx={{
                  '& .MuiOutlinedInput-root': {
                    backgroundColor: 'white',
                    borderRadius: 2,
                    '&:hover': {
                      boxShadow: '0 4px 8px rgba(0,0,0,0.1)',
                    },
                    '&.Mui-focused': {
                      boxShadow: '0 4px 12px rgba(25, 118, 210, 0.2)',
                    }
                  }
                }}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="E-mail"
                type="email"
                value={formData.emailRequired}
                onChange={(e) => handleChange('emailRequired', e.target.value)}
                required
                placeholder="Enter email address"
                sx={{
                  '& .MuiOutlinedInput-root': {
                    backgroundColor: 'white',
                    borderRadius: 2,
                    '&:hover': {
                      boxShadow: '0 4px 8px rgba(0,0,0,0.1)',
                    },
                    '&.Mui-focused': {
                      boxShadow: '0 4px 12px rgba(25, 118, 210, 0.2)',
                    }
                  }
                }}
              />
            </Grid>

          </Grid>
        </AccordionDetails>
      </Accordion>

      {/* Legal Entity Type Accordion */}
      <Accordion
        defaultExpanded
        sx={{
          mb: 2,
          boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
          '&:before': { display: 'none' },
          borderRadius: 2,
          overflow: 'hidden'
        }}
      >
        <AccordionSummary
          expandIcon={<ExpandMoreIcon />}
          sx={{
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            color: 'white',
            '& .MuiAccordionSummary-content': {
              margin: '16px 0',
            },
            '& .MuiSvgIcon-root': {
              color: 'white',
            }
          }}
        >
          <Typography variant="h6" sx={{ fontWeight: 600, display: 'flex', alignItems: 'center' }}>
            🏛️ Legal Entity Type
            {getSectionStatusIndicator('legal-entity-type')}
          </Typography>
        </AccordionSummary>
        <AccordionDetails sx={{ p: 3, backgroundColor: 'white' }}>
          {/* Adequacy Check Section */}
          <AdequacyCheckSection
            sectionId="legal-entity-type"
            title="Legal Entity Type"
            applicationStage={applicationStage}
            orgId={orgId}
            appId={appId}
            adequacyChecks={adequacyChecks}
            handleAdequacyCheck={handleAdequacyCheck}
            handleAdequacyRemarks={handleAdequacyRemarks}
            handleAdequacySubmit={handleAdequacySubmit}
            loading={loading}
            currentUser={currentUser}
            currentUserId={currentUserId}
            assessorId={assessorId}
            assessmentId={assessmentId}
          />
          <Grid container spacing={3}>
            {/* Legal Entity Type Dropdown */}
            <Grid item xs={12} md={6}>
              <FormControl fullWidth required
                error={!!validationErrors['legalEntityType']}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    backgroundColor: 'white',
                    borderRadius: 2,
                    '&:hover': {
                      boxShadow: '0 4px 8px rgba(0,0,0,0.1)',
                    },
                    '&.Mui-focused': {
                      boxShadow: '0 4px 12px rgba(25, 118, 210, 0.2)',
                    },
                    '&.Mui-error': {
                      borderColor: '#f44336',
                      '&:hover': {
                        boxShadow: '0 4px 8px rgba(244, 67, 54, 0.2)',
                      },
                      '&.Mui-focused': {
                        boxShadow: '0 4px 12px rgba(244, 67, 54, 0.3)',
                      }
                    }
                  }
                }}>
                <InputLabel>Legal Entity Type</InputLabel>
                <Select
                  value={formData.legalEntityType || ''}
                  onChange={(e) => handleChange('legalEntityType', e.target.value)}
                  label="Legal Entity Type"
                >
                  <MenuItem value="">-- Select --</MenuItem>
                  <MenuItem value="Proprietorship">Proprietorship</MenuItem>
                  <MenuItem value="Partnership">Partnership</MenuItem>
                  <MenuItem value="LLP">LLP</MenuItem>
                  <MenuItem value="Companies Act">Companies Act</MenuItem>
                  <MenuItem value="Public Ltd">Public Ltd</MenuItem>
                  <MenuItem value="Government">Government</MenuItem>
                  <MenuItem value="Trust">Trust</MenuItem>
                  <MenuItem value="Society">Society</MenuItem>
                  <MenuItem value="One Person Company">One Person Company</MenuItem>
                </Select>
                {validationErrors['legalEntityType'] && (
                  <Typography variant="caption" sx={{ color: '#f44336', mt: 1, ml: 2 }}>
                    {validationErrors['legalEntityType']}
                  </Typography>
                )}
              </FormControl>
            </Grid>


            {/* Proprietorship Info - Show only for Proprietorship */}
            {formData.legalEntityType === 'Proprietorship' && (
              <Grid item xs={12}>
                <Alert severity="info" sx={{
                  backgroundColor: '#e3f2fd',
                  border: '1px solid #bbdefb',
                  borderRadius: 2,
                  '& .MuiAlert-icon': {
                    color: '#1976d2'
                  }
                }}>
                  The Proprietorship option is only open to Doctors registered with the MCI/NMC and/or the State Medical Council.
                </Alert>
              </Grid>
            )}

            {/* Registration Requirement Info - Show for Proprietorship and Partnership */}
            {requiresRegistrationValidation(formData.legalEntityType) && (
              <Grid item xs={12}>
                <Alert severity="warning" sx={{
                  backgroundColor: '#fff3e0',
                  border: '1px solid #ffcc02',
                  borderRadius: 2,
                  '& .MuiAlert-icon': {
                    color: '#f57c00'
                  }
                }}>
                  <strong>Registration Requirement:</strong> You must provide either MCI/NMC registration details OR State registration details. At least one complete set (both number and certificate) is mandatory.
                </Alert>
              </Grid>
            )}

            {/* Dynamic Legal Entity Details Card */}
            <Grid item xs={12}>
              <Paper sx={{
                p: 3,
                borderRadius: 2,
                boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
                border: '1px solid #e0e0e0'
              }}>
                <Grid container spacing={3}>
                  {renderLegalEntityForm()}
                </Grid>
              </Paper>
            </Grid>
          </Grid>
        </AccordionDetails>
      </Accordion>

      {/* Type of Functioning Accordion */}
      <Accordion
        defaultExpanded
        sx={{
          mb: 2,
          boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
          '&:before': { display: 'none' },
          borderRadius: 2,
          overflow: 'hidden'
        }}
      >
        <AccordionSummary
          expandIcon={<ExpandMoreIcon />}
          sx={{
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            color: 'white',
            '& .MuiAccordionSummary-content': {
              margin: '16px 0',
            },
            '& .MuiSvgIcon-root': {
              color: 'white',
            }
          }}
        >
          <Typography variant="h6" sx={{ fontWeight: 600, display: 'flex', alignItems: 'center' }}>
            🏥 Type of Functioning
            {getSectionStatusIndicator('type-of-functioning')}
          </Typography>
        </AccordionSummary>
        <AccordionDetails sx={{ p: 3, backgroundColor: 'white' }}>
          {/* Adequacy Check Section */}
          <AdequacyCheckSection
            sectionId="type-of-functioning"
            title="Type of Functioning"
            applicationStage={applicationStage}
            orgId={orgId}
            appId={appId}
            adequacyChecks={adequacyChecks}
            handleAdequacyCheck={handleAdequacyCheck}
            handleAdequacyRemarks={handleAdequacyRemarks}
            handleAdequacySubmit={handleAdequacySubmit}
            loading={loading}
            currentUser={currentUser}
            currentUserId={currentUserId}
            assessorId={assessorId}
            assessmentId={assessmentId}
          />
          <Grid container spacing={3}>
            {/* Type of Functioning Dropdown */}
            <Grid item xs={12} md={6}>
              <FormControl fullWidth required
                error={!!validationErrors['typeOfFunctioning']}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    backgroundColor: 'white',
                    borderRadius: 2,
                    '&:hover': {
                      boxShadow: '0 4px 8px rgba(0,0,0,0.1)',
                    },
                    '&.Mui-focused': {
                      boxShadow: '0 4px 12px rgba(25, 118, 210, 0.2)',
                    },
                    '&.Mui-error': {
                      borderColor: '#f44336',
                      '&:hover': {
                        boxShadow: '0 4px 8px rgba(244, 67, 54, 0.2)',
                      },
                      '&.Mui-focused': {
                        boxShadow: '0 4px 12px rgba(244, 67, 54, 0.3)',
                      }
                    }
                  }
                }}>
                <InputLabel>Type of Functioning</InputLabel>
                <Select
                  value={formData.typeOfFunctioning || ''}
                  onChange={(e) => handleChange('typeOfFunctioning', e.target.value)}
                  label="Type of Functioning"
                >
                  <MenuItem value="">-- Select --</MenuItem>
                  <MenuItem value="hospital-owned">Hospital Owned</MenuItem>
                  <MenuItem value="hospital-outsource">Hospital - Outsource</MenuItem>
                  <MenuItem value="cro-lab">CRO Lab</MenuItem>
                  <MenuItem value="others">Others</MenuItem>
                </Select>
                {validationErrors['typeOfFunctioning'] && (
                  <Typography variant="caption" sx={{ color: '#f44336', mt: 1, ml: 2 }}>
                    {validationErrors['typeOfFunctioning']}
                  </Typography>
                )}
              </FormControl>
            </Grid>

            {/* Dynamic Type of Functioning Details Card */}
            <Grid item xs={12}>
              <Paper sx={{
                p: 3,
                borderRadius: 2,
                boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
                border: '1px solid #e0e0e0'
              }}>
                <Grid container spacing={3}>
                  {renderTypeOfFunctioningForm()}
                </Grid>
              </Paper>
            </Grid>
          </Grid>
        </AccordionDetails>
      </Accordion>

      {/* Contact Person from laboratory for NABL Accordion */}
      <Accordion
        defaultExpanded
        sx={{
          mb: 2,
          boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
          '&:before': { display: 'none' },
          borderRadius: 2,
          overflow: 'hidden'
        }}
      >
        <AccordionSummary
          expandIcon={<ExpandMoreIcon />}
          sx={{
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            color: 'white',
            '& .MuiAccordionSummary-content': {
              margin: '16px 0',
            },
            '& .MuiSvgIcon-root': {
              color: 'white',
            }
          }}
        >
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            👤 Contact Person from laboratory for NABL
          </Typography>
        </AccordionSummary>
        <AccordionDetails sx={{ p: 3, backgroundColor: 'white' }}>
          {/* Adequacy Check Section */}
          <AdequacyCheckSection
            sectionId="contact-person-nabl"
            title="Contact Person from laboratory for NABL"
            applicationStage={applicationStage}
            orgId={orgId}
            appId={appId}
            adequacyChecks={adequacyChecks}
            handleAdequacyCheck={handleAdequacyCheck}
            handleAdequacyRemarks={handleAdequacyRemarks}
            handleAdequacySubmit={handleAdequacySubmit}
            loading={loading}
            currentUser={currentUser}
            currentUserId={currentUserId}
            assessorId={assessorId}
            assessmentId={assessmentId}
          />
          <Grid container spacing={3}>
            {/* Name Section */}
            <Grid item xs={12}>
              <Typography variant="h6" sx={{
                mb: 2,
                color: '#667eea',
                fontWeight: 600,
                borderBottom: '2px solid #667eea',
                paddingBottom: 1
              }}>
                Name<span style={{ color: 'red', fontWeight: 'bold' }}>*</span>
              </Typography>
            </Grid>

            <Grid item xs={12} md={2}>
              <FormControl fullWidth sx={{
                '& .MuiOutlinedInput-root': {
                  backgroundColor: 'white',
                  borderRadius: 2,
                  '&:hover': {
                    boxShadow: '0 4px 8px rgba(0,0,0,0.1)',
                  },
                  '&.Mui-focused': {
                    boxShadow: '0 4px 12px rgba(25, 118, 210, 0.2)',
                  }
                }
              }}>
                <InputLabel>Salutation</InputLabel>
                <Select
                  value={formData.contactPersonSalutation || 'Dr.'}
                  onChange={(e) => handleChange('contactPersonSalutation', e.target.value)}
                  label="Salutation"
                  sx={{ width: '100%' }}
                >
                  <MenuItem value="Dr.">Dr.</MenuItem>
                  <MenuItem value="Mr.">Mr.</MenuItem>
                  <MenuItem value="Mx.">Mx.</MenuItem>
                  <MenuItem value="Ms.">Ms.</MenuItem>
                  <MenuItem value="Mrs.">Mrs.</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} md={10}>
              <TextField
                fullWidth
                label="Name of Contact Person"
                value={formData.contactPersonName || ''}
                onChange={(e) => handleChange('contactPersonName', e.target.value)}
                placeholder="Enter contact person name"
                required
                sx={{
                  '& .MuiOutlinedInput-root': {
                    backgroundColor: 'white',
                    borderRadius: 2,
                    '&:hover': {
                      boxShadow: '0 4px 8px rgba(0,0,0,0.1)',
                    },
                    '&.Mui-focused': {
                      boxShadow: '0 4px 12px rgba(25, 118, 210, 0.2)',
                    }
                  }
                }}
              />
            </Grid>

            {/* Designation */}
            <Grid item xs={12} md={6}>
              <FormControl fullWidth required sx={{
                '& .MuiOutlinedInput-root': {
                  backgroundColor: 'white',
                  borderRadius: 2,
                  '&:hover': {
                    boxShadow: '0 4px 8px rgba(0,0,0,0.1)',
                  },
                  '&.Mui-focused': {
                    boxShadow: '0 4px 12px rgba(25, 118, 210, 0.2)',
                  }
                }
              }}>
                <InputLabel>Designation<span style={{ color: 'red', fontWeight: 'bold' }}>*</span></InputLabel>
                <Select
                  value={formData.contactPersonDesignation || ''}
                  onChange={(e) => handleChange('contactPersonDesignation', e.target.value)}
                  label="Designation*"
                >
                  <MenuItem value="">--Select Designation--</MenuItem>
                  <MenuItem value="CEO of laboratory">CEO of laboratory</MenuItem>
                  <MenuItem value="Head of Laboratory">Head of Laboratory</MenuItem>
                  <MenuItem value="Laboratory Director">Laboratory Director</MenuItem>
                  <MenuItem value="Quality Officer">Quality Officer</MenuItem>
                  <MenuItem value="Section Head">Section Head</MenuItem>
                  <MenuItem value="Technical Manager">Technical Manager</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            {/* Mobile No */}
            <Grid item xs={12}>
              <Typography variant="h6" sx={{
                mb: 2,
                color: '#667eea',
                fontWeight: 600,
                borderBottom: '2px solid #667eea',
                paddingBottom: 1
              }}>
                Mobile No<span style={{ color: 'red', fontWeight: 'bold' }}>*</span>
              </Typography>
            </Grid>

            <Grid item xs={12} md={2}>
              <TextField
                fullWidth
                value="+91"
                disabled
                placeholder="+91"
                sx={{
                  '& .MuiOutlinedInput-root': {
                    backgroundColor: '#f5f5f5',
                    borderRadius: 2,
                  }
                }}
              />
            </Grid>

            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                label="Mobile No"
                value={formData.contactPersonMobileNo || ''}
                onChange={(e) => handleNumericChange('contactPersonMobileNo', e.target.value, 10)}
                placeholder="Enter 10-digit mobile number"
                inputProps={{ maxLength: 10 }}
                required
                sx={{
                  '& .MuiOutlinedInput-root': {
                    backgroundColor: 'white',
                    borderRadius: 2,
                    '&:hover': {
                      boxShadow: '0 4px 8px rgba(0,0,0,0.1)',
                    },
                    '&.Mui-focused': {
                      boxShadow: '0 4px 12px rgba(25, 118, 210, 0.2)',
                    }
                  }
                }}
              />
            </Grid>

            {/* Telephone no */}
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Telephone no"
                value={formData.contactPersonTelephoneNo || ''}
                onChange={(e) => handleChange('contactPersonTelephoneNo', e.target.value)}
                placeholder="Enter telephone number"
                sx={{
                  '& .MuiOutlinedInput-root': {
                    backgroundColor: 'white',
                    borderRadius: 2,
                    '&:hover': {
                      boxShadow: '0 4px 8px rgba(0,0,0,0.1)',
                    },
                    '&.Mui-focused': {
                      boxShadow: '0 4px 12px rgba(25, 118, 210, 0.2)',
                    }
                  }
                }}
              />
            </Grid>

            {/* Fax No */}
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Fax No."
                value={formData.contactPersonFaxNo || ''}
                onChange={(e) => handleChange('contactPersonFaxNo', e.target.value)}
                placeholder="Fax No."
                sx={{
                  '& .MuiOutlinedInput-root': {
                    backgroundColor: 'white',
                    borderRadius: 2,
                    '&:hover': {
                      boxShadow: '0 4px 8px rgba(0,0,0,0.1)',
                    },
                    '&.Mui-focused': {
                      boxShadow: '0 4px 12px rgba(25, 118, 210, 0.2)',
                    }
                  }
                }}
              />
            </Grid>

            {/* Email 1 */}
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Email 1"
                type="email"
                value={formData.contactPersonEmail1 || ''}
                onChange={(e) => handleChange('contactPersonEmail1', e.target.value)}
                placeholder="Email"
                required
                sx={{
                  '& .MuiOutlinedInput-root': {
                    backgroundColor: 'white',
                    borderRadius: 2,
                    '&:hover': {
                      boxShadow: '0 4px 8px rgba(0,0,0,0.1)',
                    },
                    '&.Mui-focused': {
                      boxShadow: '0 4px 12px rgba(25, 118, 210, 0.2)',
                    }
                  }
                }}
              />
            </Grid>

            {/* Email 2 */}
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Email 2"
                type="email"
                value={formData.contactPersonEmail2 || ''}
                onChange={(e) => handleChange('contactPersonEmail2', e.target.value)}
                placeholder="Email"
                sx={{
                  '& .MuiOutlinedInput-root': {
                    backgroundColor: 'white',
                    borderRadius: 2,
                    '&:hover': {
                      boxShadow: '0 4px 8px rgba(0,0,0,0.1)',
                    },
                    '&.Mui-focused': {
                      boxShadow: '0 4px 12px rgba(25, 118, 210, 0.2)',
                    }
                  }
                }}
              />
            </Grid>
          </Grid>
        </AccordionDetails>
      </Accordion>

      {/* Parent Organization Accordion */}
      <Accordion
        defaultExpanded
        sx={{
          mb: 2,
          boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
          '&:before': { display: 'none' },
          borderRadius: 2,
          overflow: 'hidden'
        }}
      >
        <AccordionSummary
          expandIcon={<ExpandMoreIcon />}
          sx={{
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            color: 'white',
            '& .MuiAccordionSummary-content': {
              margin: '16px 0',
            },
            '& .MuiSvgIcon-root': {
              color: 'white',
            }
          }}
        >
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            🏢 Name of the Head Office/Laboratory/Parent Organization in case the applicant laboratory is part of a Legal Entity other than laboratory
          </Typography>
        </AccordionSummary>
        <AccordionDetails sx={{ p: 3, backgroundColor: 'white' }}>
          {/* Adequacy Check Section */}
          <AdequacyCheckSection
            sectionId="parent-organization"
            title="Parent Organization"
            applicationStage={applicationStage}
            orgId={orgId}
            appId={appId}
            adequacyChecks={adequacyChecks}
            handleAdequacyCheck={handleAdequacyCheck}
            handleAdequacyRemarks={handleAdequacyRemarks}
            handleAdequacySubmit={handleAdequacySubmit}
            loading={loading}
            currentUser={currentUser}
            currentUserId={currentUserId}
            assessorId={assessorId}
            assessmentId={assessmentId}
          />
          <Grid container spacing={3}>
            {/* Is Laboratory part of an Organization */}
            <Grid item xs={12}>
              <Box>
                <Typography variant="h6" sx={{
                  mb: 2,
                  color: validationErrors['isPartOfOrganization'] ? '#f44336' : '#667eea',
                  fontWeight: 600,
                  borderBottom: `2px solid ${validationErrors['isPartOfOrganization'] ? '#f44336' : '#667eea'}`,
                  paddingBottom: 1
                }}>
                  Is Laboratory part of an Organization?<span style={{ color: 'red', fontWeight: 'bold' }}>*</span>
                </Typography>
                <Box sx={{ display: 'flex', gap: 3, mt: 2 }}>
                  <FormControlLabel
                    control={
                      <input
                        type="radio"
                        name="isPartOfOrganization"
                        value="Yes"
                        checked={formData.isPartOfOrganization === 'Yes'}
                        onChange={(e) => handleChange('isPartOfOrganization', e.target.value)}
                        style={{ marginRight: '8px' }}
                      />
                    }
                    label="Yes"
                    sx={{
                      '& .MuiFormControlLabel-label': {
                        fontSize: '1.1rem',
                        fontWeight: 500
                      }
                    }}
                  />
                  <FormControlLabel
                    control={
                      <input
                        type="radio"
                        name="isPartOfOrganization"
                        value="No"
                        checked={formData.isPartOfOrganization === 'No'}
                        onChange={(e) => handleChange('isPartOfOrganization', e.target.value)}
                        style={{ marginRight: '8px' }}
                      />
                    }
                    label="No"
                    sx={{
                      '& .MuiFormControlLabel-label': {
                        fontSize: '1.1rem',
                        fontWeight: 500
                      }
                    }}
                  />
                </Box>
                {validationErrors['isPartOfOrganization'] && (
                  <Typography variant="caption" sx={{ color: '#f44336', mt: 1, display: 'block' }}>
                    {validationErrors['isPartOfOrganization']}
                  </Typography>
                )}
              </Box>
            </Grid>

            {/* Conditional fields - show only when "Yes" is selected */}
            {formData.isPartOfOrganization === 'Yes' && (
              <>
                {/* Parent Organization Name */}
                <Grid item xs={12}>
                  <Typography variant="h6" sx={{
                    mt: 2,
                    mb: 2,
                    color: '#667eea',
                    fontWeight: 600,
                    borderBottom: '2px solid #667eea',
                    paddingBottom: 1
                  }}>
                    Name of the Head Office/Laboratory/Parent Organization in case the applicant laboratory is part of a Legal Entity other than laboratory<span style={{ color: 'red', fontWeight: 'bold' }}>*</span>
                  </Typography>
                </Grid>

                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Parent Organization"
                    value={formData.parentOrganizationName || ''}
                    onChange={(e) => handleChange('parentOrganizationName', e.target.value)}
                    placeholder="Enter parent organization name"
                    required
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        backgroundColor: 'white',
                        borderRadius: 2,
                        '&:hover': {
                          boxShadow: '0 4px 8px rgba(0,0,0,0.1)',
                        },
                        '&.Mui-focused': {
                          boxShadow: '0 4px 12px rgba(25, 118, 210, 0.2)',
                        }
                      }
                    }}
                  />
                </Grid>

                {/* Mobile No */}
                <Grid item xs={12}>
                  <Typography variant="h6" sx={{
                    mt: 2,
                    mb: 2,
                    color: '#667eea',
                    fontWeight: 600,
                    borderBottom: '2px solid #667eea',
                    paddingBottom: 1
                  }}>
                    Mobile No.
                  </Typography>
                </Grid>

                <Grid item xs={12} md={2}>
                  <TextField
                    fullWidth
                    value="+91"
                    disabled
                    placeholder="+91"
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        backgroundColor: '#f5f5f5',
                        borderRadius: 2,
                      }
                    }}
                  />
                </Grid>

                <Grid item xs={12} md={4}>
                  <TextField
                    fullWidth
                    label="Mobile No"
                    value={formData.parentMobileNo || ''}
                    onChange={(e) => handleNumericChange('parentMobileNo', e.target.value, 10)}
                    placeholder="Enter 10-digit mobile number"
                    inputProps={{ maxLength: 10 }}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        backgroundColor: 'white',
                        borderRadius: 2,
                        '&:hover': {
                          boxShadow: '0 4px 8px rgba(0,0,0,0.1)',
                        },
                        '&.Mui-focused': {
                          boxShadow: '0 4px 12px rgba(25, 118, 210, 0.2)',
                        }
                      }
                    }}
                  />
                </Grid>

                {/* Landline no */}
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Landline no"
                    value={formData.parentLandlineNo || ''}
                    onChange={(e) => handleChange('parentLandlineNo', e.target.value)}
                    placeholder="Enter landline number"
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        backgroundColor: 'white',
                        borderRadius: 2,
                        '&:hover': {
                          boxShadow: '0 4px 8px rgba(0,0,0,0.1)',
                        },
                        '&.Mui-focused': {
                          boxShadow: '0 4px 12px rgba(25, 118, 210, 0.2)',
                        }
                      }
                    }}
                  />
                </Grid>

                {/* Fax No */}
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Fax No."
                    value={formData.parentFaxNo || ''}
                    onChange={(e) => handleChange('parentFaxNo', e.target.value)}
                    placeholder="Fax No."
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        backgroundColor: 'white',
                        borderRadius: 2,
                        '&:hover': {
                          boxShadow: '0 4px 8px rgba(0,0,0,0.1)',
                        },
                        '&.Mui-focused': {
                          boxShadow: '0 4px 12px rgba(25, 118, 210, 0.2)',
                        }
                      }
                    }}
                  />
                </Grid>

                {/* E-mail */}
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="E-mail"
                    type="email"
                    value={formData.parentEmail || ''}
                    onChange={(e) => handleChange('parentEmail', e.target.value)}
                    placeholder="Email"
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        backgroundColor: 'white',
                        borderRadius: 2,
                        '&:hover': {
                          boxShadow: '0 4px 8px rgba(0,0,0,0.1)',
                        },
                        '&.Mui-focused': {
                          boxShadow: '0 4px 12px rgba(25, 118, 210, 0.2)',
                        }
                      }
                    }}
                  />
                </Grid>
              </>
            )}
          </Grid>
        </AccordionDetails>
      </Accordion>

      {/* Adverse Action Accordion */}
      <Accordion
        defaultExpanded
        sx={{
          mb: 2,
          boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
          '&:before': { display: 'none' },
          borderRadius: 2,
          overflow: 'hidden'
        }}
      >
        <AccordionSummary
          expandIcon={<ExpandMoreIcon />}
          sx={{
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            color: 'white',
            '& .MuiAccordionSummary-content': {
              margin: '16px 0',
            },
            '& .MuiSvgIcon-root': {
              color: 'white',
            }
          }}
        >
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            ⚠️ Adverse Action History <i>(In case of any false information, the application will be rejected and accreditation will be denied or withdrawn.)</i>
          </Typography>
        </AccordionSummary>
        <AccordionDetails sx={{ p: 3, backgroundColor: 'white' }}>
          {/* Adequacy Check Section */}
          <AdequacyCheckSection
            sectionId="adverse-action-history"
            title="Adverse Action History"
            applicationStage={applicationStage}
            orgId={orgId}
            appId={appId}
            adequacyChecks={adequacyChecks}
            handleAdequacyCheck={handleAdequacyCheck}
            handleAdequacyRemarks={handleAdequacyRemarks}
            handleAdequacySubmit={handleAdequacySubmit}
            loading={loading}
            currentUser={currentUser}
            currentUserId={currentUserId}
            assessorId={assessorId}
            assessmentId={assessmentId}
          />
          <Grid container spacing={3}>
            {/* Has adverse action question */}
            <Grid item xs={12}>
              <Box>
                <Typography variant="h6" sx={{
                  mb: 2,
                  color: validationErrors['hasAdverseAction'] ? '#f44336' : '#667eea',
                  fontWeight: 600,
                  borderBottom: `2px solid ${validationErrors['hasAdverseAction'] ? '#f44336' : '#667eea'}`,
                  paddingBottom: 1
                }}>
                  Has adverse action has been initiated/taken against the laboratory in the past?<span style={{ color: 'red', fontWeight: 'bold' }}>*</span>
                </Typography>
                <Typography variant="body2" sx={{ color: '#666', mb: 2, fontStyle: 'italic' }}>
                  (If yes, please provide the details with present status)
                </Typography>
                <Box sx={{ display: 'flex', gap: 3, mt: 2 }}>
                  <FormControlLabel
                    control={
                      <input
                        type="radio"
                        name="hasAdverseAction"
                        value="Yes"
                        checked={formData.hasAdverseAction === 'Yes'}
                        onChange={(e) => handleChange('hasAdverseAction', e.target.value)}
                        style={{ marginRight: '8px' }}
                      />
                    }
                    label="Yes"
                    sx={{
                      '& .MuiFormControlLabel-label': {
                        fontSize: '1.1rem',
                        fontWeight: 500
                      }
                    }}
                  />
                  <FormControlLabel
                    control={
                      <input
                        type="radio"
                        name="hasAdverseAction"
                        value="No"
                        checked={formData.hasAdverseAction === 'No'}
                        onChange={(e) => handleChange('hasAdverseAction', e.target.value)}
                        style={{ marginRight: '8px' }}
                      />
                    }
                    label="No"
                    sx={{
                      '& .MuiFormControlLabel-label': {
                        fontSize: '1.1rem',
                        fontWeight: 500
                      }
                    }}
                  />
                </Box>
                {validationErrors['hasAdverseAction'] && (
                  <Typography variant="caption" sx={{ color: '#f44336', mt: 1, display: 'block' }}>
                    {validationErrors['hasAdverseAction']}
                  </Typography>
                )}
              </Box>
            </Grid>

            {/* Conditional fields - show only when "Yes" is selected */}
            {formData.hasAdverseAction === 'Yes' && (
              <>
                <Grid item xs={12} md={6}>
                  <FormControl fullWidth required sx={{
                    '& .MuiOutlinedInput-root': {
                      backgroundColor: 'white',
                      borderRadius: 2,
                      '&:hover': {
                        boxShadow: '0 4px 8px rgba(0,0,0,0.1)',
                      },
                      '&.Mui-focused': {
                        boxShadow: '0 4px 12px rgba(25, 118, 210, 0.2)',
                      }
                    }
                  }}>
                    <InputLabel>Type of adverse action</InputLabel>
                    <Select
                      value={formData.adverseActionType || 'Closed'}
                      onChange={(e) => handleChange('adverseActionType', e.target.value)}
                      label="Type of adverse action"
                    >
                      <MenuItem value="Closed">Closed</MenuItem>
                      <MenuItem value="Denial of Accreditation">Denial of Accreditation</MenuItem>
                      <MenuItem value="Debar from applying/re-applying">Debar from applying/re-applying</MenuItem>
                      <MenuItem value="Suspension of Accreditation">Suspension of Accreditation</MenuItem>
                      <MenuItem value="Withdrawal of Accreditation">Withdrawal of Accreditation</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>

                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Adverse decision date"
                    type="date"
                    value={formData.adverseDecisionDate || ''}
                    onChange={(e) => handleChange('adverseDecisionDate', e.target.value)}
                    InputLabelProps={{
                      shrink: true,
                    }}
                    inputProps={{
                      max: new Date().toISOString().split('T')[0] // ✅ today date
                    }}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        backgroundColor: 'white',
                        borderRadius: 2,
                        '&:hover': {
                          boxShadow: '0 4px 8px rgba(0,0,0,0.1)',
                        },
                        '&.Mui-focused': {
                          boxShadow: '0 4px 12px rgba(25, 118, 210, 0.2)',
                        }
                      }
                    }}
                  />
                </Grid>

                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Previous Lab Id"
                    value={formData.previousLabId || ''}
                    onChange={(e) => handleChange('previousLabId', e.target.value)}
                    placeholder="Enter previous lab ID"
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        backgroundColor: 'white',
                        borderRadius: 2,
                        '&:hover': {
                          boxShadow: '0 4px 8px rgba(0,0,0,0.1)',
                        },
                        '&.Mui-focused': {
                          boxShadow: '0 4px 12px rgba(25, 118, 210, 0.2)',
                        }
                      }
                    }}
                  />
                </Grid>
              </>
            )}
          </Grid>
        </AccordionDetails>
      </Accordion>

      {/* SEZ Region Accordion */}
      <Accordion
        defaultExpanded
        sx={{
          mb: 2,
          boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
          '&:before': { display: 'none' },
          borderRadius: 2,
          overflow: 'hidden'
        }}
      >
        <AccordionSummary
          expandIcon={<ExpandMoreIcon />}
          sx={{
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            color: 'white',
            '& .MuiAccordionSummary-content': {
              margin: '16px 0',
            },
            '& .MuiSvgIcon-root': {
              color: 'white',
            }
          }}
        >
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            🏭 SEZ Region (Special Economic Zone)
          </Typography>
        </AccordionSummary>
        <AccordionDetails sx={{ p: 3, backgroundColor: 'white' }}>
          {/* Adequacy Check Section */}
          <AdequacyCheckSection
            sectionId="sez-region"
            title="SEZ Region"
            applicationStage={applicationStage}
            orgId={orgId}
            appId={appId}
            adequacyChecks={adequacyChecks}
            handleAdequacyCheck={handleAdequacyCheck}
            handleAdequacyRemarks={handleAdequacyRemarks}
            handleAdequacySubmit={handleAdequacySubmit}
            loading={loading}
            currentUser={currentUser}
            currentUserId={currentUserId}
            assessorId={assessorId}
            assessmentId={assessmentId}
          />
          <Grid container spacing={3}>
            {/* Are you from SEZ region question */}
            <Grid item xs={12}>
              <Box>
                <Typography variant="h6" sx={{
                  mb: 2,
                  color: validationErrors['isFromSEZ'] ? '#f44336' : '#667eea',
                  fontWeight: 600,
                  borderBottom: `2px solid ${validationErrors['isFromSEZ'] ? '#f44336' : '#667eea'}`,
                  paddingBottom: 1
                }}>
                  Are you from SEZ region(Special economic zone)<span style={{ color: 'red', fontWeight: 'bold' }}>*</span>
                </Typography>
                <Box sx={{ display: 'flex', gap: 3, mt: 2 }}>
                  <FormControlLabel
                    control={
                      <input
                        type="radio"
                        name="isFromSEZ"
                        value="Yes"
                        checked={formData.isFromSEZ === 'Yes'}
                        onChange={(e) => handleChange('isFromSEZ', e.target.value)}
                        style={{ marginRight: '8px' }}
                      />
                    }
                    label="Yes"
                    sx={{
                      '& .MuiFormControlLabel-label': {
                        fontSize: '1.1rem',
                        fontWeight: 500
                      }
                    }}
                  />
                  <FormControlLabel
                    control={
                      <input
                        type="radio"
                        name="isFromSEZ"
                        value="No"
                        checked={formData.isFromSEZ === 'No'}
                        onChange={(e) => handleChange('isFromSEZ', e.target.value)}
                        style={{ marginRight: '8px' }}
                      />
                    }
                    label="No"
                    sx={{
                      '& .MuiFormControlLabel-label': {
                        fontSize: '1.1rem',
                        fontWeight: 500
                      }
                    }}
                  />
                </Box>
                {validationErrors['isFromSEZ'] && (
                  <Typography variant="caption" sx={{ color: '#f44336', mt: 1, display: 'block' }}>
                    {validationErrors['isFromSEZ']}
                  </Typography>
                )}
              </Box>
            </Grid>

            {/* Conditional SEZ Certificate upload - show only when "Yes" is selected */}
            {formData.isFromSEZ === 'Yes' && (
              <Grid item xs={12} md={6}>
                <FileUploadComponent
                  fieldName="sezCertificateFile"
                  label="SEZ Certificate - Upload your SEZ certificate"
                  orgId={orgId}
                  section="special-certificates"
                  required={true}
                  accept=".pdf,.jpg,.jpeg,.png"
                  maxSize={10}
                  onChange={handleS3FileUpload}
                  initialFile={formData.sezCertificateFile}
                  disabled={isFieldReadOnly('sez-region')}
                />
              </Grid>
            )}
          </Grid>
        </AccordionDetails>
      </Accordion>

      {/* Consultancy Accordion */}
      <Accordion
        defaultExpanded
        sx={{
          mb: 2,
          boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
          '&:before': { display: 'none' },
          borderRadius: 2,
          overflow: 'hidden'
        }}
      >
        <AccordionSummary
          expandIcon={<ExpandMoreIcon />}
          sx={{
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            color: 'white',
            '& .MuiAccordionSummary-content': {
              margin: '16px 0',
            },
            '& .MuiSvgIcon-root': {
              color: 'white',
            }
          }}
        >
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            🤝 NABL Accreditation Consultancy
          </Typography>
        </AccordionSummary>
        <AccordionDetails sx={{ p: 3, backgroundColor: 'white' }}>
          {/* Adequacy Check Section */}
          <AdequacyCheckSection
            sectionId="consultancy"
            title="NABL Accreditation Consultancy"
            applicationStage={applicationStage}
            orgId={orgId}
            appId={appId}
            adequacyChecks={adequacyChecks}
            handleAdequacyCheck={handleAdequacyCheck}
            handleAdequacyRemarks={handleAdequacyRemarks}
            handleAdequacySubmit={handleAdequacySubmit}
            loading={loading}
            currentUser={currentUser}
            currentUserId={currentUserId}
            assessorId={assessorId}
            assessmentId={assessmentId}
          />
          <Grid container spacing={3}>
            {/* Consultancy question */}
            <Grid item xs={12}>
              <Box>
                <Typography variant="h6" sx={{
                  mb: 2,
                  color: validationErrors['hasConsultancy'] ? '#f44336' : '#667eea',
                  fontWeight: 600,
                  borderBottom: `2px solid ${validationErrors['hasConsultancy'] ? '#f44336' : '#667eea'}`,
                  paddingBottom: 1
                }}>
                  Whether any individual or organization has provided consultancy for preparing towards NABL Accreditation<span style={{ color: 'red', fontWeight: 'bold' }}>*</span>
                </Typography>
                <Box sx={{ display: 'flex', gap: 3, mt: 2 }}>
                  <FormControlLabel
                    control={
                      <input
                        type="radio"
                        name="hasConsultancy"
                        value="Yes"
                        checked={formData.hasConsultancy === 'Yes'}
                        onChange={(e) => handleChange('hasConsultancy', e.target.value)}
                        style={{ marginRight: '8px' }}
                      />
                    }
                    label="Yes"
                    sx={{
                      '& .MuiFormControlLabel-label': {
                        fontSize: '1.1rem',
                        fontWeight: 500
                      }
                    }}
                  />
                  <FormControlLabel
                    control={
                      <input
                        type="radio"
                        name="hasConsultancy"
                        value="No"
                        checked={formData.hasConsultancy === 'No'}
                        onChange={(e) => handleChange('hasConsultancy', e.target.value)}
                        style={{ marginRight: '8px' }}
                      />
                    }
                    label="No"
                    sx={{
                      '& .MuiFormControlLabel-label': {
                        fontSize: '1.1rem',
                        fontWeight: 500
                      }
                    }}
                  />
                </Box>
                {validationErrors['hasConsultancy'] && (
                  <Typography variant="caption" sx={{ color: '#f44336', mt: 1, display: 'block' }}>
                    {validationErrors['hasConsultancy']}
                  </Typography>
                )}
              </Box>
            </Grid>

            {/* Conditional consultancy fields - show only when "Yes" is selected */}
            {formData.hasConsultancy === 'Yes' && (
              <>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Name of Individual/organization with city"
                    value={formData.consultancyName || ''}
                    onChange={(e) => handleChange('consultancyName', e.target.value)}
                    placeholder="Name of Individual/organization with city"
                    required
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        backgroundColor: 'white',
                        borderRadius: 2,
                        '&:hover': {
                          boxShadow: '0 4px 8px rgba(0,0,0,0.1)',
                        },
                        '&.Mui-focused': {
                          boxShadow: '0 4px 12px rgba(25, 118, 210, 0.2)',
                        }
                      }
                    }}
                  />
                </Grid>

                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Registered Office Address*"
                    value={formData.consultancyAddress || ''}
                    onChange={(e) => handleChange('consultancyAddress', e.target.value)}
                    placeholder="Enter Details.."
                    multiline
                    rows={2}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        backgroundColor: 'white',
                        borderRadius: 2,
                        '&:hover': {
                          boxShadow: '0 4px 8px rgba(0,0,0,0.1)',
                        },
                        '&.Mui-focused': {
                          boxShadow: '0 4px 12px rgba(25, 118, 210, 0.2)',
                        }
                      }
                    }}
                  />
                </Grid>
              </>
            )}
          </Grid>
        </AccordionDetails>
      </Accordion>

      {/* Medical Testing Facility Category Accordion */}
      <Accordion
        defaultExpanded
        sx={{
          mb: 2,
          boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
          '&:before': { display: 'none' },
          borderRadius: 2,
          overflow: 'hidden'
        }}
      >
        <AccordionSummary
          expandIcon={<ExpandMoreIcon />}
          sx={{
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            color: 'white',
            '& .MuiAccordionSummary-content': {
              margin: '16px 0',
            },
            '& .MuiSvgIcon-root': {
              color: 'white',
            }
          }}
        >
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            🏥 Category of Medical Testing Facility
          </Typography>
        </AccordionSummary>
        <AccordionDetails sx={{ p: 3, backgroundColor: 'white' }}>
          {/* Adequacy Check Section */}
          <AdequacyCheckSection
            sectionId="medical-testing-facility"
            title="Category of Medical Testing Facility"
            applicationStage={applicationStage}
            orgId={orgId}
            appId={appId}
            adequacyChecks={adequacyChecks}
            handleAdequacyCheck={handleAdequacyCheck}
            handleAdequacyRemarks={handleAdequacyRemarks}
            handleAdequacySubmit={handleAdequacySubmit}
            loading={loading}
            currentUser={currentUser}
            currentUserId={currentUserId}
            assessorId={assessorId}
            assessmentId={assessmentId}
          />
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <Typography variant="body1" sx={{
                mb: 3,
                color: '#666',
                fontStyle: 'italic',
                fontSize: '1rem'
              }}>
                Category of medical testing facility applied under the scope of Accreditation<br />
                (If Yes, Please Clearly Indicate in the Scope of Accreditation)
              </Typography>
            </Grid>

            {/* Permanent Facility */}
            <Grid item xs={12} md={4}>
              <Box>
                <Typography variant="h6" sx={{
                  mb: 2,
                  color: validationErrors['permanentFacility'] ? '#f44336' : '#667eea',
                  fontWeight: 600,
                  fontSize: '1.1rem'
                }}>
                  Permanent Facility<span style={{ color: 'red', fontWeight: 'bold' }}>*</span>
                </Typography>
                <Box sx={{ display: 'flex', gap: 3 }}>
                  <FormControlLabel
                    control={
                      <input
                        type="radio"
                        name="permanentFacility"
                        value="Yes"
                        checked={formData.permanentFacility === 'Yes'}
                        onChange={(e) => handleChange('permanentFacility', e.target.value)}
                        style={{ marginRight: '8px' }}
                      />
                    }
                    label="Yes"
                    sx={{ '& .MuiFormControlLabel-label': { fontSize: '1rem', fontWeight: 500 } }}
                  />
                  <FormControlLabel
                    control={
                      <input
                        type="radio"
                        name="permanentFacility"
                        value="No"
                        checked={formData.permanentFacility === 'No'}
                        onChange={(e) => handleChange('permanentFacility', e.target.value)}
                        style={{ marginRight: '8px' }}
                      />
                    }
                    label="No"
                    sx={{ '& .MuiFormControlLabel-label': { fontSize: '1rem', fontWeight: 500 } }}
                  />
                </Box>
                {validationErrors['permanentFacility'] && (
                  <Typography variant="caption" sx={{ color: '#f44336', mt: 1, display: 'block' }}>
                    {validationErrors['permanentFacility']}
                  </Typography>
                )}
              </Box>
            </Grid>

            {/* Laboratory carrying out testing at site facility */}
            {formData.laboratoryType === "Medical Imaging" && (
              <Grid item xs={12} md={4}>
                <Box>
                  <Typography variant="h6" sx={{
                    mb: 2,
                    color: validationErrors['laboratorySiteTesting'] ? '#f44336' : '#667eea',
                    fontWeight: 600,
                    fontSize: '1.1rem'
                  }}>
                    Laboratory carrying out testing at site facility<span style={{ color: 'red', fontWeight: 'bold' }}>*</span>
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 3 }}>
                    <FormControlLabel
                      control={
                        <input
                          type="radio"
                          name="laboratorySiteTesting"
                          value="Yes"
                          checked={formData.laboratorySiteTesting === 'Yes'}
                          onChange={(e) => handleChange('laboratorySiteTesting', e.target.value)}
                          style={{ marginRight: '8px' }}
                        />
                      }
                      label="Yes"
                      sx={{ '& .MuiFormControlLabel-label': { fontSize: '1rem', fontWeight: 500 } }}
                    />
                    <FormControlLabel
                      control={
                        <input
                          type="radio"
                          name="laboratorySiteTesting"
                          value="No"
                          checked={formData.laboratorySiteTesting === 'No'}
                          onChange={(e) => handleChange('laboratorySiteTesting', e.target.value)}
                          style={{ marginRight: '8px' }}
                        />
                      }
                      label="No"
                      sx={{ '& .MuiFormControlLabel-label': { fontSize: '1rem', fontWeight: 500 } }}
                    />
                  </Box>
                  {validationErrors['laboratorySiteTesting'] && (
                    <Typography variant="caption" sx={{ color: '#f44336', mt: 1, display: 'block' }}>
                      {validationErrors['laboratorySiteTesting']}
                    </Typography>
                  )}
                </Box>
              </Grid>
            )}

            {/* POCT (Point of Care Testing) */}
            <Grid item xs={12} md={4}>
              <Box>
                <Typography variant="h6" sx={{
                  mb: 2,
                  color: validationErrors['poctTesting'] ? '#f44336' : '#667eea',
                  fontWeight: 600,
                  fontSize: '1.1rem'
                }}>
                  POCT (Point of Care Testing)<span style={{ color: 'red', fontWeight: 'bold' }}>*</span>
                </Typography>
                <Box sx={{ display: 'flex', gap: 3 }}>
                  <FormControlLabel
                    control={
                      <input
                        type="radio"
                        name="poctTesting"
                        value="Yes"
                        checked={formData.poctTesting === 'Yes'}
                        onChange={(e) => handleChange('poctTesting', e.target.value)}
                        style={{ marginRight: '8px' }}
                      />
                    }
                    label="Yes"
                    sx={{ '& .MuiFormControlLabel-label': { fontSize: '1rem', fontWeight: 500 } }}
                  />
                  <FormControlLabel
                    control={
                      <input
                        type="radio"
                        name="poctTesting"
                        value="No"
                        checked={formData.poctTesting === 'No'}
                        onChange={(e) => handleChange('poctTesting', e.target.value)}
                        style={{ marginRight: '8px' }}
                      />
                    }
                    label="No"
                    sx={{ '& .MuiFormControlLabel-label': { fontSize: '1rem', fontWeight: 500 } }}
                  />
                </Box>
                {validationErrors['poctTesting'] && (
                  <Typography variant="caption" sx={{ color: '#f44336', mt: 1, display: 'block' }}>
                    {validationErrors['poctTesting']}
                  </Typography>
                )}
              </Box>
            </Grid>

            {/* Permanent Site Facility */}
            <Grid item xs={12} md={4}>
              <Box>
                <Typography variant="h6" sx={{
                  mb: 2,
                  color: validationErrors['permanentSiteFacility'] ? '#f44336' : '#667eea',
                  fontWeight: 600,
                  fontSize: '1.1rem'
                }}>
                  Permanent Site Facility<span style={{ color: 'red', fontWeight: 'bold' }}>*</span>
                </Typography>
                <Box sx={{ display: 'flex', gap: 3 }}>
                  <FormControlLabel
                    control={
                      <input
                        type="radio"
                        name="permanentSiteFacility"
                        value="Yes"
                        checked={formData.permanentSiteFacility === 'Yes'}
                        onChange={(e) => handleChange('permanentSiteFacility', e.target.value)}
                        style={{ marginRight: '8px' }}
                      />
                    }
                    label="Yes"
                    sx={{ '& .MuiFormControlLabel-label': { fontSize: '1rem', fontWeight: 500 } }}
                  />
                  <FormControlLabel
                    control={
                      <input
                        type="radio"
                        name="permanentSiteFacility"
                        value="No"
                        checked={formData.permanentSiteFacility === 'No'}
                        onChange={(e) => handleChange('permanentSiteFacility', e.target.value)}
                        style={{ marginRight: '8px' }}
                      />
                    }
                    label="No"
                    sx={{ '& .MuiFormControlLabel-label': { fontSize: '1rem', fontWeight: 500 } }}
                  />
                </Box>
                {validationErrors['permanentSiteFacility'] && (
                  <Typography variant="caption" sx={{ color: '#f44336', mt: 1, display: 'block' }}>
                    {validationErrors['permanentSiteFacility']}
                  </Typography>
                )}
              </Box>
            </Grid>

            {/* Permanent Site Facility Address - Conditional when Yes */}
            {formData.permanentSiteFacility === 'Yes' && (
              <Grid item xs={12} md={8}>
                <TextField
                  fullWidth
                  label="Permanent Site Facility Address"
                  name="permanentSiteFacilityAddress"
                  value={formData.permanentSiteFacilityAddress}
                  onChange={(e) => handleChange('permanentSiteFacilityAddress', e.target.value)}
                  required={formData.permanentSiteFacility === 'Yes'}
                  multiline
                  rows={3}
                  error={!!validationErrors['permanentSiteFacilityAddress']}
                  helperText={validationErrors['permanentSiteFacilityAddress']}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      backgroundColor: 'white',
                      borderRadius: 2,
                      '&:hover': {
                        boxShadow: '0 4px 8px rgba(0,0,0,0.1)',
                      },
                      '&.Mui-focused': {
                        boxShadow: '0 4px 12px rgba(102, 126, 234, 0.2)',
                      }
                    }
                  }}
                />
              </Grid>
            )}
            {/* Category for Which Accreditation is Being Sought */}
            <Grid item xs={12}>
              <FormControl fullWidth required sx={{
                '& .MuiOutlinedInput-root': {
                  backgroundColor: 'white',
                  borderRadius: 2,
                  '&:hover': {
                    boxShadow: '0 4px 8px rgba(0,0,0,0.1)',
                  },
                  '&.Mui-focused': {
                    boxShadow: '0 4px 12px rgba(25, 118, 210, 0.2)',
                  }
                }
              }}>
                <InputLabel>Category for Which Accreditation is Being Sought<span style={{ color: 'red', fontWeight: 'bold' }}>*</span></InputLabel>
                <Select
                  value={formData.accreditationCategory || ''}
                  onChange={(e) => handleChange('accreditationCategory', e.target.value)}
                  label="Category for Which Accreditation is Being Sought*"
                >
                  <MenuItem value="">--Select--</MenuItem>
                  <MenuItem value="Micro Laboratories (Up to 25 patients/ day)">
                    Micro Laboratories (Up to 25 patients/ day)
                  </MenuItem>
                  <MenuItem value="Mini Laboratories (26 - 50 patients/ day)">
                    Mini Laboratories (26 - 50 patients/ day)
                  </MenuItem>
                  <MenuItem value="Small Laboratories">
                    Small Laboratories(51-100 patients/ day)
                  </MenuItem>
                  <MenuItem value="Medium Laboratories">
                    Medium Laboratories(101-400 patients/ day)
                  </MenuItem>
                  <MenuItem value="Large Laboratories">
                    Large Laboratories(401-1000 patients/ day)
                  </MenuItem>
                  <MenuItem value="Very Large Laboratories">
                    Very Large Laboratories(Above 1000 patients/ day)
                  </MenuItem>
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        </AccordionDetails>
      </Accordion>

      {/* Sample Collection Accordion */}
      <Accordion
        defaultExpanded
        sx={{
          mb: 2,
          boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
          '&:before': { display: 'none' },
          borderRadius: 2,
          overflow: 'hidden'
        }}
      >
        <AccordionSummary
          expandIcon={<ExpandMoreIcon />}
          sx={{
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            color: 'white',
            '& .MuiAccordionSummary-content': {
              margin: '16px 0',
            },
            '& .MuiSvgIcon-root': {
              color: 'white',
            }
          }}
        >
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            🧪 Collection of Sample From
          </Typography>
        </AccordionSummary>
        <AccordionDetails sx={{ p: 3, backgroundColor: 'white' }}>
          {/* Adequacy Check Section */}
          <AdequacyCheckSection
            sectionId="sample-collection"
            title="Collection of Sample From"
            applicationStage={applicationStage}
            orgId={orgId}
            appId={appId}
            adequacyChecks={adequacyChecks}
            handleAdequacyCheck={handleAdequacyCheck}
            handleAdequacyRemarks={handleAdequacyRemarks}
            handleAdequacySubmit={handleAdequacySubmit}
            loading={loading}
            currentUser={currentUser}
            currentUserId={currentUserId}
            assessorId={assessorId}
            assessmentId={assessmentId}
          />
          <Grid container spacing={3}>
            {/* Sample Collection Centre(s)/ Facility(ies) */}
            <Grid item xs={12}>
              <Box>
                <Typography variant="h6" sx={{
                  mb: 2,
                  color: validationErrors['sampleCollectionCentre'] ? '#f44336' : '#667eea',
                  fontWeight: 600,
                  fontSize: '1.1rem'
                }}>
                  Sample Collection Centre(s)/ Facility(ies)<span style={{ color: 'red', fontWeight: 'bold' }}>*</span>
                  <Typography variant="caption" sx={{ display: 'block', color: '#888', fontStyle: 'italic' }}>
                    (Download Sample)
                  </Typography>
                </Typography>
                <Box sx={{ display: 'flex', gap: 3, mb: 3 }}>
                  <FormControlLabel
                    control={
                      <input
                        type="radio"
                        name="sampleCollectionCentre"
                        value="Yes"
                        checked={formData.sampleCollectionCentre === 'Yes'}
                        onChange={(e) => handleChange('sampleCollectionCentre', e.target.value)}
                        style={{ marginRight: '8px' }}
                      />
                    }
                    label="Yes"
                    sx={{ '& .MuiFormControlLabel-label': { fontSize: '1rem', fontWeight: 500 } }}
                  />
                  <FormControlLabel
                    control={
                      <input
                        type="radio"
                        name="sampleCollectionCentre"
                        value="No"
                        checked={formData.sampleCollectionCentre === 'No'}
                        onChange={(e) => handleChange('sampleCollectionCentre', e.target.value)}
                        style={{ marginRight: '8px' }}
                      />
                    }
                    label="No"
                    sx={{ '& .MuiFormControlLabel-label': { fontSize: '1rem', fontWeight: 500 } }}
                  />
                </Box>
                {validationErrors['sampleCollectionCentre'] && (
                  <Typography variant="caption" sx={{ color: '#f44336', mt: 1, display: 'block' }}>
                    {validationErrors['sampleCollectionCentre']}
                  </Typography>
                )}
              </Box>
            </Grid>

            {/* Conditional fields for Sample Collection Centre */}
            {formData.sampleCollectionCentre === 'Yes' && (
              <>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Number of Collection Centers"
                    type="number"
                    value={formData.numberOfCollectionCenters || ''}
                    onChange={(e) => handleChange('numberOfCollectionCenters', e.target.value)}
                    placeholder="No of Collection Center"
                    required
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        backgroundColor: 'white',
                        borderRadius: 2,
                        '&:hover': { boxShadow: '0 4px 8px rgba(0,0,0,0.1)' },
                        '&.Mui-focused': { boxShadow: '0 4px 12px rgba(25, 118, 210, 0.2)' }
                      }
                    }}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <FileUploadComponent
                    fieldName="sampleCollectionDocument"
                    label="Upload Relevant Document"
                    orgId={orgId}
                    section="sample-collection"
                    required={true}
                    accept=".pdf,.jpg,.jpeg,.png,.doc,.docx"
                    maxSize={10}
                    onChange={handleS3FileUpload}
                    initialFile={formData.sampleCollectionDocument}
                    disabled={isFieldReadOnly('sample-collection')}
                  />
                </Grid>
              </>
            )}

            {/* Home collection */}
            <Grid item xs={12}>
              <Box sx={{ mt: 3 }}>
                <Typography variant="h6" sx={{
                  mb: 2,
                  color: validationErrors['homeCollection'] ? '#f44336' : '#667eea',
                  fontWeight: 600,
                  fontSize: '1.1rem'
                }}>
                  Home collection<span style={{ color: 'red', fontWeight: 'bold' }}>*</span>
                  <Typography variant="caption" sx={{ display: 'block', color: '#888', fontStyle: 'italic' }}>
                    (Download Sample)
                  </Typography>
                </Typography>
                <Box sx={{ display: 'flex', gap: 3, mb: 2 }}>
                  <FormControlLabel
                    control={
                      <input
                        type="radio"
                        name="homeCollection"
                        value="Yes"
                        checked={formData.homeCollection === 'Yes'}
                        onChange={(e) => handleChange('homeCollection', e.target.value)}
                        style={{ marginRight: '8px' }}
                      />
                    }
                    label="Yes"
                    sx={{ '& .MuiFormControlLabel-label': { fontSize: '1rem', fontWeight: 500 } }}
                  />
                  <FormControlLabel
                    control={
                      <input
                        type="radio"
                        name="homeCollection"
                        value="No"
                        checked={formData.homeCollection === 'No'}
                        onChange={(e) => handleChange('homeCollection', e.target.value)}
                        style={{ marginRight: '8px' }}
                      />
                    }
                    label="No"
                    sx={{ '& .MuiFormControlLabel-label': { fontSize: '1rem', fontWeight: 500 } }}
                  />
                </Box>
                {validationErrors['homeCollection'] && (
                  <Typography variant="caption" sx={{ color: '#f44336', mt: 1, display: 'block' }}>
                    {validationErrors['homeCollection']}
                  </Typography>
                )}
                <Alert severity="info" sx={{
                  backgroundColor: '#e3f2fd',
                  border: '1px solid #bbdefb',
                  borderRadius: 2,
                  '& .MuiAlert-icon': { color: '#1976d2' }
                }}>
                  Home collection of samples shall be performed by trained lab staff only, and names and other details of such personnel shall be included in NABL 153
                </Alert>
              </Box>
            </Grid>

            {/* Conditional upload for Home collection */}
            {formData.homeCollection === 'Yes' && (
              <Grid item xs={12} md={6}>
                <FileUploadComponent
                  fieldName="homeCollectionDocument"
                  label="Upload Relevant Document"
                  orgId={orgId}
                  section="home-collection"
                  required={true}
                  accept=".pdf,.jpg,.jpeg,.png,.doc,.docx"
                  maxSize={10}
                  onChange={handleS3FileUpload}
                  initialFile={formData.homeCollectionDocument}
                  disabled={isFieldReadOnly('sample-collection')}
                />
              </Grid>
            )}

            {/* Other source(s) */}
            <Grid item xs={12}>
              <Box sx={{ mt: 3 }}>
                <Typography variant="h6" sx={{
                  mb: 2,
                  color: validationErrors['otherSources'] ? '#f44336' : '#667eea',
                  fontWeight: 600,
                  fontSize: '1.1rem'
                }}>
                  Other source(s)<span style={{ color: 'red', fontWeight: 'bold' }}>*</span>
                  <Typography variant="caption" sx={{ display: 'block', color: '#888', fontStyle: 'italic' }}>
                    (Other than the medical laboratory or sample collection centre/ facility)
                  </Typography>
                </Typography>
                <Box sx={{ display: 'flex', gap: 3, mb: 2 }}>
                  <FormControlLabel
                    control={
                      <input
                        type="radio"
                        name="otherSources"
                        value="Yes"
                        checked={formData.otherSources === 'Yes'}
                        onChange={(e) => handleChange('otherSources', e.target.value)}
                        style={{ marginRight: '8px' }}
                      />
                    }
                    label="Yes"
                    sx={{ '& .MuiFormControlLabel-label': { fontSize: '1rem', fontWeight: 500 } }}
                  />
                  <FormControlLabel
                    control={
                      <input
                        type="radio"
                        name="otherSources"
                        value="No"
                        checked={formData.otherSources === 'No'}
                        onChange={(e) => handleChange('otherSources', e.target.value)}
                        style={{ marginRight: '8px' }}
                      />
                    }
                    label="No"
                    sx={{ '& .MuiFormControlLabel-label': { fontSize: '1rem', fontWeight: 500 } }}
                  />
                </Box>
                {validationErrors['otherSources'] && (
                  <Typography variant="caption" sx={{ color: '#f44336', mt: 1, display: 'block' }}>
                    {validationErrors['otherSources']}
                  </Typography>
                )}
                <Alert severity="warning" sx={{
                  backgroundColor: '#fff3e0',
                  border: '1px solid #ffcc02',
                  borderRadius: 2,
                  '& .MuiAlert-icon': { color: '#f57c00' }
                }}>
                  Enlist all other sources of sample collection with complete contact details and address
                </Alert>
              </Box>
            </Grid>

            {/* Conditional upload for Other sources */}
            {formData.otherSources === 'Yes' && (
              <Grid item xs={12} md={6}>
                <FileUploadComponent
                  fieldName="otherSourcesDocument"
                  label="Upload Relevant Document"
                  orgId={orgId}
                  section="other-sources"
                  required={true}
                  accept=".pdf,.jpg,.jpeg,.png,.doc,.docx"
                  maxSize={10}
                  onChange={handleS3FileUpload}
                  initialFile={formData.otherSourcesDocument}
                  disabled={isFieldReadOnly('sample-collection')}
                />
              </Grid>
            )}
          </Grid>
        </AccordionDetails>
      </Accordion>

      {/* Internal Audit and Management Review Accordion */}
      <Accordion
        defaultExpanded
        sx={{
          mb: 2,
          boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
          '&:before': { display: 'none' },
          borderRadius: 2,
          overflow: 'hidden'
        }}
      >
        <AccordionSummary
          expandIcon={<ExpandMoreIcon />}
          sx={{
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            color: 'white',
            '& .MuiAccordionSummary-content': {
              margin: '16px 0',
            },
            '& .MuiSvgIcon-root': {
              color: 'white',
            }
          }}
        >
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            📋 Internal Audit and Management Review
          </Typography>
        </AccordionSummary>
        <AccordionDetails sx={{ p: 3, backgroundColor: 'white' }}>
          {/* Adequacy Check Section */}
          <AdequacyCheckSection
            sectionId="internal-audit-management-review"
            title="Internal Audit and Management Review"
            applicationStage={applicationStage}
            orgId={orgId}
            appId={appId}
            adequacyChecks={adequacyChecks}
            handleAdequacyCheck={handleAdequacyCheck}
            handleAdequacyRemarks={handleAdequacyRemarks}
            handleAdequacySubmit={handleAdequacySubmit}
            loading={loading}
            currentUser={currentUser}
            currentUserId={currentUserId}
            assessorId={assessorId}
            assessmentId={assessmentId}
          />
          <Grid container spacing={3}>
            {/* Internal Audit Section */}
            <Grid item xs={12}>
              <Typography variant="h6" sx={{
                mb: 3,
                color: '#667eea',
                fontWeight: 600,
                borderBottom: '2px solid #667eea',
                paddingBottom: 1
              }}>
                Internal Audit
              </Typography>
            </Grid>

            <Grid item xs={12} md={3}>
              <TextField
                fullWidth
                label="From Date"
                type="date"
                value={formData.internalAuditFromDate || ''}
                onChange={(e) => handleChange('internalAuditFromDate', e.target.value)}
                InputLabelProps={{ shrink: true }}
                required
                placeholder="Start Date"
                error={!!validationErrors['internalAuditFromDate']}
                helperText={validationErrors['internalAuditFromDate']}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    backgroundColor: 'white',
                    borderRadius: 2,
                    '&:hover': { boxShadow: '0 4px 8px rgba(0,0,0,0.1)' },
                    '&.Mui-focused': { boxShadow: '0 4px 12px rgba(25, 118, 210, 0.2)' },
                    '&.Mui-error': {
                      borderColor: '#f44336',
                      '&:hover': { boxShadow: '0 4px 8px rgba(244, 67, 54, 0.2)' },
                      '&.Mui-focused': { boxShadow: '0 4px 12px rgba(244, 67, 54, 0.3)' }
                    }
                  }
                }}
              />
            </Grid>

            <Grid item xs={12} md={3}>
              <TextField
                fullWidth
                label="To Date"
                type="date"
                value={formData.internalAuditToDate || ''}
                onChange={(e) => handleChange('internalAuditToDate', e.target.value)}
                InputLabelProps={{ shrink: true }}
                required
                placeholder="End Date"
                error={!!validationErrors['internalAuditToDate']}
                helperText={validationErrors['internalAuditToDate']}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    backgroundColor: 'white',
                    borderRadius: 2,
                    '&:hover': { boxShadow: '0 4px 8px rgba(0,0,0,0.1)' },
                    '&.Mui-focused': { boxShadow: '0 4px 12px rgba(25, 118, 210, 0.2)' },
                    '&.Mui-error': {
                      borderColor: '#f44336',
                      '&:hover': { boxShadow: '0 4px 8px rgba(244, 67, 54, 0.2)' },
                      '&.Mui-focused': { boxShadow: '0 4px 12px rgba(244, 67, 54, 0.3)' }
                    }
                  }
                }}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <FileUploadComponent
                fieldName="internalAuditDocument"
                label="Procedure of Internal Audit document upload"
                description="(Only PNG, JPG, JPEG, and PDF files are allowed.)"
                orgId={orgId}
                section="internal-audit"
                required={false}
                accept=".pdf,.jpg,.jpeg,.png"
                maxSize={10}
                onChange={handleS3FileUpload}
                initialFile={formData.internalAuditDocument}
                disabled={isFieldReadOnly('internal-audit-management-review')}
              />
            </Grid>

            {/* Management Review Section */}
            <Grid item xs={12}>
              <Typography variant="h6" sx={{
                mt: 3, mb: 3,
                color: '#667eea',
                fontWeight: 600,
                borderBottom: '2px solid #667eea',
                paddingBottom: 1
              }}>
                Management Review
              </Typography>
            </Grid>

            <Grid item xs={12} md={3}>
              <TextField
                fullWidth
                label="From Date"
                type="date"
                value={formData.managementReviewFromDate || ''}
                onChange={(e) => handleChange('managementReviewFromDate', e.target.value)}
                InputLabelProps={{ shrink: true }}
                required
                placeholder="Start Date"
                error={!!validationErrors['managementReviewFromDate']}
                helperText={validationErrors['managementReviewFromDate']}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    backgroundColor: 'white',
                    borderRadius: 2,
                    '&:hover': { boxShadow: '0 4px 8px rgba(0,0,0,0.1)' },
                    '&.Mui-focused': { boxShadow: '0 4px 12px rgba(25, 118, 210, 0.2)' },
                    '&.Mui-error': {
                      borderColor: '#f44336',
                      '&:hover': { boxShadow: '0 4px 8px rgba(244, 67, 54, 0.2)' },
                      '&.Mui-focused': { boxShadow: '0 4px 12px rgba(244, 67, 54, 0.3)' }
                    }
                  }
                }}
              />
            </Grid>

            <Grid item xs={12} md={3}>
              <TextField
                fullWidth
                label="To Date"
                type="date"
                value={formData.managementReviewToDate || ''}
                onChange={(e) => handleChange('managementReviewToDate', e.target.value)}
                InputLabelProps={{ shrink: true }}
                required
                placeholder="End Date"
                error={!!validationErrors['managementReviewToDate']}
                helperText={validationErrors['managementReviewToDate']}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    backgroundColor: 'white',
                    borderRadius: 2,
                    '&:hover': { boxShadow: '0 4px 8px rgba(0,0,0,0.1)' },
                    '&.Mui-focused': { boxShadow: '0 4px 12px rgba(25, 118, 210, 0.2)' },
                    '&.Mui-error': {
                      borderColor: '#f44336',
                      '&:hover': { boxShadow: '0 4px 8px rgba(244, 67, 54, 0.2)' },
                      '&.Mui-focused': { boxShadow: '0 4px 12px rgba(244, 67, 54, 0.3)' }
                    }
                  }
                }}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <FileUploadComponent
                fieldName="managementReviewDocument"
                label="Last Management Review Report"
                description="(Only PNG, JPG, JPEG, and PDF files are allowed.)"
                orgId={orgId}
                section="management-review"
                required={false}
                accept=".pdf,.jpg,.jpeg,.png"
                maxSize={10}
                onChange={handleS3FileUpload}
                initialFile={formData.managementReviewDocument}
                disabled={isFieldReadOnly('internal-audit-management-review')}
              />
            </Grid>

            {/* Audit Requirements Section */}
            {/* <Grid item xs={12}>
              <Typography variant="h6" sx={{
                mt: 3, mb: 3,
                color: '#667eea',
                fontWeight: 600,
                borderBottom: '2px solid #667eea',
                paddingBottom: 1
              }}>
                Audit Requirements
              </Typography>
            </Grid> */}

            <Grid item xs={12}>
              <Box>
                <Typography variant="body1" sx={{
                  mb: 2,
                  fontWeight: 500,
                  color: validationErrors['iso15189Audited'] ? '#f44336' : 'inherit'
                }}>
                  Whether all requirements of ISO 15189:2022 covering all activities of laboratory have been audited at least once in last one year<span style={{ color: 'red', fontWeight: 'bold' }}>*</span>
                </Typography>
                <Box sx={{ display: 'flex', gap: 3 }}>
                  <FormControlLabel
                    control={
                      <input
                        type="radio"
                        name="iso15189Audited"
                        value="Yes"
                        checked={formData.iso15189Audited === 'Yes'}
                        onChange={(e) => handleChange('iso15189Audited', e.target.value)}
                        style={{ marginRight: '8px' }}
                      />
                    }
                    label="Yes"
                    sx={{ '& .MuiFormControlLabel-label': { fontSize: '1rem', fontWeight: 500 } }}
                  />
                </Box>
                {validationErrors['iso15189Audited'] && (
                  <Typography variant="caption" sx={{ color: '#f44336', mt: 1, display: 'block' }}>
                    {validationErrors['iso15189Audited']}
                  </Typography>
                )}
              </Box>
            </Grid>

            <Grid item xs={12}>
              <Box>
                <Typography variant="body1" sx={{
                  mb: 2,
                  fontWeight: 500,
                  color: validationErrors['locationsAudited'] ? '#f44336' : 'inherit'
                }}>
                  Whether various locations (including collection centers) were covered in the audit<span style={{ color: 'red', fontWeight: 'bold' }}>*</span>
                </Typography>
                <Box sx={{ display: 'flex', gap: 3 }}>
                  <FormControlLabel
                    control={
                      <input
                        type="radio"
                        name="locationsAudited"
                        value="Yes"
                        checked={formData.locationsAudited === 'Yes'}
                        onChange={(e) => handleChange('locationsAudited', e.target.value)}
                        style={{ marginRight: '8px' }}
                      />
                    }
                    label="Yes"
                    sx={{ '& .MuiFormControlLabel-label': { fontSize: '1rem', fontWeight: 500 } }}
                  />
                </Box>
                {validationErrors['locationsAudited'] && (
                  <Typography variant="caption" sx={{ color: '#f44336', mt: 1, display: 'block' }}>
                    {validationErrors['locationsAudited']}
                  </Typography>
                )}
              </Box>
            </Grid>

            <Grid item xs={12}>
              <Box>
                <Typography variant="body1" sx={{
                  mb: 2,
                  fontWeight: 500,
                  color: validationErrors['prePostExamAudited'] ? '#f44336' : 'inherit'
                }}>
                  Whether pre and post examination activities were included in the audit schedule<span style={{ color: 'red', fontWeight: 'bold' }}>*</span>
                </Typography>
                <Box sx={{ display: 'flex', gap: 3 }}>
                  <FormControlLabel
                    control={
                      <input
                        type="radio"
                        name="prePostExamAudited"
                        value="Yes"
                        checked={formData.prePostExamAudited === 'Yes'}
                        onChange={(e) => handleChange('prePostExamAudited', e.target.value)}
                        style={{ marginRight: '8px' }}
                      />
                    }
                    label="Yes"
                    sx={{ '& .MuiFormControlLabel-label': { fontSize: '1rem', fontWeight: 500 } }}
                  />
                </Box>
                {validationErrors['prePostExamAudited'] && (
                  <Typography variant="caption" sx={{ color: '#f44336', mt: 1, display: 'block' }}>
                    {validationErrors['prePostExamAudited']}
                  </Typography>
                )}
              </Box>
            </Grid>

            {/* Save Data Note */}
            <Grid item xs={12}>
              <Alert severity="info" sx={{
                mt: 3,
                backgroundColor: '#e3f2fd',
                border: '1px solid #bbdefb',
                borderRadius: 2,
                '& .MuiAlert-icon': {
                  color: '#1976d2'
                }
              }}>
                {hasExistingData
                  ? 'Please update your data first before clicking on Next or Back button.'
                  : 'Please save your data first before clicking on Next or Back button.'
                }
              </Alert>
            </Grid>
          </Grid>
        </AccordionDetails>
      </Accordion>

      {/* Validation Summary */}
      {Object.keys(validationErrors).length > 0 && (
        <Box sx={{
          mt: 3,
          p: 2,
          backgroundColor: '#ffebee',
          borderRadius: 2,
          border: '1px solid #e57373'
        }}>
          <Typography variant="h6" sx={{ color: '#c62828', mb: 2, fontWeight: 600 }}>
            Please fix the following errors:
          </Typography>
          <Grid container spacing={1}>
            {Object.entries(validationErrors)
              .filter(([_, error]) => error)
              .map(([field, error], index) => (
                <Grid item xs={12} sm={6} md={4} key={index}>
                  <Typography variant="body2" sx={{ color: '#d32f2f' }}>
                    • {error}
                  </Typography>
                </Grid>
              ))
            }
          </Grid>
        </Box>
      )}


      {/* Form Status Indicator */}
      <Box sx={{ mt: 2, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1 }}>
        <Box
          sx={{
            width: 12,
            height: 12,
            borderRadius: '50%',
            backgroundColor: isFormValid ? '#4caf50' : '#f44336'
          }}
        />
        <Typography variant="body2" sx={{ color: isFormValid ? '#2e7d32' : '#c62828' }}>
          Form is {isFormValid ? 'valid and ready to save' : 'incomplete or has errors'}
        </Typography>
      </Box>

      {/* Save Button - Show for initial state (null stage), form filling stages, stage 18a (inadequate sections), and unfrozen sections */}
      {(() => {
        // Allow saving when no stage is set (initial form state)
        const isInitialState = applicationStage === null || applicationStage === '' || applicationStage === undefined;
        const formFillingStages = ["1", "2", "3", "4", "5", "6", "7", "8", "9", "10", "11", "12", "13", "14", "15", "16", "17"];
        const isFormFillingStage = formFillingStages.includes(applicationStage);
        const isStage18a = applicationStage === "18a";
        const hasUnfrozenSections = isSectionUnfrozen('Laboratory Details') ||
          isSectionUnfrozen('Internal Audit and Management Review') ||
          isSectionUnfrozen('Contact Person Details') ||
          isSectionUnfrozen('Legal Entity Type') ||
          isSectionUnfrozen('Type of Functioning') ||
          isSectionUnfrozen('Parent Organization') ||
          isSectionUnfrozen('Adverse Action History') ||
          isSectionUnfrozen('SEZ Region') ||
          isSectionUnfrozen('Consultancy') ||
          isSectionUnfrozen('Medical Testing Facility') ||
          isSectionUnfrozen('Sample Collection') ||
          isSectionUnfrozen('Collection of Sample From');
        return isInitialState || isFormFillingStage || isStage18a || hasUnfrozenSections;
      })() && (
          <Box sx={{ mt: 3, display: 'flex', justifyContent: 'center' }}>
            <Button
              variant="contained"
              size="large"
              onClick={saveLaboratoryDetails}
              disabled={loading}
              sx={{
                background: isFormValid
                  ? 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'
                  : 'linear-gradient(135deg, #bdbdbd 0%, #9e9e9e 100%)',
                px: 4,
                py: 2,
                fontSize: '1.1rem',
                fontWeight: 600,
                '&:hover': {
                  background: isFormValid
                    ? 'linear-gradient(135deg, #5a67d8 0%, #6b46c1 100%)'
                    : 'linear-gradient(135deg, #a5a5a5 0%, #8a8a8a 100%)',
                },
                '&:disabled': {
                  background: '#ccc',
                }
              }}
            >
              <LoaderWrapper loading={loading} type="button">
                {(() => {
                  if (loading) {
                    return hasExistingData ? 'Updating...' : 'Saving...';
                  }

                  // Check different states
                  const isInitialState = applicationStage === null || applicationStage === '' || applicationStage === undefined;
                  const formFillingStages = ["1", "2", "3", "4", "5", "6", "7", "8", "9", "10", "11", "12", "13", "14", "15", "16", "17"];
                  const isFormFillingStage = formFillingStages.includes(applicationStage);
                  const hasUnfrozenSections = isSectionUnfrozen('Laboratory Details') ||
                    isSectionUnfrozen('Contact Person Details') ||
                    isSectionUnfrozen('Legal Entity Type') ||
                    isSectionUnfrozen('Type of Functioning') ||
                    isSectionUnfrozen('Parent Organization') ||
                    isSectionUnfrozen('Adverse Action History') ||
                    isSectionUnfrozen('SEZ Region') ||
                    isSectionUnfrozen('Consultancy') ||
                    isSectionUnfrozen('Medical Testing Facility') ||
                    isSectionUnfrozen('Collection of Sample From') ||
                    isSectionUnfrozen('Internal Audit and Management Review');

                  if (hasUnfrozenSections) {
                    return 'Update Unfrozen Sections';
                  } else if (applicationStage === "18a") {
                    return 'Update Inadequate Sections';
                  } else if (isInitialState || isFormFillingStage) {
                    return hasExistingData ? 'Update Laboratory Details' : 'Save Laboratory Details';
                  } else {
                    return 'Read Only Mode';
                  }
                })()}
              </LoaderWrapper>
            </Button>
          </Box>
        )}

      {/* Loading Overlay */}
      {loading && (
        <LoaderWrapper
          loading={true}
          type="fullscreen"
          message="Processing laboratory details..."
        />
      )}

      {/* Notifications */}
      <Snackbar
        open={notification.open}
        autoHideDuration={6000}
        onClose={closeNotification}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Alert
          onClose={closeNotification}
          severity={notification.severity}
          sx={{ width: '100%' }}
        >
          {notification.message}
        </Alert>
      </Snackbar>

    </Box>

  );
};

export default CalibrationOrganizationSection;
